import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Lang = "en" | "nl" | "it" | "de" | "fr" | "es";
// `label` is the two-letter code for the compact switcher in the header and
// footer. `name` is what the language calls itself, for anywhere we address
// the reader in a sentence: "This page is also in Nederlands" is nonsense,
// and "also in NL" is worse.
export const LANGS: { code: Lang; label: string; name: string }[] = [
  { code: "en", label: "EN", name: "English" },
  { code: "nl", label: "NL", name: "Nederlands" },
  { code: "it", label: "IT", name: "italiano" },
  { code: "de", label: "DE", name: "Deutsch" },
  { code: "fr", label: "FR", name: "français" },
  { code: "es", label: "ES", name: "español" },
];

type Dict = Record<string, string>;

const en: Dict = {
  nav_explore: "Explore clubs",
  nav_partner: "List your club",
  hero_title_1: "{n} beach clubs.",
  hero_title_2: "One place.",
  hero_sub: "See real prices and book your sunbed in minutes.",
  search_placeholder: "Where to? Town, region or club",
  dest_label: "Destination",
  socials_title: "Their own channels",
  dest_all: "All destinations",
  dest_guide: "Guide to {place}",
  sug_region: "Coast",
  sug_town: "Town",
  sug_club: "Club",
  search_cta: "Search beach clubs",
  near_me: "Near me",
  locating: "Finding you...",
  regions_eyebrow: "Regions",
  regions_title: "Where the sea is sorted",
  regions_sub: "From famous shores to quiet coves, pick a coast and see who has a bed for you.",
  live_title: "On the platform today",
  live_sub: "We map beach clubs ourselves, coast by coast, from the Mediterranean to Bali and the Caribbean. Verified ones show real prices, the rest are waiting for their owner to claim them.",
  all_clubs: "View all clubs",
  stats_clubs: "beach clubs",
  stats_regions: "coasts",
  how_title: "How it works",
  how_1_t: "Find your beach",
  how_1_d: "Search by region or let us look around you. Every club in one list, not ten tabs.",
  how_2_t: "Compare real prices",
  how_2_d: "Beds, parasols and what is included. No calling around, no guessing.",
  how_3_t: "Book your bed",
  how_3_d: "Send one request and the club confirms directly. Your spot is sorted before you arrive.",
  owners_eyebrow: "For owners",
  owners_title: "Run a beach club?",
  owners_sub: "Get bookings from travelers before they even land.",
  owners_cta: "List your club",
  clubs_title: "Beach clubs",
  clubs_found: "clubs found",
  filter_all: "All regions",
  verified: "Checked by us",
  unverified: "Not claimed yet",
  from: "from",
  price_on_request: "Ask today’s price",
  km_away: "km away",
  view_club: "View club",
  approx_note: "Location shown at town level.",
  amenity_bed_service: "Service at your bed",
  amenity_jetty: "Jetty into the sea",
  amenity_lunch: "Lunch and kitchen",
  amenity_towels: "Towels included",
  amenity_sunscreen: "Sunscreen included",
  amenity_cushions: "Cushions included",
  amenity_party: "DJ and events",
  request_title: "Request availability",
  request_sub: "The club confirms directly. No payment until they do.",
  form_name: "Your name",
  form_contact: "WhatsApp number or email",
  form_date: "Date of visit",
  form_party: "People",
  form_message: "Anything else? (front row, cabana, lunch)",
  form_send: "Send request",
  form_sending: "Sending...",
  request_ok_t: "Request sent",
  request_ok_d: "We pass it straight to the club. You get an answer on the contact you left.",
  request_ok_expect: "Most clubs reply within 24 to 48 hours.",
  request_ok_fallback: "No reply after a few days? Message them on WhatsApp directly, or",
  request_error: "Something went wrong. Please try again.",
  contact_us: "contact us",
  wa_fastest: "Fastest way to reach them",
  footer_contact: "Contact",
  footer_contact_note: "Questions about sunbedly itself?",
  claim_note: "Is this your club? Claim this page and set your own prices.",
  claim_cta: "Claim this club",
  partner_title: "Put your club where travelers look",
  partner_sub: "A free page with your prices, your extras and direct requests to your phone. No sign-up fee, no exclusivity, cancel whenever.",
  partner_p1_t: "Free listing",
  partner_p1_d: "Your beds, prices and extras on a page that works, in six languages.",
  partner_p2_t: "Requests, not spam",
  partner_p2_d: "Travelers send structured requests. You confirm on WhatsApp like you already do.",
  partner_p3_t: "New guests, not your regulars",
  partner_p3_d: "We bring international travelers who cannot find you today.",
  partner_form_club: "Club name",
  partner_form_name: "Contact person",
  partner_form_contact: "WhatsApp number or email",
  partner_form_country: "Country",
  partner_form_town: "Town or beach",
  partner_form_instagram: "Instagram (optional)",
  partner_form_system: "Do you use a booking system?",
  partner_sys_yes: "Yes",
  partner_sys_no: "No",
  partner_sys_unknown: "Not sure",
  partner_form_message: "Tell us about your club (optional)",
  partner_send: "Apply for a listing",
  partner_ok_t: "Application received",
  partner_ok_d: "We set up your page and contact you to confirm prices and photos.",
  footer_tag: "The world’s beach clubs, sorted.",
  footer_travelers: "Travelers",
  footer_owners: "Club owners",
  footer_langs: "Language",
  back_to_clubs: "All clubs",
  no_results: "No clubs match yet. Try another region.",
  wa_book: "Book via WhatsApp",
  sponsored_badge: "Sponsored",
  get_directions: "Get directions",
  show_map: "Show map",
  show_list: "Show list",
  last_checked: "Checked",
  season_may_oct: "Typically open May to October",
  checking_availability: "Checking availability...",
  route_title: "Getting there",
  map_title: "Pick your spot on the beach",
  map_sub: "Choose a date, then tap the beds you want. Row A is right at the water.",
  sea: "Sea",
  jetty_label: "Jetty",
  row_label: "Row",
  free_legend: "Free",
  taken_legend: "Taken",
  selected_legend: "Your pick",
  max_spots: "Max 4 sets per reservation.",
  reserve_cta: "Reserve these beds",
  reserve_ok_t: "Reservation sent",
  reserve_ok_d: "The club confirms your spots directly on the contact you left. You pay at the club.",
  conflict_err: "Someone just took one of these spots. Pick again.",
  your_spots: "Your spots",
  reviews_title: "Reviews",
  reviews_count: "reviews",
  no_reviews_yet: "No reviews yet. Be the first to share your visit.",
  write_review_cta: "Write a review",
  review_form_title: "Share your visit",
  review_form_sub: "Real visits only. Keep it useful for the next traveler.",
  review_name: "Your name",
  review_contact: "Email (not shown publicly)",
  review_rating: "Your rating",
  review_headline: "Headline (optional)",
  review_body: "Your review",
  review_visit_month: "When did you visit?",
  review_booked_via: "How did you book?",
  booked_whatsapp: "WhatsApp",
  booked_lidoo: "sunbedly request",
  booked_phone: "Phone",
  booked_walkin: "Walked in",
  booked_other: "Other",
  review_send: "Post review",
  review_sending: "Posting...",
  review_ok_t: "Thanks for sharing",
  review_ok_d: "Your review is live. It helps other travelers, and the club, know what to expect.",
  review_spam_err: "Please remove links from your review.",
  review_duplicate_err: "You already reviewed this club.",
  review_generic_err: "Something went wrong. Please try again.",
  add_photo_cta: "Add a photo",
  add_photo_title: "Share a photo of this club",
  add_photo_sub: "Real photos only, taken by you. Avoid recognisable people who have not agreed to appear here. We review every upload before it goes live.",
  photo_name: "Your name (optional)",
  photo_file: "Choose photo",
  photo_send: "Upload photo",
  photo_sending: "Uploading...",
  photo_ok_t: "Thanks, photo received",
  photo_ok_d: "It will appear on the club page once reviewed.",
  photo_err_type: "Please use a JPG, PNG or WebP photo.",
  photo_err_size: "Photo is too large, max 6 MB.",
  photo_err_generic: "Upload failed. Please try again.",
  nav_faq: "FAQ",
  nav_prices: "Price Index",
  nav_method: "How we verify",
  faq_title: "Frequently asked questions",
  faq_sub: "Everything travelers and club owners ask us most.",
  faq_q1: "Is sunbedly free to use?",
  faq_a1: "Yes. Searching, comparing and sending a request costs nothing. You pay the club directly, exactly like you would by calling or messaging them yourself.",
  faq_q2: "How does booking actually work?",
  faq_a2: "You send a request with your dates and party size. The club confirms directly, usually by WhatsApp or phone. Payment happens at the club, not on sunbedly.",
  faq_q3: "What if a club doesn’t reply?",
  faq_a3: "Most clubs reply within 24 to 48 hours. If you don’t hear back after a few days, message them directly on WhatsApp, or contact us and we’ll follow up with the club.",
  faq_q4: "Are the prices real?",
  faq_a4: "Verified clubs show a published price and the date it was last checked. Unverified listings show \"price on request\" until an owner confirms it directly.",
  faq_q5: "Can I cancel or change my booking?",
  faq_a5: "Contact the club directly, the same way you booked. sunbedly doesn’t process payments or cancellations, the club handles your reservation start to finish.",
  faq_q6: "Do I need an account to search or book?",
  faq_a6: "No. Browsing and sending a request never requires signing up. Club owners create a free listing if they want to manage their page.",
  faq_q7: "How do I list my beach club on sunbedly?",
  faq_a7: "Apply on our partner page. It’s free, there’s no exclusivity, and you can stop at any time.",
  faq_q8: "Which countries does sunbedly cover?",
  faq_a8: "We map coast by coast, and there are now nineteen countries: Italy, Spain, Greece, Croatia, France, Portugal, Turkey, Cyprus, Malta, Montenegro, Albania, the Emirates, Thailand, Indonesia, Mexico, the United States, South Africa, Tanzania and Mauritius. We keep going.",
  meta_clubs_desc: "Browse the beach clubs on sunbedly: real prices, facilities and direct contact, on nineteen countries' coasts from Ibiza to Bali.",
  search_label: "Destination",
  trust_clubs: "beach clubs mapped",
  trust_coasts: "coasts covered",
  trust_free: "free, no account needed",
  destinations_title: "Pick your coast",
  destinations_sub: "Six coastlines mapped club by club. Start where you are headed.",
  explore_region: "Explore",
  why_title: "Why sunbedly",
  why_1_t: "Real prices, before you go",
  why_1_d: "Where a club publishes its rates, we show them. No hidden day-rate surprise once you have your feet in the sand.",
  why_2_t: "Reach the club direct",
  why_2_d: "Call, WhatsApp or email straight from the page. No middleman sitting between you and the beach.",
  why_3_t: "No account, no booking fee",
  why_3_d: "Search, compare and send a request without signing up. You pay the club, we never touch your money.",
  contact_call: "Call",
  near_word: "near",
  oh_daily: "Daily",
  oh_in_season: "in season",
  oh_summer: "Summer season",
  co_italy: "Italy",
  co_spain: "Spain",
  co_greece: "Greece",
  co_croatia: "Croatia",
  co_france: "France",
  co_portugal: "Portugal",
  co_uae: "United Arab Emirates",
  co_netherlands: "Netherlands",
  co_mexico: "Mexico",
  co_indonesia: "Indonesia",
  co_united_states: "United States",
  co_thailand: "Thailand",
  co_turkey: "Turkey",
  co_south_africa: "South Africa",
  co_tanzania: "Tanzania",
  co_mauritius: "Mauritius",
  co_malta: "Malta",
  co_montenegro: "Montenegro",
  co_albania: "Albania",
  co_cyprus: "Cyprus",
  pu_bed: "per sunbed",
  pu_bed_parasol: "sunbed with parasol",
  pu_two_beds: "2 sunbeds with parasol",
  pu_umbrella: "per parasol pitch",
  pu_person: "per person",
  pu_spot: "per spot",
  pu_daypass: "day pass per person",
  pu_half_day: "half day",
  pu_per_day: "per day",
  pu_per_week: "per week",
  pu_detail: "As the club describes it",
  contact_whatsapp: "WhatsApp",
  contact_email: "Email",
  contact_title: "Reach this club",
  contact_sub: "Most clubs answer fastest on WhatsApp.",
  rq_title: "Ask about a date",
  rq_sub_wa: "We open WhatsApp with your question already written, in the club’s own language.",
  rq_sub_mail: "We open your mail app with the question already written, in the club’s own language.",
  rq_date: "Date",
  rq_beds: "Sunbeds",
  rq_name: "Your name",
  rq_contact: "Phone or email",
  rq_send_wa: "Send on WhatsApp",
  rq_sub_phone: "We note your question down and give you the number. This club takes requests by phone.",
  rq_send_phone: "Get the number",
  rq_ok_title_phone: "Call them and ask this",
  rq_ok_warn_phone: "This club has a landline, so WhatsApp will not reach them. Read the line below out on the phone.",
  rq_send_mail: "Send by email",
  rq_sending_wa: "Opening WhatsApp",
  rq_sending_mail: "Opening your mail app",
  rq_ok_title_wa: "Your request is ready in WhatsApp",
  rq_ok_title_mail: "Your request is ready in your mail app",
  rq_ok_club: "Club",
  rq_ok_who: "In the name of",
  rq_ok_warn_wa:
    "The club does not have your message yet. Press send in WhatsApp, or nothing arrives.",
  rq_ok_warn_mail:
    "The club does not have your message yet. Press send in your mail app, or nothing arrives.",
  rq_ok_reopen_wa: "Open WhatsApp again",
  rq_ok_reopen_mail: "Open your mail app again",
  rq_ok_call: "Call {phone}",
  rq_error: "That did not go through. Use the buttons below to reach the club directly.",
  rq_phone_only: "This club takes requests by phone.",
  rq_sticky: "Ask availability",
  rq_privacy: "Your name and contact go to this club and nowhere else.",
  nav_privacy: "Privacy",
  map_failed: "The map could not load. Everything else on this page still works.",
  sort_label: "Sort",
  sort_relevance: "Our pick",
  sort_rating: "Best rated",
  sort_price_asc: "Price, low to high",
  sort_price_desc: "Price, high to low",
  sort_name: "Name, A to Z",
  filter_has_price: "Only clubs with a published price",
  results_count: "{n} beach clubs",
  results_count_one: "1 beach club",
  updated_by: "Checked by sunbedly on {date}",
  foot_role: "sunbedly lists beach clubs. We are not the seller, we take no commission and we handle no payments: you pay the club, at the club.",
  nav_terms: "Terms",
  ck_title: "Analytics, only if you say so",
  ck_body: "We would like to use Google Analytics to see which coasts people look at. It sets a cookie. Nothing loads until you choose, and refusing changes nothing about how the site works.",
  ck_accept: "Accept",
  ck_reject: "Refuse",
  ck_more: "What we collect",
  ck_settings: "Cookie choice",
  rf_title: "Straight answers about {place}",
  rf_q_price: "What does a sunbed cost in {place}?",
  rf_a_price: "Rates run from {cur} {low} to {cur} {high}, with a middle price of {cur} {mid}. That is measured across the {n} clubs in {place} that publish a rate, out of {total} we list. Checked {date}.",
  rf_q_count: "How many beach clubs are there in {place}?",
  rf_a_count: "sunbedly lists {total} beach clubs in {place}, spread across {towns} towns. {rated} of them carry a Google rating, averaging {rating} out of 5.",
  rf_q_cheap: "Which beach club in {place} is the cheapest?",
  rf_a_cheap: "Of the clubs in {place} that publish a rate, {club} is the lowest at {cur} {low}. Clubs that do not publish a price are not in that comparison, so cheaper ones may exist.",
  rf_q_book: "Can you book a sunbed in {place} online?",
  rf_a_book: "Almost never. Beach clubs in {place} take reservations by phone or WhatsApp on the day. sunbedly writes the message for you in the club's own language and hands you straight to them. No account, no booking fee.",
  region_clubs_title: "Clubs by coast",
  rg_sicily: "Sicily",
  rg_miami: "Miami & Miami Beach",
  rg_tulum: "Tulum & Riviera Maya",
  rg_cape_town: "Cape Town",
  rg_zanzibar: "Zanzibar",
  rg_mauritius: "Mauritius",
  rg_bali: "Bali",
  rg_phuket: "Phuket",
  rg_koh_samui: "Koh Samui",
  rg_abu_dhabi: "Abu Dhabi",
  rg_turkey_aegean: "Bodrum & Çeşme",
  rg_cyprus: "Cyprus",
  rg_malta: "Malta & Gozo",
  rg_canaries: "Canary Islands",
  rg_montenegro: "Montenegro",
  rg_albania: "Albanian Riviera",
  rg_sardinia: "Sardinia",
  rg_cote_azur: "Côte d’Azur",
  rg_puglia: "Puglia",
  rg_amalfi: "Amalfi Coast & Capri",
  rg_romagna: "Riviera Romagnola",
  rg_versilia_liguria: "Versilia & Liguria",
  rg_costa_brava: "Costa Brava",
  rg_costa_blanca: "Costa Blanca",
  rg_costa_del_sol: "Costa del Sol",
  rg_mallorca: "Mallorca",
  rg_ibiza: "Ibiza",
  rg_algarve: "Algarve",
  rg_mykonos_santorini: "Mykonos & Santorini",
  rg_crete_rhodes: "Crete & Rhodes",
  rg_corfu_halkidiki: "Corfu & Halkidiki",
  rg_croatia_south: "South Dalmatia",
  rg_croatia_central: "Central Dalmatia",
  rg_croatia_north: "Istria & Kvarner",
  rg_dubai: "Dubai",
  rg_greece: "Greek Islands",
  filters_show: "Filters",
  filters_hide: "Hide filters",
  filters_active: "{n} active",
  lang_offer: "This page is also in {lang}.",
  lang_switch: "Switch",
  lang_dismiss: "No thanks",
  pg_prev: "Previous",
  pg_next: "Next",
  pg_of: "Page {a} of {b}",
  cl_none_t: "Nothing matches that yet",
  cl_none_d: "Try one filter less, or search a town instead of a coast.",
  cl_clear_all: "Clear everything",
  bk_title: "Log a booking you took yourself",
  bk_sub: "Someone phoned or walked up. Put it here and your day stays in one place, so the beds you have left are the beds we show.",
  bk_name: "Name",
  bk_contact: "Phone or email (optional)",
  bk_date: "Date",
  bk_beds: "Beds",
  bk_note: "Note (optional)",
  bk_add: "Add booking",
  bk_saved: "Added.",
  bk_delete: "Remove",
  bk_by_club: "Taken by you",
  bk_by_site: "Via sunbedly",
  bk_today: "Today",
  bk_beds_today: "{n} beds booked today",
  mg_title: "Club dashboard",
  mg_view_public: "View public page",
  mg_requests: "Booking requests",
  mg_requests_sub: "Travelers who asked about your beds. Confirm one and reply on the contact they left.",
  mg_no_requests: "No requests yet. They appear here the moment a traveler sends one.",
  mg_reservations: "Bed reservations",
  mg_res_sub: "Specific beds picked on your beach map.",
  mg_close_dates: "Close dates",
  mg_close_sub: "Days you are shut, fully booked, or privately hired. Travelers stop seeing beds for these dates straight away.",
  mg_no_closed: "No closed dates. Your beach shows as open on every date.",
  mg_note_opt: "Note (optional)",
  mg_private: "Private event",
  mg_close_btn: "Close this date",
  mg_reopen: "Reopen",
  mg_prices: "Prices and contact",
  mg_photos: "Your photos",
  mg_saved: "Saved and live.",
  mg_confirm: "Confirm",
  mg_decline: "Decline",
  mg_lang: "Language",
  ofaq_title: "Questions you might have",
  ofaq_q1: "How did you get my details?",
  ofaq_a1: "From what your business publishes itself: your own website, and the official Google Places API for your rating and photos. Nothing was bought and nothing was scraped from Instagram or review sites. You can have all of it removed in one email.",
  ofaq_q2: "Who else can open this link?",
  ofaq_a2: "Anyone you forward it to, so treat it like a key. It cannot be guessed. Ask us and we will issue a new one, which makes the old link stop working immediately.",
  ofaq_q3: "What happens to my guests' details?",
  ofaq_a3: "They reach you and stop there. A request shows you the traveler’s own phone or email so you can answer directly. We do not sell it, we do not market to it, and we never take payment, so there is no card data anywhere.",
  ofaq_q4: "Why is my Google rating on the page?",
  ofaq_a4: "Because a page with no rating gets ignored, and yours is already public. It comes through Google’s official API, always credited to them, and it is never mixed into a score of our own.",
  ofaq_q5: "Can I take my page offline?",
  ofaq_a5: "Yes, one email and it is gone, usually the same day. No notice period, no contract, and we will not ask you three times why.",
  ofaq_contact: "Ask us anything",
  region_meta_title: "Beach clubs in {place}",
  region_meta_desc: "{n} beach clubs in {place}: real prices, facilities and direct contact. Compare and reach them yourself on sunbedly.",
  th_kids_area: "Family-friendly beach clubs",
  th_pool: "Beach clubs with a pool",
  th_pets_allowed: "Dog-friendly beach clubs",
  th_accessible: "Wheelchair-accessible beach clubs",
  th_water_sports: "Beach clubs with water sports",
  th_dj: "Beach clubs with a DJ",
  th_cabanas: "Beach clubs with cabanas",
  th_parking: "Beach clubs with parking",
  th_in: "{what} in {place}",
  th_meta: "{what} in {place}: {n} clubs with real prices, facilities and direct contact. Compare them and reach them yourself on sunbedly.",
  th_meta_all: "{what}: {n} clubs across {r} coasts, with real prices, facilities and direct contact on sunbedly.",
  th_intro: "Every club below is listed on what it publishes about its own facilities. Prices and opening hours are shown where the club makes them public, and you contact the club directly.",
  th_browse: "Looking for something specific?",
  th_other_regions: "The same on other coasts",
  th_all_regions: "Choose a coast",
  th_count: "{n} clubs",
  share: "Share",
  share_copied: "Link copied",
  badge_price_shown: "Price shown",
  no_cut_note: "You pay the club, at the club. We never take a cut.",
  no_account_note: "No account, no card, no booking fee.",
  reviews_count_google: "{n} Google reviews",
  region_priced_note: "{n} of these publish what a sunbed costs.",
  contact_none_t: "No direct line yet",
  contact_none_d: "We do not have a phone number for this club. Their own site is the fastest route until they join sunbedly.",
  contact_website: "Visit their website",
  contact_none_bare: "We have no contact details for this club yet. If you run it, claim the page and add them in a minute.",
  fac_bar: "Bar",
  fac_restaurant: "Restaurant",
  fac_showers: "Showers",
  fac_sunbeds: "Sunbeds",
  fac_umbrellas: "Parasols",
  fac_dj: "DJ",
  fac_live_music: "Live music",
  fac_parking: "Parking",
  fac_accessible: "Accessible",
  fac_wifi: "Wifi",
  fac_pool: "Pool",
  fac_terrace: "Terrace",
  fac_toilets: "Toilets",
  fac_kids_area: "Kids area",
  fac_playground: "Playground",
  fac_sea_view: "Sea view",
  fac_water_sports: "Water sports",
  fac_beach_volley: "Beach volleyball",
  fac_beach_tennis: "Beach tennis",
  fac_changing_rooms: "Changing rooms",
  fac_cabanas: "Cabanas",
  fac_lifeguard: "Lifeguard",
  fac_pets_allowed: "Pets allowed",
  fac_events: "Events",
  fac_jetty: "Jetty",
  fac_massage: "Massage",
  fac_card_payment: "Card payment",
  fac_boat_rental: "Boat rental",
  fac_towels: "Towels included",
  fac_sunscreen: "Sunscreen provided",
  fac_bed_service: "Service at your bed",
  fac_lockers: "Lockers",
  fac_spa: "Spa",
  fac_gym: "Gym",
  fac_yoga: "Yoga",
  fac_sports: "Sports and games",
  fac_boat_shuttle: "Boat shuttle",
  fac_sea_access: "Direct sea access",
  fac_lounge: "Lounge area",
  fac_shop: "Shop",
  fac_adults_only: "Adults only",
  fac_nightclub: "Night club",
  fac_grounds: "Gardens and grounds",
  gallery_all: "See all {n} photos",
  gallery_close: "Close",
  gallery_prev: "Previous photo",
  gallery_next: "Next photo",
  photo_by: "Photo: {name} via Google",
  photo_credit: "Photo: {name}",
  photo_google: "Photo via Google",
  photo_nearby: "Beach near {place}",
  photo_nearby_note: "Surroundings photo, not of the club itself",
  nav_photo_credits: "Photo credits",
  nearby_title: "Other beach clubs nearby",
  nearby_sub: "Same coast, different mood.",
  crumb_home: "Home",
  book_panel_title: "Check availability",
  book_panel_sub: "We pass your request straight to the club. No account, no card.",
  guides_eyebrow: "Guides",
  guides_title: "Which beach club is actually worth it",
  guides_sub: "Rankings built from the prices clubs publish themselves, not from who paid for placement. Updated as the coasts change.",
  guides_read: "Read the guide",
  guides_all: "All guides",
  guides_updated: "Updated",
  guides_clubs_in_piece: "The clubs in this guide",
  nav_guides: "Guides",
  filters_title: "Filters",
  filter_priced: "Price shown upfront",
  filters_clear: "Clear filters",
  footer_destinations: "Destinations",
  footer_beach_clubs_in: "Beach clubs in {place}",
  footer_more_coasts: "More coasts are being mapped, one at a time. We would rather cover a coast properly than list a country with two clubs in it.",
  b2b_eyebrow: "For beach club owners",
  b2b_title: "Your beach, your prices, your guests.",
  b2b_sub: "A free page on sunbedly with your real rates, your photos and requests that land straight on your phone. We take no commission and never handle your money.",
  b2b_cta: "List your club",
  b2b_cta_sub: "Free. No contract, no exclusivity, stop whenever you want.",
  b2b_stat_clubs: "clubs already mapped",
  b2b_stat_langs: "languages, one page",
  b2b_stat_fee: "commission, ever",
  b2b_what_title: "What you get",
  b2b_w1_t: "A page that sells for you",
  b2b_w1_d: "Your rates, facilities, opening hours and house rules, in six languages, on a page built to rank. Travelers stop calling to ask what a sunbed costs.",
  b2b_w2_t: "Your own dashboard",
  b2b_w2_d: "Confirm or decline requests, upload photos, change your prices and close dates you are full or shut. One link, no password to forget.",
  b2b_w3_t: "Guests contact you directly",
  b2b_w3_d: "Call, WhatsApp and email buttons point at your number, not ours. You keep the relationship and the guest data.",
  b2b_w4_t: "You keep every euro",
  b2b_w4_d: "The guest pays you at the beach, exactly as they do now. No commission, no service fee skimmed off your guest, no waiting for a payout.",
  b2b_diff_title: "How this differs from the usual platform",
  b2b_diff_us: "sunbedly",
  b2b_diff_them: "Typical booking platform",
  b2b_d1: "Commission on each booking",
  b2b_d1_us: "None",
  b2b_d1_them: "Undisclosed percentage",
  b2b_d2: "Fee charged to your guest",
  b2b_d2_us: "None",
  b2b_d2_them: "Non-refundable service fee",
  b2b_d3: "Who holds the money",
  b2b_d3_us: "You. We never touch it",
  b2b_d3_them: "The platform, then pays out",
  b2b_d4: "Your prices before booking",
  b2b_d4_us: "Public on your page",
  b2b_d4_them: "Hidden until checkout",
  b2b_d5: "Guest contact details",
  b2b_d5_us: "Straight to you",
  b2b_d5_them: "Held by the platform",
  b2b_d6: "Exclusivity",
  b2b_d6_us: "None, leave anytime",
  b2b_d6_them: "Often contracted",
  b2b_honest_title: "Where we are honest with you",
  b2b_honest_d: "We are early. sunbedly maps {clubs} clubs and is growing coast by coast, so we are not going to claim millions of users. What we can promise is that your listing costs nothing, your prices are visible before someone travels, and nothing sits between you and your guest. If that stops being true, you can leave the same day.",
  b2b_how_title: "Getting listed",
  b2b_how_1_t: "Send the form",
  b2b_how_1_d: "Club name, town and a number we can reach you on. Two minutes.",
  b2b_how_2_t: "We build your page",
  b2b_how_2_d: "We research your beach and write it up, then send you the dashboard link to check it and fix anything wrong.",
  b2b_how_3_t: "Requests start arriving",
  b2b_how_3_d: "Travelers find you, see your real price, and message you directly.",
  b2b_faq_title: "Questions owners ask",
  b2b_fq1: "What does it cost?",
  b2b_fa1: "Nothing. No listing fee, no commission, no cut of what your guest spends. If we ever introduce a paid option it will be something you choose to buy, not a percentage taken off your bookings.",
  b2b_fq2: "Do I need a booking system?",
  b2b_fa2: "No. Most clubs we work with take bookings on WhatsApp and a paper plan, and that is fine. Your page can simply send people to your phone.",
  b2b_fq3: "Who owns the guest data?",
  b2b_fa3: "You do. Requests arrive with the guest’s own contact details so you can reply directly. We do not sit between you and them.",
  b2b_fq4: "Can I change my prices myself?",
  b2b_fa4: "Yes, from your dashboard, and the change is live immediately. You can also close dates when you are full or shut.",
  b2b_fq5: "What if I want to leave?",
  b2b_fa5: "Tell us and we take the page down. There is no contract, no notice period and no exclusivity clause.",
  about_title: "About this beach club",
  facilities_title: "Facilities",
  hours_title: "Opening hours",
  rules_title: "Good to know",
  good_for: "Good for",
  reviews_google: "Google reviews",
  reviews_sunbedly: "sunbedly reviews",
  hours_unknown: "Opening hours not confirmed yet. Check with the club before you travel.",
  club_faq_title: "Questions about {club}",
  club_title_type: "beach club",
  club_title_in: "beach club in {town}",
  cfaq_q_price: "How much does a sunbed cost at {club}?",
  cfaq_a_price: "A spot at {club} starts at {price}. The club sets its own rates, and prices can change in high season, so treat this as the starting point rather than a quote.",
  cfaq_a_price_none: "{club} has not published its rates, so we show \"price on request\". Message the club directly and they will tell you the current day rate.",
  cfaq_q_book: "Do I need to book ahead at {club}?",
  cfaq_a_book: "In July and August the good rows go early, so booking ahead is worth it. Send a request here and the club confirms directly, usually by WhatsApp.",
  cfaq_q_hours: "When is {club} open?",
  cfaq_q_contact: "How do I contact {club}?",
  cfaq_a_contact: "Call, WhatsApp or email them straight from this page. Most beach clubs answer fastest on WhatsApp.",
  cfaq_q_where: "Where exactly is {club}?",
  cfaq_a_where: "{club} is at {town}. Use the map on this page for directions door to door.",
};

const nl: Dict = {
  nav_explore: "Ontdek clubs",
  nav_partner: "Meld je club aan",
  hero_title_1: "{n} beachclubs.",
  hero_title_2: "Op één plek.",
  hero_sub: "Bekijk echte prijzen en boek je bedje in een paar minuten.",
  search_placeholder: "Waarheen? Plaats, regio of club",
  dest_label: "Bestemming",
  socials_title: "Hun eigen kanalen",
  dest_all: "Alle bestemmingen",
  dest_guide: "Gids voor {place}",
  sug_region: "Kust",
  sug_town: "Plaats",
  sug_club: "Club",
  search_cta: "Zoek beachclubs",
  near_me: "In de buurt",
  locating: "Locatie zoeken...",
  regions_eyebrow: "Regio’s",
  regions_title: "Waar je plek aan zee geregeld is",
  regions_sub: "Van beroemde stranden tot stille baaien, kies een kust en zie wie een bedje voor je heeft.",
  live_title: "Vandaag op het platform",
  live_sub: "We brengen beachclubs zelf in kaart, kust voor kust, van de Middellandse Zee tot Bali en de Cariben. Geverifieerde clubs tonen echte prijzen, de rest wacht op hun eigenaar.",
  all_clubs: "Bekijk alle clubs",
  stats_clubs: "beachclubs",
  stats_regions: "kusten",
  how_title: "Zo werkt het",
  how_1_t: "Vind je strand",
  how_1_d: "Zoek per regio of laat ons in je omgeving zoeken. Elke club in één lijst, niet tien tabbladen.",
  how_2_t: "Vergelijk echte prijzen",
  how_2_d: "Bedjes, parasols en wat erbij zit. Geen rondbellen, geen gokken.",
  how_3_t: "Boek je bedje",
  how_3_d: "Stuur één aanvraag en de club bevestigt rechtstreeks. Je plek is geregeld voordat je aankomt.",
  owners_eyebrow: "Voor eigenaren",
  owners_title: "Heb jij een beachclub?",
  owners_sub: "Ontvang boekingen van reizigers nog voordat ze geland zijn.",
  owners_cta: "Meld je club aan",
  clubs_title: "Beachclubs",
  clubs_found: "clubs gevonden",
  filter_all: "Alle regio’s",
  verified: "Door ons gecheckt",
  unverified: "Nog niet geclaimd",
  from: "vanaf",
  price_on_request: "Vraag de prijs van vandaag",
  km_away: "km hiervandaan",
  view_club: "Bekijk club",
  approx_note: "We tonen de locatie op plaatsniveau.",
  amenity_bed_service: "Bediening aan je bedje",
  amenity_jetty: "Steiger de zee in",
  amenity_lunch: "Lunch en keuken",
  amenity_towels: "Handdoeken inbegrepen",
  amenity_sunscreen: "Zonnebrand inbegrepen",
  amenity_cushions: "Kussens inbegrepen",
  amenity_party: "DJ en events",
  request_title: "Beschikbaarheid aanvragen",
  request_sub: "De club bevestigt rechtstreeks. Je betaalt pas als dat rond is.",
  form_name: "Je naam",
  form_contact: "WhatsApp-nummer of e-mail",
  form_date: "Datum van bezoek",
  form_party: "Personen",
  form_message: "Nog iets? (eerste rij, cabana, lunch)",
  form_send: "Verstuur aanvraag",
  form_sending: "Versturen...",
  request_ok_t: "Aanvraag verstuurd",
  request_ok_d: "We sturen hem meteen door naar de club. Je krijgt antwoord via de gegevens die je hebt achtergelaten.",
  request_ok_expect: "De meeste clubs reageren binnen 24 tot 48 uur.",
  request_ok_fallback: "Na een paar dagen niets gehoord? Stuur ze rechtstreeks een bericht op WhatsApp, of",
  request_error: "Er ging iets mis. Probeer het opnieuw.",
  contact_us: "neem contact met ons op",
  wa_fastest: "Snelste manier om ze te bereiken",
  footer_contact: "Contact",
  footer_contact_note: "Vragen over sunbedly zelf?",
  claim_note: "Is dit jouw club? Claim deze pagina en zet je eigen prijzen erop.",
  claim_cta: "Claim deze club",
  partner_title: "Zet je club waar reizigers zoeken",
  partner_sub: "Een gratis pagina met je prijzen, je extra’s en aanvragen direct op je telefoon. Geen aanmeldkosten, geen exclusiviteit, stoppen wanneer je wilt.",
  partner_p1_t: "Gratis vermelding",
  partner_p1_d: "Je bedjes, prijzen en extra’s op een pagina die werkt, in zes talen.",
  partner_p2_t: "Aanvragen, geen spam",
  partner_p2_d: "Reizigers sturen gestructureerde aanvragen. Jij bevestigt via WhatsApp zoals je al doet.",
  partner_p3_t: "Nieuwe gasten, niet je vaste klanten",
  partner_p3_d: "Wij brengen internationale reizigers die je nu nog niet kunnen vinden.",
  partner_form_club: "Naam van de club",
  partner_form_name: "Contactpersoon",
  partner_form_contact: "WhatsApp-nummer of e-mail",
  partner_form_country: "Land",
  partner_form_town: "Plaats of strand",
  partner_form_instagram: "Instagram (optioneel)",
  partner_form_system: "Gebruik je een boekingssysteem?",
  partner_sys_yes: "Ja",
  partner_sys_no: "Nee",
  partner_sys_unknown: "Weet ik niet",
  partner_form_message: "Vertel over je club (optioneel)",
  partner_send: "Vraag een vermelding aan",
  partner_ok_t: "Aanvraag ontvangen",
  partner_ok_d: "We zetten je pagina klaar en nemen contact op om prijzen en foto’s te bevestigen.",
  footer_tag: "De beachclubs van de wereld, geregeld.",
  footer_travelers: "Reizigers",
  footer_owners: "Clubeigenaren",
  footer_langs: "Taal",
  back_to_clubs: "Alle clubs",
  no_results: "Nog geen clubs gevonden. Probeer een andere regio.",
  wa_book: "Boek via WhatsApp",
  sponsored_badge: "Gesponsord",
  get_directions: "Route",
  show_map: "Toon kaart",
  show_list: "Toon lijst",
  last_checked: "Gecheckt",
  season_may_oct: "Meestal open van mei tot oktober",
  checking_availability: "Beschikbaarheid controleren...",
  route_title: "Zo kom je er",
  map_title: "Kies je plek op het strand",
  map_sub: "Kies een datum en tik de bedjes aan die je wilt. Rij A ligt direct aan het water.",
  sea: "Zee",
  jetty_label: "Steiger",
  row_label: "Rij",
  free_legend: "Vrij",
  taken_legend: "Bezet",
  selected_legend: "Jouw keuze",
  max_spots: "Maximaal 4 sets per reservering.",
  reserve_cta: "Reserveer deze bedjes",
  reserve_ok_t: "Reservering verstuurd",
  reserve_ok_d: "De club bevestigt je plekken rechtstreeks via de gegevens die je hebt achtergelaten. Je betaalt bij de club.",
  conflict_err: "Iemand was je net voor bij een van deze plekken. Kies opnieuw.",
  your_spots: "Jouw plekken",
  reviews_title: "Reviews",
  reviews_count: "reviews",
  no_reviews_yet: "Nog geen reviews. Deel als eerste jouw bezoek.",
  write_review_cta: "Schrijf een review",
  review_form_title: "Deel je bezoek",
  review_form_sub: "Alleen echte bezoeken. Houd het nuttig voor de volgende reiziger.",
  review_name: "Je naam",
  review_contact: "E-mail (niet openbaar zichtbaar)",
  review_rating: "Jouw beoordeling",
  review_headline: "Titel (optioneel)",
  review_body: "Jouw review",
  review_visit_month: "Wanneer was je er?",
  review_booked_via: "Hoe heb je geboekt?",
  booked_whatsapp: "WhatsApp",
  booked_lidoo: "aanvraag via sunbedly",
  booked_phone: "Telefoon",
  booked_walkin: "Gewoon binnengelopen",
  booked_other: "Anders",
  review_send: "Plaats review",
  review_sending: "Plaatsen...",
  review_ok_t: "Bedankt voor het delen",
  review_ok_d: "Je review staat online. Zo weten andere reizigers, en de club, wat ze kunnen verwachten.",
  review_spam_err: "Haal de links uit je review.",
  review_duplicate_err: "Je hebt deze club al beoordeeld.",
  review_generic_err: "Er ging iets mis. Probeer het opnieuw.",
  add_photo_cta: "Voeg een foto toe",
  add_photo_title: "Deel een foto van deze club",
  add_photo_sub: "Alleen echte foto’s die je zelf hebt gemaakt. Vermijd herkenbare personen die daar geen toestemming voor gaven. Wij bekijken elke upload voordat hij online komt.",
  photo_name: "Je naam (optioneel)",
  photo_file: "Kies foto",
  photo_send: "Upload foto",
  photo_sending: "Uploaden...",
  photo_ok_t: "Bedankt, foto ontvangen",
  photo_ok_d: "Hij verschijnt op de clubpagina zodra hij is beoordeeld.",
  photo_err_type: "Gebruik een JPG-, PNG- of WebP-foto.",
  photo_err_size: "Foto is te groot, max 6 MB.",
  photo_err_generic: "Uploaden mislukt. Probeer het opnieuw.",
  nav_faq: "FAQ",
  nav_prices: "Prijsindex",
  nav_method: "Zo controleren wij",
  faq_title: "Veelgestelde vragen",
  faq_sub: "Alles wat reizigers en clubeigenaren ons het meest vragen.",
  faq_q1: "Is sunbedly gratis te gebruiken?",
  faq_a1: "Ja. Zoeken, vergelijken en een aanvraag sturen kost niets. Je betaalt rechtstreeks bij de club, precies zoals wanneer je zelf zou bellen of appen.",
  faq_q2: "Hoe werkt boeken precies?",
  faq_a2: "Je stuurt een aanvraag met je datum en aantal personen. De club bevestigt rechtstreeks, meestal via WhatsApp of telefoon. Je betaalt bij de club, niet op sunbedly.",
  faq_q3: "Wat als een club niet reageert?",
  faq_a3: "De meeste clubs reageren binnen 24 tot 48 uur. Niets gehoord na een paar dagen? Stuur ze rechtstreeks een bericht op WhatsApp, of laat het ons weten, dan gaan wij er bij de club achteraan.",
  faq_q4: "Zijn de prijzen echt?",
  faq_a4: "Geverifieerde clubs tonen een gepubliceerde prijs en de datum waarop die voor het laatst is gecontroleerd. Ongeverifieerde vermeldingen tonen \"prijs op aanvraag\" tot een eigenaar dit rechtstreeks bevestigt.",
  faq_q5: "Kan ik mijn boeking annuleren of wijzigen?",
  faq_a5: "Neem rechtstreeks contact op met de club, op dezelfde manier waarop je hebt geboekt. sunbedly verwerkt geen betalingen of annuleringen, de club regelt je reservering van begin tot eind.",
  faq_q6: "Heb ik een account nodig om te zoeken of te boeken?",
  faq_a6: "Nee. Zoeken en een aanvraag sturen vereist nooit een account. Clubeigenaren maken een gratis vermelding aan als ze hun pagina willen beheren.",
  faq_q7: "Hoe meld ik mijn beachclub aan bij sunbedly?",
  faq_a7: "Meld je aan via onze partnerpagina. Het is gratis, geen exclusiviteit, en je kunt op elk moment stoppen.",
  faq_q8: "Welke landen dekt sunbedly?",
  faq_a8: "We brengen kust voor kust in kaart, en inmiddels staan er negentien landen in: Italië, Spanje, Griekenland, Kroatië, Frankrijk, Portugal, Turkije, Cyprus, Malta, Montenegro, Albanië, de Emiraten, Thailand, Indonesië, Mexico, de Verenigde Staten, Zuid-Afrika, Tanzania en Mauritius. En we gaan door.",
  meta_clubs_desc: "Bekijk de beachclubs op sunbedly: echte prijzen, voorzieningen en rechtstreeks contact, aan de kusten van negentien landen, van Ibiza tot Bali.",
  search_label: "Bestemming",
  trust_clubs: "beachclubs in kaart",
  trust_coasts: "kusten gedekt",
  trust_free: "gratis, geen account nodig",
  destinations_title: "Kies je kust",
  destinations_sub: "Zes kustlijnen, club voor club in kaart gebracht. Begin waar je heen gaat.",
  explore_region: "Bekijk",
  why_title: "Waarom sunbedly",
  why_1_t: "Echte prijzen, voor je gaat",
  why_1_d: "Waar een club zijn tarieven publiceert, laten we ze zien. Geen verrassing over de dagprijs als je al met je voeten in het zand staat.",
  why_2_t: "Direct contact met de club",
  why_2_d: "Bel, app of mail rechtstreeks vanaf de pagina. Geen tussenpersoon tussen jou en het strand.",
  why_3_t: "Geen account, geen boekingskosten",
  why_3_d: "Zoeken, vergelijken en een aanvraag sturen zonder je aan te melden. Je betaalt de club, wij komen nooit aan je geld.",
  contact_call: "Bellen",
  near_word: "bij",
  oh_daily: "Dagelijks",
  oh_in_season: "in het seizoen",
  oh_summer: "Zomerseizoen",
  co_italy: "Italië",
  co_spain: "Spanje",
  co_greece: "Griekenland",
  co_croatia: "Kroatië",
  co_france: "Frankrijk",
  co_portugal: "Portugal",
  co_uae: "Verenigde Arabische Emiraten",
  co_netherlands: "Nederland",
  co_mexico: "Mexico",
  co_indonesia: "Indonesië",
  co_united_states: "Verenigde Staten",
  co_thailand: "Thailand",
  co_turkey: "Turkije",
  co_south_africa: "Zuid-Afrika",
  co_tanzania: "Tanzania",
  co_mauritius: "Mauritius",
  co_malta: "Malta",
  co_montenegro: "Montenegro",
  co_albania: "Albanië",
  co_cyprus: "Cyprus",
  pu_bed: "per bedje",
  pu_bed_parasol: "bedje met parasol",
  pu_two_beds: "2 bedjes met parasol",
  pu_umbrella: "per parasolplek",
  pu_person: "per persoon",
  pu_spot: "per plek",
  pu_daypass: "dagkaart per persoon",
  pu_half_day: "halve dag",
  pu_per_day: "per dag",
  pu_per_week: "per week",
  pu_detail: "Zoals de club het omschrijft",
  contact_whatsapp: "WhatsApp",
  contact_email: "Mailen",
  contact_title: "Contact met deze club",
  contact_sub: "De meeste clubs reageren het snelst op WhatsApp.",
  rq_title: "Vraag een datum aan",
  rq_sub_wa: "We openen WhatsApp met je vraag er al in, in de taal van de club.",
  rq_sub_mail: "We openen je mailprogramma met de vraag er al in, in de taal van de club.",
  rq_date: "Datum",
  rq_beds: "Bedjes",
  rq_name: "Je naam",
  rq_contact: "Telefoon of e-mail",
  rq_send_wa: "Versturen via WhatsApp",
  rq_sub_phone: "Wij noteren je vraag en geven je het nummer. Deze club neemt aanvragen telefonisch aan.",
  rq_send_phone: "Toon het nummer",
  rq_ok_title_phone: "Bel ze en vraag dit",
  rq_ok_warn_phone: "Deze club heeft een vaste lijn, dus WhatsApp komt niet aan. Lees de regel hieronder voor aan de telefoon.",
  rq_send_mail: "Versturen per e-mail",
  rq_sending_wa: "WhatsApp openen",
  rq_sending_mail: "Je e-mail openen",
  rq_ok_title_wa: "Je aanvraag staat klaar in WhatsApp",
  rq_ok_title_mail: "Je aanvraag staat klaar in je e-mail",
  rq_ok_club: "Club",
  rq_ok_who: "Op naam van",
  rq_ok_warn_wa:
    "De club heeft je bericht nog niet. Druk in WhatsApp nog op verzenden, anders komt er niets aan.",
  rq_ok_warn_mail:
    "De club heeft je bericht nog niet. Druk in je e-mail nog op verzenden, anders komt er niets aan.",
  rq_ok_reopen_wa: "WhatsApp opnieuw openen",
  rq_ok_reopen_mail: "E-mail opnieuw openen",
  rq_ok_call: "Bellen: {phone}",
  rq_error: "Dat lukte niet. Gebruik de knoppen hieronder om de club direct te bereiken.",
  rq_phone_only: "Deze club neemt aanvragen telefonisch aan.",
  rq_sticky: "Beschikbaarheid",
  rq_privacy: "Je naam en contactgegevens gaan naar deze club en nergens anders heen.",
  nav_privacy: "Privacy",
  map_failed: "De kaart kon niet laden. De rest van deze pagina werkt gewoon.",
  sort_label: "Sorteren",
  sort_relevance: "Onze volgorde",
  sort_rating: "Best beoordeeld",
  sort_price_asc: "Prijs, laag naar hoog",
  sort_price_desc: "Prijs, hoog naar laag",
  sort_name: "Naam, A tot Z",
  filter_has_price: "Alleen clubs met een gepubliceerde prijs",
  results_count: "{n} beachclubs",
  results_count_one: "1 beachclub",
  updated_by: "Gecontroleerd door sunbedly op {date}",
  foot_role: "sunbedly vermeldt beachclubs. Wij zijn niet de verkoper, wij rekenen geen commissie en verwerken geen betalingen: je betaalt de club, bij de club.",
  nav_terms: "Voorwaarden",
  ck_title: "Statistieken, alleen als jij dat wilt",
  ck_body: "We gebruiken graag Google Analytics om te zien naar welke kusten mensen kijken. Dat zet een cookie. Er laadt niets tot jij kiest, en weigeren verandert niets aan hoe de site werkt.",
  ck_accept: "Accepteren",
  ck_reject: "Weigeren",
  ck_more: "Wat we verzamelen",
  ck_settings: "Cookiekeuze",
  rf_title: "Directe antwoorden over {place}",
  rf_q_price: "Wat kost een ligbed in {place}?",
  rf_a_price: "De tarieven lopen van {cur} {low} tot {cur} {high}, met {cur} {mid} als middenprijs. Gemeten over de {n} clubs in {place} die een tarief publiceren, van de {total} die we vermelden. Gecontroleerd {date}.",
  rf_q_count: "Hoeveel beachclubs zijn er in {place}?",
  rf_a_count: "sunbedly vermeldt {total} beachclubs in {place}, verdeeld over {towns} plaatsen. {rated} daarvan hebben een Google-beoordeling, gemiddeld {rating} van de 5.",
  rf_q_cheap: "Welke beachclub in {place} is het goedkoopst?",
  rf_a_cheap: "Van de clubs in {place} die een tarief publiceren is {club} de laagste, met {cur} {low}. Clubs zonder gepubliceerde prijs zitten niet in die vergelijking, dus goedkopere kunnen bestaan.",
  rf_q_book: "Kun je online een ligbed boeken in {place}?",
  rf_a_book: "Bijna nooit. Beachclubs in {place} nemen reserveringen aan per telefoon of WhatsApp, meestal op de dag zelf. sunbedly schrijft het bericht voor je in de taal van de club en zet je er direct naartoe. Geen account, geen boekingskosten.",
  region_clubs_title: "Clubs per kust",
  rg_sicily: "Sicilië",
  rg_miami: "Miami en Miami Beach",
  rg_tulum: "Tulum en Riviera Maya",
  rg_cape_town: "Kaapstad",
  rg_zanzibar: "Zanzibar",
  rg_mauritius: "Mauritius",
  rg_bali: "Bali",
  rg_phuket: "Phuket",
  rg_koh_samui: "Koh Samui",
  rg_abu_dhabi: "Abu Dhabi",
  rg_turkey_aegean: "Bodrum en Çeşme",
  rg_cyprus: "Cyprus",
  rg_malta: "Malta en Gozo",
  rg_canaries: "Canarische Eilanden",
  rg_montenegro: "Montenegro",
  rg_albania: "Albanese Rivièra",
  rg_sardinia: "Sardinië",
  rg_cote_azur: "Côte d’Azur",
  rg_puglia: "Puglia",
  rg_amalfi: "Amalfikust en Capri",
  rg_romagna: "Riviera Romagnola",
  rg_versilia_liguria: "Versilia en Ligurië",
  rg_costa_brava: "Costa Brava",
  rg_costa_blanca: "Costa Blanca",
  rg_costa_del_sol: "Costa del Sol",
  rg_mallorca: "Mallorca",
  rg_ibiza: "Ibiza",
  rg_algarve: "Algarve",
  rg_mykonos_santorini: "Mykonos en Santorini",
  rg_crete_rhodes: "Kreta en Rhodos",
  rg_corfu_halkidiki: "Corfu en Chalkidiki",
  rg_croatia_south: "Zuid-Dalmatië",
  rg_croatia_central: "Midden-Dalmatië",
  rg_croatia_north: "Istrië en Kvarner",
  rg_dubai: "Dubai",
  rg_greece: "Griekse eilanden",
  filters_show: "Filters",
  filters_hide: "Filters verbergen",
  filters_active: "{n} actief",
  lang_offer: "Deze pagina staat ook in het {lang}.",
  lang_switch: "Overschakelen",
  lang_dismiss: "Nee, dank je",
  pg_prev: "Vorige",
  pg_next: "Volgende",
  pg_of: "Pagina {a} van {b}",
  cl_none_t: "Hier komt nog niets uit",
  cl_none_d: "Probeer een filter minder, of zoek op een plaats in plaats van een kust.",
  cl_clear_all: "Alles wissen",
  bk_title: "Zet een boeking erin die je zelf aannam",
  bk_sub: "Iemand belde of stond aan de bar. Zet het hier neer, dan staat je dag op één plek en kloppen de bedjes die wij tonen.",
  bk_name: "Naam",
  bk_contact: "Telefoon of e-mail (optioneel)",
  bk_date: "Datum",
  bk_beds: "Bedjes",
  bk_note: "Notitie (optioneel)",
  bk_add: "Boeking toevoegen",
  bk_saved: "Toegevoegd.",
  bk_delete: "Verwijderen",
  bk_by_club: "Door jou aangenomen",
  bk_by_site: "Via sunbedly",
  bk_today: "Vandaag",
  bk_beds_today: "{n} bedjes vandaag geboekt",
  mg_title: "Clubdashboard",
  mg_view_public: "Bekijk je publieke pagina",
  mg_requests: "Aanvragen",
  mg_requests_sub: "Reizigers die naar je bedjes vroegen. Bevestig er een en reageer op het contact dat ze achterlieten.",
  mg_no_requests: "Nog geen aanvragen. Ze verschijnen hier zodra een reiziger er een stuurt.",
  mg_reservations: "Bedreserveringen",
  mg_res_sub: "Specifieke bedjes gekozen op je strandplattegrond.",
  mg_close_dates: "Dagen sluiten",
  mg_close_sub: "Dagen dat je dicht bent, vol zit of privé verhuurd. Reizigers zien voor die dagen meteen geen bedjes meer.",
  mg_no_closed: "Geen gesloten dagen. Je strand staat op elke datum als open.",
  mg_note_opt: "Notitie (optioneel)",
  mg_private: "Privé-evenement",
  mg_close_btn: "Deze dag sluiten",
  mg_reopen: "Heropenen",
  mg_prices: "Prijzen en contact",
  mg_photos: "Je foto’s",
  mg_saved: "Opgeslagen en live.",
  mg_confirm: "Bevestigen",
  mg_decline: "Afwijzen",
  mg_lang: "Taal",
  ofaq_title: "Vragen die je vast hebt",
  ofaq_q1: "Hoe kom je aan mijn gegevens?",
  ofaq_a1: "Uit wat je bedrijf zelf publiceert: je eigen website, en de officiële Google Places API voor je beoordeling en foto’s. Niets gekocht, niets van Instagram of reviewsites geplukt. Je kunt alles met één mail laten verwijderen.",
  ofaq_q2: "Wie kan deze link nog meer openen?",
  ofaq_a2: "Iedereen aan wie je hem doorstuurt, dus behandel hem als een sleutel. Raden kan niet. Vraag ons om een nieuwe, dan werkt de oude meteen niet meer.",
  ofaq_q3: "Wat gebeurt er met de gegevens van mijn gasten?",
  ofaq_a3: "Die komen bij jou en gaan niet verder. Bij een aanvraag zie je het telefoonnummer of mailadres van de reiziger zelf, zodat je direct kunt antwoorden. Wij verkopen het niet, mailen er niet naartoe, en innen nooit geld, dus er staan nergens kaartgegevens.",
  ofaq_q4: "Waarom staat mijn Google-beoordeling erop?",
  ofaq_a4: "Omdat een pagina zonder beoordeling wordt overgeslagen, en die van jou is al openbaar. Hij komt via de officiële API van Google, staat altijd op hun naam, en wordt nooit met een eigen cijfer vermengd.",
  ofaq_q5: "Kan ik mijn pagina offline halen?",
  ofaq_a5: "Ja, één mail en hij is weg, meestal dezelfde dag. Geen opzegtermijn, geen contract, en we vragen niet drie keer waarom.",
  ofaq_contact: "Stel ons gerust een vraag",
  region_meta_title: "Beachclubs in {place}",
  region_meta_desc: "{n} beachclubs in {place}: echte prijzen, voorzieningen en rechtstreeks contact. Vergelijk ze en bel of app ze zelf via sunbedly.",
  th_kids_area: "Beachclubs voor gezinnen",
  th_pool: "Beachclubs met zwembad",
  th_pets_allowed: "Hondvriendelijke beachclubs",
  th_accessible: "Rolstoeltoegankelijke beachclubs",
  th_water_sports: "Beachclubs met watersport",
  th_dj: "Beachclubs met dj",
  th_cabanas: "Beachclubs met cabana’s",
  th_parking: "Beachclubs met parkeergelegenheid",
  th_in: "{what} in {place}",
  th_meta: "{what} in {place}: {n} clubs met echte prijzen, voorzieningen en rechtstreeks contact. Vergelijk ze en bel of app ze zelf via sunbedly.",
  th_meta_all: "{what}: {n} clubs aan {r} kusten, met echte prijzen, voorzieningen en rechtstreeks contact via sunbedly.",
  th_intro: "Elke club hieronder staat hier op grond van wat hij zelf over zijn voorzieningen publiceert. Prijzen en openingstijden staan erbij waar de club ze openbaar maakt, en je neemt zelf rechtstreeks contact op.",
  th_browse: "Zoek op wat jij nodig hebt",
  th_other_regions: "Hetzelfde aan andere kusten",
  th_all_regions: "Kies een kust",
  th_count: "{n} clubs",
  share: "Delen",
  share_copied: "Link gekopieerd",
  badge_price_shown: "Prijs bekend",
  no_cut_note: "Je betaalt de club, bij de club. Wij pakken er niets tussenuit.",
  no_account_note: "Geen account, geen creditcard, geen boekingskosten.",
  reviews_count_google: "{n} Google-reviews",
  region_priced_note: "{n} hiervan publiceren wat een bedje kost.",
  contact_none_t: "Nog geen directe lijn",
  contact_none_d: "We hebben geen telefoonnummer van deze club. Hun eigen site is voorlopig de snelste route, tot ze zich bij sunbedly aansluiten.",
  contact_website: "Naar hun website",
  contact_none_bare: "We hebben nog geen contactgegevens van deze club. Ben jij de eigenaar? Claim de pagina en zet ze er in een minuut bij.",
  fac_bar: "Bar",
  fac_restaurant: "Restaurant",
  fac_showers: "Douches",
  fac_sunbeds: "Ligbedden",
  fac_umbrellas: "Parasols",
  fac_dj: "DJ",
  fac_live_music: "Livemuziek",
  fac_parking: "Parkeren",
  fac_accessible: "Toegankelijk",
  fac_wifi: "Wifi",
  fac_pool: "Zwembad",
  fac_terrace: "Terras",
  fac_toilets: "Toiletten",
  fac_kids_area: "Kinderhoek",
  fac_playground: "Speeltuin",
  fac_sea_view: "Zeezicht",
  fac_water_sports: "Watersport",
  fac_beach_volley: "Beachvolleybal",
  fac_beach_tennis: "Beachtennis",
  fac_changing_rooms: "Kleedhokjes",
  fac_cabanas: "Cabana’s",
  fac_lifeguard: "Strandwacht",
  fac_pets_allowed: "Huisdieren welkom",
  fac_events: "Evenementen",
  fac_jetty: "Steiger",
  fac_massage: "Massage",
  fac_card_payment: "Pinnen",
  fac_boat_rental: "Bootverhuur",
  fac_towels: "Handdoeken inbegrepen",
  fac_sunscreen: "Zonnebrand aanwezig",
  fac_bed_service: "Bediening aan je bed",
  fac_lockers: "Lockers",
  fac_spa: "Spa",
  fac_gym: "Sportschool",
  fac_yoga: "Yoga",
  fac_sports: "Sport en spel",
  fac_boat_shuttle: "Bootpendel",
  fac_sea_access: "Direct de zee in",
  fac_lounge: "Loungehoek",
  fac_shop: "Winkel",
  fac_adults_only: "Alleen volwassenen",
  fac_nightclub: "Nachtclub",
  fac_grounds: "Tuin en terrein",
  gallery_all: "Bekijk alle {n} foto’s",
  gallery_close: "Sluiten",
  gallery_prev: "Vorige foto",
  gallery_next: "Volgende foto",
  photo_by: "Foto: {name} via Google",
  photo_credit: "Foto: {name}",
  photo_google: "Foto via Google",
  photo_nearby: "Strand bij {place}",
  photo_nearby_note: "Omgevingsfoto, niet van de club zelf",
  nav_photo_credits: "Fotoverantwoording",
  nearby_title: "Andere beachclubs in de buurt",
  nearby_sub: "Zelfde kust, andere sfeer.",
  crumb_home: "Home",
  book_panel_title: "Beschikbaarheid checken",
  book_panel_sub: "Wij sturen je aanvraag rechtstreeks naar de club. Geen account, geen creditcard.",
  guides_eyebrow: "Gidsen",
  guides_title: "Welke beachclub het echt waard is",
  guides_sub: "Ranglijsten op basis van prijzen die clubs zelf publiceren, niet op basis van wie voor een plek betaalt. Bijgewerkt zodra er iets aan de kust verandert.",
  guides_read: "Lees de gids",
  guides_all: "Alle gidsen",
  guides_updated: "Bijgewerkt",
  guides_clubs_in_piece: "De clubs uit deze gids",
  nav_guides: "Gidsen",
  filters_title: "Filters",
  filter_priced: "Prijs vooraf bekend",
  filters_clear: "Wis filters",
  footer_destinations: "Bestemmingen",
  footer_beach_clubs_in: "Beachclubs in {place}",
  footer_more_coasts: "We brengen steeds een nieuwe kust in kaart, één voor één. Liever één kust goed dan een land met twee clubs erin.",
  b2b_eyebrow: "Voor beachclub-eigenaren",
  b2b_title: "Jouw strand, jouw prijzen, jouw gasten.",
  b2b_sub: "Een gratis pagina op sunbedly met je echte tarieven, je foto’s en aanvragen die direct op je telefoon binnenkomen. Wij rekenen geen commissie en komen nooit aan je geld.",
  b2b_cta: "Meld je club aan",
  b2b_cta_sub: "Gratis. Geen contract, geen exclusiviteit, stoppen wanneer je wilt.",
  b2b_stat_clubs: "clubs in kaart gebracht",
  b2b_stat_langs: "talen, één pagina",
  b2b_stat_fee: "commissie, nooit",
  b2b_what_title: "Wat je krijgt",
  b2b_w1_t: "Een pagina die voor je verkoopt",
  b2b_w1_d: "Je tarieven, voorzieningen, openingstijden en huisregels, in zes talen, op een pagina die gebouwd is om gevonden te worden. Reizigers bellen niet meer om te vragen wat een bedje kost.",
  b2b_w2_t: "Je eigen dashboard",
  b2b_w2_d: "Aanvragen bevestigen of weigeren, foto’s uploaden, prijzen aanpassen en dagen sluiten waarop je vol of dicht bent. Eén link, geen wachtwoord om te vergeten.",
  b2b_w3_t: "Gasten nemen direct contact op",
  b2b_w3_d: "De bel-, WhatsApp- en mailknoppen wijzen naar jouw nummer, niet naar het onze. Jij houdt het contact en de gastgegevens.",
  b2b_w4_t: "Je houdt elke euro",
  b2b_w4_d: "De gast betaalt bij jou op het strand, precies zoals nu. Geen commissie, geen servicekosten die je gast er bovenop betaalt, geen wachten op uitbetaling.",
  b2b_diff_title: "Hoe dit verschilt van het gebruikelijke platform",
  b2b_diff_us: "sunbedly",
  b2b_diff_them: "Gangbaar boekingsplatform",
  b2b_d1: "Commissie per boeking",
  b2b_d1_us: "Geen",
  b2b_d1_them: "Onbekend percentage",
  b2b_d2: "Kosten voor je gast",
  b2b_d2_us: "Geen",
  b2b_d2_them: "Servicekosten die je niet terugkrijgt",
  b2b_d3: "Wie het geld houdt",
  b2b_d3_us: "Jij. Wij komen er niet aan",
  b2b_d3_them: "Het platform, betaalt later uit",
  b2b_d4: "Je prijzen vóór het boeken",
  b2b_d4_us: "Openbaar op je pagina",
  b2b_d4_them: "Pas zichtbaar bij het afrekenen",
  b2b_d5: "Contactgegevens van de gast",
  b2b_d5_us: "Rechtstreeks naar jou",
  b2b_d5_them: "In handen van het platform",
  b2b_d6: "Exclusiviteit",
  b2b_d6_us: "Geen, stop wanneer je wilt",
  b2b_d6_them: "Vaak contractueel",
  b2b_honest_title: "Waar we eerlijk tegen je zijn",
  b2b_honest_d: "We zijn nieuw. sunbedly heeft {clubs} clubs in kaart en groeit kust voor kust, dus we gaan niet beweren dat we miljoenen gebruikers hebben. Wat we wel beloven: je vermelding kost niets, je prijzen zijn zichtbaar voordat iemand afreist, en er zit niets tussen jou en je gast. Klopt dat niet meer, dan stop je dezelfde dag.",
  b2b_how_title: "Aanmelden",
  b2b_how_1_t: "Stuur het formulier in",
  b2b_how_1_d: "Naam van de club, plaats en een nummer waarop we je kunnen bereiken. Twee minuten.",
  b2b_how_2_t: "Wij bouwen je pagina",
  b2b_how_2_d: "We zoeken alles over je strand op en schrijven je pagina. Daarna krijg je de dashboardlink om alles na te kijken en te corrigeren.",
  b2b_how_3_t: "De aanvragen komen binnen",
  b2b_how_3_d: "Reizigers vinden je, zien je echte prijs en sturen je rechtstreeks een bericht.",
  b2b_faq_title: "Wat eigenaren vragen",
  b2b_fq1: "Wat kost het?",
  b2b_fa1: "Niets. Geen aanmeldkosten, geen commissie, geen deel van wat je gast uitgeeft. Komt er ooit een betaalde optie, dan is dat iets wat je zelf kiest te kopen, geen percentage van je boekingen.",
  b2b_fq2: "Heb ik een boekingssysteem nodig?",
  b2b_fa2: "Nee. De meeste clubs waar we mee werken nemen boekingen aan via WhatsApp en een papieren plattegrond, en dat is prima. Je pagina kan mensen gewoon naar je telefoon sturen.",
  b2b_fq3: "Van wie zijn de gastgegevens?",
  b2b_fa3: "Van jou. Aanvragen komen binnen met de contactgegevens van de gast zelf, zodat je direct kunt antwoorden. Wij zitten er niet tussen.",
  b2b_fq4: "Kan ik mijn prijzen zelf aanpassen?",
  b2b_fa4: "Ja, vanuit je dashboard, en de wijziging staat meteen live. Je kunt ook dagen sluiten waarop je vol of dicht bent.",
  b2b_fq5: "En als ik wil stoppen?",
  b2b_fa5: "Zeg het en we halen de pagina offline. Geen contract, geen opzegtermijn, geen exclusiviteitsclausule.",
  about_title: "Over deze beachclub",
  facilities_title: "Voorzieningen",
  hours_title: "Openingstijden",
  rules_title: "Goed om te weten",
  good_for: "Geschikt voor",
  reviews_google: "Google-reviews",
  reviews_sunbedly: "sunbedly-reviews",
  hours_unknown: "Openingstijden nog niet bevestigd. Check even bij de club voordat je gaat.",
  club_faq_title: "Vragen over {club}",
  club_title_type: "beachclub",
  club_title_in: "beachclub in {town}",
  cfaq_q_price: "Wat kost een bedje bij {club}?",
  cfaq_a_price: "Een plek bij {club} begint bij {price}. De club bepaalt zelf de tarieven en in het hoogseizoen kunnen die anders liggen, dus zie dit als startprijs en niet als offerte.",
  cfaq_a_price_none: "{club} heeft geen tarieven gepubliceerd, daarom staat er \"prijs op aanvraag\". Stuur de club een bericht, dan hoor je de dagprijs.",
  cfaq_q_book: "Moet ik vooraf reserveren bij {club}?",
  cfaq_a_book: "In juli en augustus zijn de goede rijen snel weg, dus vooraf regelen loont. Stuur hier een aanvraag, de club bevestigt rechtstreeks, meestal via WhatsApp.",
  cfaq_q_hours: "Wanneer is {club} open?",
  cfaq_q_contact: "Hoe bereik ik {club}?",
  cfaq_a_contact: "Bel, app of mail ze rechtstreeks vanaf deze pagina. De meeste beachclubs reageren het snelst op WhatsApp.",
  cfaq_q_where: "Waar ligt {club} precies?",
  cfaq_a_where: "{club} ligt in {town}. Gebruik de kaart op deze pagina voor de route van deur tot deur.",
};

const it: Dict = {
  nav_explore: "Scopri i lidi",
  nav_partner: "Registra il tuo lido",
  hero_title_1: "{n} lidi.",
  hero_title_2: "In un posto solo.",
  hero_sub: "Prezzi veri e il tuo lettino prenotato in pochi minuti.",
  search_placeholder: "Dove? Città, regione o lido",
  dest_label: "Destinazione",
  socials_title: "I loro canali",
  dest_all: "Tutte le destinazioni",
  dest_guide: "Guida a {place}",
  sug_region: "Costa",
  sug_town: "Località",
  sug_club: "Lido",
  search_cta: "Cerca beach club",
  near_me: "Vicino a me",
  locating: "Ti localizziamo...",
  regions_eyebrow: "Regioni",
  regions_title: "Dove il mare è già sistemato",
  regions_sub: "Dalle spiagge famose alle calette tranquille, scegli una costa e vedi chi ha un lettino per te.",
  live_title: "Oggi sulla piattaforma",
  live_sub: "Mappiamo personalmente i beach club, costa per costa, dal Mediterraneo a Bali e ai Caraibi. Quelli verificati mostrano prezzi veri, gli altri aspettano che il gestore li rivendichi.",
  all_clubs: "Vedi tutti i lidi",
  stats_clubs: "beach club",
  stats_regions: "coste",
  how_title: "Come funziona",
  how_1_t: "Trova la tua spiaggia",
  how_1_d: "Cerca per regione o guarda cosa c’è vicino a te. Tutti i lidi in una lista sola, non in dieci pagine diverse.",
  how_2_t: "Confronta prezzi veri",
  how_2_d: "Lettini, ombrelloni e cosa è incluso. Niente telefonate in giro, niente prezzi da indovinare.",
  how_3_t: "Prenota il tuo lettino",
  how_3_d: "Invia una richiesta e il lido conferma direttamente. Il posto è tuo prima di arrivare.",
  owners_eyebrow: "Per i gestori",
  owners_title: "Gestisci un lido?",
  owners_sub: "Ricevi prenotazioni dai turisti prima ancora che atterrino.",
  owners_cta: "Registra il tuo lido",
  clubs_title: "Beach club",
  clubs_found: "lidi trovati",
  filter_all: "Tutte le regioni",
  verified: "Verificato da noi",
  unverified: "Non ancora rivendicato",
  from: "da",
  price_on_request: "Chiedi il prezzo di oggi",
  km_away: "km da te",
  view_club: "Vedi il lido",
  approx_note: "Posizione mostrata a livello di località.",
  amenity_bed_service: "Servizio al lettino",
  amenity_jetty: "Pontile sul mare",
  amenity_lunch: "Pranzo e cucina",
  amenity_towels: "Teli mare inclusi",
  amenity_sunscreen: "Crema solare inclusa",
  amenity_cushions: "Cuscini inclusi",
  amenity_party: "DJ ed eventi",
  request_title: "Chiedi disponibilità",
  request_sub: "Il lido conferma direttamente. Non paghi finché non confermano.",
  form_name: "Il tuo nome",
  form_contact: "Numero WhatsApp o email",
  form_date: "Data della visita",
  form_party: "Persone",
  form_message: "Altro? (prima fila, gazebo, pranzo)",
  form_send: "Invia richiesta",
  form_sending: "Invio...",
  request_ok_t: "Richiesta inviata",
  request_ok_d: "La passiamo subito al lido. Riceverai risposta sul contatto che hai lasciato.",
  request_ok_expect: "La maggior parte dei lidi risponde entro 24-48 ore.",
  request_ok_fallback: "Nessuna risposta dopo qualche giorno? Scrivi direttamente su WhatsApp, oppure",
  request_error: "Qualcosa è andato storto. Riprova.",
  contact_us: "contattaci",
  wa_fastest: "Il modo più veloce per contattarli",
  footer_contact: "Contatti",
  footer_contact_note: "Domande su sunbedly?",
  claim_note: "È il tuo lido? Rivendica questa pagina e metti i tuoi prezzi.",
  claim_cta: "Rivendica questo lido",
  partner_title: "Metti il tuo lido dove guardano i turisti",
  partner_sub: "Una pagina gratuita con i tuoi prezzi, i tuoi extra e richieste dirette sul telefono. Nessun costo di ingresso, nessuna esclusiva.",
  partner_p1_t: "Scheda gratuita",
  partner_p1_d: "Lettini, prezzi ed extra su una pagina che funziona, in sei lingue.",
  partner_p2_t: "Richieste, non spam",
  partner_p2_d: "I turisti inviano richieste strutturate. Tu confermi su WhatsApp come fai già.",
  partner_p3_t: "Ospiti nuovi, non i tuoi abituali",
  partner_p3_d: "Portiamo turisti internazionali che oggi non riescono a trovarti.",
  partner_form_club: "Nome del lido",
  partner_form_name: "Referente",
  partner_form_contact: "Numero WhatsApp o email",
  partner_form_country: "Paese",
  partner_form_town: "Località o spiaggia",
  partner_form_instagram: "Instagram (facoltativo)",
  partner_form_system: "Usi un gestionale?",
  partner_sys_yes: "Sì",
  partner_sys_no: "No",
  partner_sys_unknown: "Non lo so",
  partner_form_message: "Racconta il tuo lido (facoltativo)",
  partner_send: "Richiedi la scheda",
  partner_ok_t: "Richiesta ricevuta",
  partner_ok_d: "Prepariamo la tua pagina e ti contattiamo per confermare prezzi e foto.",
  footer_tag: "I lidi del mondo, tutti in un posto.",
  footer_travelers: "Viaggiatori",
  footer_owners: "Gestori",
  footer_langs: "Lingua",
  back_to_clubs: "Tutti i lidi",
  no_results: "Nessun lido trovato. Prova un’altra regione.",
  wa_book: "Prenota su WhatsApp",
  sponsored_badge: "Sponsorizzato",
  get_directions: "Indicazioni",
  show_map: "Mostra mappa",
  show_list: "Mostra elenco",
  last_checked: "Verificato",
  season_may_oct: "Solitamente aperto da maggio a ottobre",
  checking_availability: "Controllo disponibilità...",
  route_title: "Come arrivare",
  map_title: "Scegli il tuo posto in spiaggia",
  map_sub: "Scegli una data e tocca i lettini che vuoi. La fila A è in riva al mare.",
  sea: "Mare",
  jetty_label: "Pontile",
  row_label: "Fila",
  free_legend: "Libero",
  taken_legend: "Occupato",
  selected_legend: "La tua scelta",
  max_spots: "Massimo 4 postazioni per prenotazione.",
  reserve_cta: "Prenota questi lettini",
  reserve_ok_t: "Prenotazione inviata",
  reserve_ok_d: "Il lido conferma i tuoi posti sul contatto che hai lasciato. Paghi al lido.",
  conflict_err: "Qualcuno ha appena preso uno di questi posti. Scegli di nuovo.",
  your_spots: "I tuoi posti",
  reviews_title: "Recensioni",
  reviews_count: "recensioni",
  no_reviews_yet: "Ancora nessuna recensione. Sii il primo a condividere la tua visita.",
  write_review_cta: "Scrivi una recensione",
  review_form_title: "Condividi la tua visita",
  review_form_sub: "Solo visite reali. Rendila utile per il prossimo viaggiatore.",
  review_name: "Il tuo nome",
  review_contact: "Email (non mostrata pubblicamente)",
  review_rating: "La tua valutazione",
  review_headline: "Titolo (facoltativo)",
  review_body: "La tua recensione",
  review_visit_month: "Quando ci sei stato?",
  review_booked_via: "Come hai prenotato?",
  booked_whatsapp: "WhatsApp",
  booked_lidoo: "richiesta via sunbedly",
  booked_phone: "Telefono",
  booked_walkin: "Senza prenotazione",
  booked_other: "Altro",
  review_send: "Pubblica recensione",
  review_sending: "Pubblicazione...",
  review_ok_t: "Grazie per aver condiviso",
  review_ok_d: "La tua recensione è online. Aiuta altri viaggiatori, e il lido, a sapere cosa aspettarsi.",
  review_spam_err: "Rimuovi i link dalla recensione.",
  review_duplicate_err: "Hai già recensito questo lido.",
  review_generic_err: "Qualcosa è andato storto. Riprova.",
  add_photo_cta: "Aggiungi una foto",
  add_photo_title: "Condividi una foto di questo lido",
  add_photo_sub: "Solo foto reali, scattate da te. Evita persone riconoscibili che non hanno dato il consenso. Controlliamo ogni caricamento prima che sia visibile.",
  photo_name: "Il tuo nome (facoltativo)",
  photo_file: "Scegli foto",
  photo_send: "Carica foto",
  photo_sending: "Caricamento...",
  photo_ok_t: "Grazie, foto ricevuta",
  photo_ok_d: "Apparirà sulla pagina del lido dopo la verifica.",
  photo_err_type: "Usa una foto JPG, PNG o WebP.",
  photo_err_size: "Foto troppo grande, massimo 6 MB.",
  photo_err_generic: "Caricamento non riuscito. Riprova.",
  nav_faq: "FAQ",
  nav_prices: "Indice dei prezzi",
  nav_method: "Come verifichiamo",
  faq_title: "Domande frequenti",
  faq_sub: "Tutto quello che viaggiatori e gestori ci chiedono più spesso.",
  faq_q1: "sunbedly è gratuito?",
  faq_a1: "Sì. Cercare, confrontare e inviare una richiesta non costa nulla. Paghi direttamente al lido, esattamente come se chiamassi o scrivessi tu stesso.",
  faq_q2: "Come funziona esattamente la prenotazione?",
  faq_a2: "Invii una richiesta con le tue date e il numero di persone. Il lido conferma direttamente, di solito su WhatsApp o telefono. Il pagamento avviene al lido, non su sunbedly.",
  faq_q3: "Cosa succede se un lido non risponde?",
  faq_a3: "La maggior parte dei lidi risponde entro 24-48 ore. Nessuna risposta dopo qualche giorno? Scrivi direttamente su WhatsApp, oppure contattaci e seguiremo noi il lido.",
  faq_q4: "I prezzi sono reali?",
  faq_a4: "I lidi verificati mostrano un prezzo pubblicato e la data dell’ultimo controllo. Gli annunci non verificati mostrano \"prezzo su richiesta\" finché un titolare non lo conferma direttamente.",
  faq_q5: "Posso cancellare o modificare la mia prenotazione?",
  faq_a5: "Contatta direttamente il lido, nello stesso modo in cui hai prenotato. sunbedly non gestisce pagamenti o cancellazioni, è il lido a occuparsi della tua prenotazione dall’inizio alla fine.",
  faq_q6: "Serve un account per cercare o prenotare?",
  faq_a6: "No. Cercare e inviare una richiesta non richiede mai la registrazione. I titolari dei lidi creano un annuncio gratuito se vogliono gestire la propria pagina.",
  faq_q7: "Come iscrivo il mio beach club a sunbedly?",
  faq_a7: "Fai domanda sulla nostra pagina partner. È gratis, nessuna esclusiva, e puoi interrompere quando vuoi.",
  faq_q8: "Quali paesi copre sunbedly?",
  faq_a8: "Mappiamo costa per costa, e ora siamo in diciannove paesi: Italia, Spagna, Grecia, Croazia, Francia, Portogallo, Turchia, Cipro, Malta, Montenegro, Albania, Emirati, Thailandia, Indonesia, Messico, Stati Uniti, Sudafrica, Tanzania e Mauritius. E andiamo avanti.",
  meta_clubs_desc: "Sfoglia i beach club su sunbedly: prezzi reali, servizi e contatto diretto, sulle coste di diciannove paesi, da Ibiza a Bali.",
  search_label: "Destinazione",
  trust_clubs: "beach club mappati",
  trust_coasts: "coste coperte",
  trust_free: "gratis, senza account",
  destinations_title: "Scegli la tua costa",
  destinations_sub: "Sei litorali mappati lido per lido. Inizia da dove sei diretto.",
  explore_region: "Scopri",
  why_title: "Perché sunbedly",
  why_1_t: "Prezzi veri, prima di partire",
  why_1_d: "Dove un lido pubblica le sue tariffe, noi le mostriamo. Nessuna sorpresa sul prezzo giornaliero quando hai già i piedi nella sabbia.",
  why_2_t: "Contatta il lido direttamente",
  why_2_d: "Chiama, scrivi su WhatsApp o manda una mail dalla pagina stessa. Nessun intermediario tra te e la spiaggia.",
  why_3_t: "Nessun account, nessun costo di prenotazione",
  why_3_d: "Cerca, confronta e invia una richiesta senza registrarti. Paghi il lido, i tuoi soldi non passano mai da noi.",
  contact_call: "Chiama",
  near_word: "vicino a",
  oh_daily: "Tutti i giorni",
  oh_in_season: "in stagione",
  oh_summer: "Stagione estiva",
  co_italy: "Italia",
  co_spain: "Spagna",
  co_greece: "Grecia",
  co_croatia: "Croazia",
  co_france: "Francia",
  co_portugal: "Portogallo",
  co_uae: "Emirati Arabi Uniti",
  co_netherlands: "Paesi Bassi",
  co_mexico: "Messico",
  co_indonesia: "Indonesia",
  co_united_states: "Stati Uniti",
  co_thailand: "Thailandia",
  co_turkey: "Turchia",
  co_south_africa: "Sudafrica",
  co_tanzania: "Tanzania",
  co_mauritius: "Mauritius",
  co_malta: "Malta",
  co_montenegro: "Montenegro",
  co_albania: "Albania",
  co_cyprus: "Cipro",
  pu_bed: "a lettino",
  pu_bed_parasol: "lettino con ombrellone",
  pu_two_beds: "2 lettini con ombrellone",
  pu_umbrella: "a ombrellone",
  pu_person: "a persona",
  pu_spot: "a postazione",
  pu_daypass: "ingresso giornaliero a persona",
  pu_half_day: "mezza giornata",
  pu_per_day: "al giorno",
  pu_per_week: "a settimana",
  pu_detail: "Come lo descrive il lido",
  contact_whatsapp: "WhatsApp",
  contact_email: "Email",
  contact_title: "Contatta questo lido",
  contact_sub: "La maggior parte dei lidi risponde più in fretta su WhatsApp.",
  rq_title: "Chiedi per una data",
  rq_sub_wa: "Apriamo WhatsApp con la tua richiesta già scritta, nella lingua del lido.",
  rq_sub_mail: "Apriamo la tua posta con la richiesta già scritta, nella lingua del lido.",
  rq_date: "Data",
  rq_beds: "Lettini",
  rq_name: "Il tuo nome",
  rq_contact: "Telefono o email",
  rq_send_wa: "Invia su WhatsApp",
  rq_sub_phone: "Annotiamo la tua richiesta e ti diamo il numero. Questo lido riceve le richieste per telefono.",
  rq_send_phone: "Mostra il numero",
  rq_ok_title_phone: "Chiamali e chiedi questo",
  rq_ok_warn_phone: "Questo lido ha un numero fisso, quindi WhatsApp non arriva. Leggi al telefono la frase qui sotto.",
  rq_send_mail: "Invia per email",
  rq_sending_wa: "Apertura di WhatsApp",
  rq_sending_mail: "Apertura della tua email",
  rq_ok_title_wa: "La tua richiesta è pronta su WhatsApp",
  rq_ok_title_mail: "La tua richiesta è pronta nella tua email",
  rq_ok_club: "Lido",
  rq_ok_who: "A nome di",
  rq_ok_warn_wa:
    "Il lido non ha ancora il tuo messaggio. Premi invia su WhatsApp, altrimenti non arriva nulla.",
  rq_ok_warn_mail:
    "Il lido non ha ancora il tuo messaggio. Premi invia nella tua email, altrimenti non arriva nulla.",
  rq_ok_reopen_wa: "Riapri WhatsApp",
  rq_ok_reopen_mail: "Riapri l’email",
  rq_ok_call: "Chiama {phone}",
  rq_error: "Non è andata a buon fine. Usa i pulsanti qui sotto per contattare il lido.",
  rq_phone_only: "Questo lido riceve le richieste per telefono.",
  rq_sticky: "Disponibilità",
  rq_privacy: "Il tuo nome e i tuoi contatti vanno solo a questo lido.",
  nav_privacy: "Privacy",
  map_failed: "La mappa non si è caricata. Il resto della pagina funziona.",
  sort_label: "Ordina",
  sort_relevance: "La nostra scelta",
  sort_rating: "Meglio recensiti",
  sort_price_asc: "Prezzo, dal più basso",
  sort_price_desc: "Prezzo, dal più alto",
  sort_name: "Nome, dalla A alla Z",
  filter_has_price: "Solo lidi con un prezzo pubblicato",
  results_count: "{n} lidi",
  results_count_one: "1 lido",
  updated_by: "Verificato da sunbedly il {date}",
  foot_role: "sunbedly elenca lidi. Non siamo il venditore, non tratteniamo commissioni e non gestiamo pagamenti: paghi il lido, sul posto.",
  nav_terms: "Termini",
  ck_title: "Statistiche, solo se sei d’accordo",
  ck_body: "Vorremmo usare Google Analytics per vedere quali coste vengono guardate. Imposta un cookie. Non si carica nulla finché non scegli, e rifiutare non cambia nulla nel funzionamento del sito.",
  ck_accept: "Accetta",
  ck_reject: "Rifiuta",
  ck_more: "Cosa raccogliamo",
  ck_settings: "Scelta cookie",
  rf_title: "Risposte dirette su {place}",
  rf_q_price: "Quanto costa un lettino in {place}?",
  rf_a_price: "I prezzi vanno da {cur} {low} a {cur} {high}, con {cur} {mid} come valore centrale. Misurato sui {n} lidi in {place} che pubblicano un prezzo, sui {total} che elenchiamo. Verificato il {date}.",
  rf_q_count: "Quanti lidi ci sono in {place}?",
  rf_a_count: "sunbedly elenca {total} lidi in {place}, distribuiti su {towns} localita. {rated} hanno una valutazione Google, in media {rating} su 5.",
  rf_q_cheap: "Qual e il lido piu economico in {place}?",
  rf_a_cheap: "Tra i lidi in {place} che pubblicano un prezzo, {club} e il piu basso con {cur} {low}. I lidi senza prezzo pubblicato non rientrano nel confronto, quindi ne possono esistere di piu economici.",
  rf_q_book: "Si puo prenotare un lettino online in {place}?",
  rf_a_book: "Quasi mai. I lidi in {place} accettano prenotazioni per telefono o WhatsApp, di solito in giornata. sunbedly scrive il messaggio per te nella lingua del lido e ti mette in contatto diretto. Nessun account, nessun costo.",
  region_clubs_title: "Lidi per costa",
  rg_sicily: "Sicilia",
  rg_miami: "Miami e Miami Beach",
  rg_tulum: "Tulum e Riviera Maya",
  rg_cape_town: "Città del Capo",
  rg_zanzibar: "Zanzibar",
  rg_mauritius: "Mauritius",
  rg_bali: "Bali",
  rg_phuket: "Phuket",
  rg_koh_samui: "Koh Samui",
  rg_abu_dhabi: "Abu Dhabi",
  rg_turkey_aegean: "Bodrum e Çeşme",
  rg_cyprus: "Cipro",
  rg_malta: "Malta e Gozo",
  rg_canaries: "Isole Canarie",
  rg_montenegro: "Montenegro",
  rg_albania: "Riviera albanese",
  rg_sardinia: "Sardegna",
  rg_cote_azur: "Costa Azzurra",
  rg_puglia: "Puglia",
  rg_amalfi: "Costiera Amalfitana e Capri",
  rg_romagna: "Riviera Romagnola",
  rg_versilia_liguria: "Versilia e Liguria",
  rg_costa_brava: "Costa Brava",
  rg_costa_blanca: "Costa Blanca",
  rg_costa_del_sol: "Costa del Sol",
  rg_mallorca: "Maiorca",
  rg_ibiza: "Ibiza",
  rg_algarve: "Algarve",
  rg_mykonos_santorini: "Mykonos e Santorini",
  rg_crete_rhodes: "Creta e Rodi",
  rg_corfu_halkidiki: "Corfù e Calcidica",
  rg_croatia_south: "Dalmazia meridionale",
  rg_croatia_central: "Dalmazia centrale",
  rg_croatia_north: "Istria e Quarnaro",
  rg_dubai: "Dubai",
  rg_greece: "Isole greche",
  filters_show: "Filtri",
  filters_hide: "Nascondi filtri",
  filters_active: "{n} attivi",
  lang_offer: "Questa pagina c’è anche in {lang}.",
  lang_switch: "Passa",
  lang_dismiss: "No grazie",
  pg_prev: "Precedente",
  pg_next: "Successiva",
  pg_of: "Pagina {a} di {b}",
  cl_none_t: "Nessun risultato",
  cl_none_d: "Provate con un filtro in meno, o cercate un paese invece di una costa.",
  cl_clear_all: "Azzera tutto",
  bk_title: "Registra una prenotazione presa da voi",
  bk_sub: "Qualcuno ha telefonato o si è presentato. Mettetelo qui: la giornata resta in un posto solo e i lettini che mostriamo sono quelli veri.",
  bk_name: "Nome",
  bk_contact: "Telefono o email (facoltativo)",
  bk_date: "Data",
  bk_beds: "Lettini",
  bk_note: "Nota (facoltativa)",
  bk_add: "Aggiungi",
  bk_saved: "Aggiunta.",
  bk_delete: "Rimuovi",
  bk_by_club: "Presa da voi",
  bk_by_site: "Via sunbedly",
  bk_today: "Oggi",
  bk_beds_today: "{n} lettini prenotati oggi",
  mg_title: "Pannello del lido",
  mg_view_public: "Vedi la pagina pubblica",
  mg_requests: "Richieste di prenotazione",
  mg_requests_sub: "Chi ha chiesto informazioni sui vostri lettini. Confermate e rispondete al recapito che hanno lasciato.",
  mg_no_requests: "Ancora nessuna richiesta. Compaiono qui appena qualcuno ne invia una.",
  mg_reservations: "Prenotazioni lettini",
  mg_res_sub: "Lettini specifici scelti sulla vostra mappa.",
  mg_close_dates: "Chiudere giornate",
  mg_close_sub: "Giornate in cui siete chiusi, pieni o affittati per un evento. Chi cerca smette subito di vedere lettini per quelle date.",
  mg_no_closed: "Nessuna giornata chiusa. Il lido risulta aperto in ogni data.",
  mg_note_opt: "Nota (facoltativa)",
  mg_private: "Evento privato",
  mg_close_btn: "Chiudi questa data",
  mg_reopen: "Riapri",
  mg_prices: "Prezzi e contatti",
  mg_photos: "Le vostre foto",
  mg_saved: "Salvato e online.",
  mg_confirm: "Conferma",
  mg_decline: "Rifiuta",
  mg_lang: "Lingua",
  ofaq_title: "Domande che potreste avere",
  ofaq_q1: "Come avete avuto i miei dati?",
  ofaq_a1: "Da quello che la vostra attività pubblica da sé: il vostro sito e le API ufficiali di Google Places per valutazione e foto. Niente comprato, niente preso da Instagram o dai siti di recensioni. Potete far cancellare tutto con una email.",
  ofaq_q2: "Chi altro può aprire questo link?",
  ofaq_a2: "Chiunque a cui lo inoltrate, quindi trattatelo come una chiave. Non è indovinabile. Chiedeteci un link nuovo e il vecchio smette subito di funzionare.",
  ofaq_q3: "Che fine fanno i dati dei miei clienti?",
  ofaq_a3: "Arrivano a voi e si fermano lì. In una richiesta vedete il telefono o l’email del cliente, così rispondete direttamente. Non li vendiamo, non facciamo marketing e non incassiamo mai, quindi non esistono dati di pagamento.",
  ofaq_q4: "Perché c’è la mia valutazione Google?",
  ofaq_a4: "Perché una pagina senza valutazione viene ignorata, e la vostra è già pubblica. Arriva dalle API ufficiali di Google, sempre attribuita a loro, e non viene mai mescolata a un nostro punteggio.",
  ofaq_q5: "Posso togliere la mia pagina?",
  ofaq_a5: "Sì, una email e sparisce, di solito in giornata. Nessun preavviso, nessun contratto, e non vi chiediamo tre volte perché.",
  ofaq_contact: "Scriveteci pure",
  region_meta_title: "Lidi a {place}",
  region_meta_desc: "{n} lidi a {place}: prezzi reali, servizi e contatto diretto. Confrontali e contattali tu stesso su sunbedly.",
  th_kids_area: "Lidi per famiglie",
  th_pool: "Lidi con piscina",
  th_pets_allowed: "Lidi che accettano cani",
  th_accessible: "Lidi accessibili in sedia a rotelle",
  th_water_sports: "Lidi con sport acquatici",
  th_dj: "Lidi con DJ",
  th_cabanas: "Lidi con gazebo",
  th_parking: "Lidi con parcheggio",
  th_in: "{what} in {place}",
  th_meta: "{what} in {place}: {n} lidi con prezzi reali, servizi e contatto diretto. Confrontali e contattali tu stesso su sunbedly.",
  th_meta_all: "{what}: {n} lidi su {r} coste, con prezzi reali, servizi e contatto diretto su sunbedly.",
  th_intro: "Ogni lido qui sotto compare in base a ciò che dichiara sui propri servizi. Prezzi e orari sono indicati dove il lido li rende pubblici, e il contatto avviene direttamente con loro.",
  th_browse: "Cerca quello che ti serve",
  th_other_regions: "Lo stesso su altre coste",
  th_all_regions: "Scegli una costa",
  th_count: "{n} lidi",
  share: "Condividi",
  share_copied: "Link copiato",
  badge_price_shown: "Prezzo indicato",
  no_cut_note: "Paghi il lido, sul posto. Noi non tratteniamo nulla.",
  no_account_note: "Nessun account, nessuna carta, nessuna commissione.",
  reviews_count_google: "{n} recensioni Google",
  region_priced_note: "{n} di questi pubblicano quanto costa un lettino.",
  contact_none_t: "Ancora nessun contatto diretto",
  contact_none_d: "Non abbiamo un numero di telefono per questo lido. Il loro sito è la via più rapida finché non entrano in sunbedly.",
  contact_website: "Vai al loro sito",
  contact_none_bare: "Non abbiamo ancora contatti per questo lido. Se lo gestisci tu, rivendica la pagina e aggiungili in un minuto.",
  fac_bar: "Bar",
  fac_restaurant: "Ristorante",
  fac_showers: "Docce",
  fac_sunbeds: "Lettini",
  fac_umbrellas: "Ombrelloni",
  fac_dj: "DJ",
  fac_live_music: "Musica dal vivo",
  fac_parking: "Parcheggio",
  fac_accessible: "Accessibile",
  fac_wifi: "Wi-Fi",
  fac_pool: "Piscina",
  fac_terrace: "Terrazza",
  fac_toilets: "Servizi igienici",
  fac_kids_area: "Area bambini",
  fac_playground: "Parco giochi",
  fac_sea_view: "Vista mare",
  fac_water_sports: "Sport acquatici",
  fac_beach_volley: "Beach volley",
  fac_beach_tennis: "Beach tennis",
  fac_changing_rooms: "Cabine",
  fac_cabanas: "Gazebo",
  fac_lifeguard: "Bagnino",
  fac_pets_allowed: "Animali ammessi",
  fac_events: "Eventi",
  fac_jetty: "Pontile",
  fac_massage: "Massaggi",
  fac_card_payment: "Pagamento con carta",
  fac_boat_rental: "Noleggio barche",
  fac_towels: "Teli mare inclusi",
  fac_sunscreen: "Crema solare inclusa",
  fac_bed_service: "Servizio al lettino",
  fac_lockers: "Armadietti",
  fac_spa: "Spa",
  fac_gym: "Palestra",
  fac_yoga: "Yoga",
  fac_sports: "Sport e giochi",
  fac_boat_shuttle: "Servizio navetta in barca",
  fac_sea_access: "Accesso diretto al mare",
  fac_lounge: "Zona lounge",
  fac_shop: "Negozio",
  fac_adults_only: "Solo adulti",
  fac_nightclub: "Discoteca",
  fac_grounds: "Giardini e spazi verdi",
  gallery_all: "Vedi tutte le {n} foto",
  gallery_close: "Chiudi",
  gallery_prev: "Foto precedente",
  gallery_next: "Foto successiva",
  photo_by: "Foto: {name} via Google",
  photo_credit: "Foto: {name}",
  photo_google: "Foto via Google",
  photo_nearby: "Spiaggia vicino a {place}",
  photo_nearby_note: "Foto dei dintorni, non del club stesso",
  nav_photo_credits: "Crediti fotografici",
  nearby_title: "Altri lidi qui vicino",
  nearby_sub: "Stessa costa, atmosfera diversa.",
  crumb_home: "Home",
  book_panel_title: "Verifica disponibilità",
  book_panel_sub: "Giriamo la richiesta direttamente al lido. Senza account, senza carta.",
  guides_eyebrow: "Guide",
  guides_title: "Quale lido vale davvero",
  guides_sub: "Classifiche costruite sui prezzi che i lidi pubblicano da soli, non su chi ha pagato per esserci. Aggiornate quando la costa cambia.",
  guides_read: "Leggi la guida",
  guides_all: "Tutte le guide",
  guides_updated: "Aggiornato",
  guides_clubs_in_piece: "I lidi di questa guida",
  nav_guides: "Guide",
  filters_title: "Filtri",
  filter_priced: "Prezzo indicato in anticipo",
  filters_clear: "Rimuovi filtri",
  footer_destinations: "Destinazioni",
  footer_beach_clubs_in: "Beach club in {place}",
  footer_more_coasts: "Stiamo mappando altre coste, una alla volta. Meglio coprire bene una costa che elencare un paese con due soli lidi.",
  b2b_eyebrow: "Per i gestori di lidi",
  b2b_title: "La tua spiaggia, i tuoi prezzi, i tuoi ospiti.",
  b2b_sub: "Una pagina gratuita su sunbedly con le tue tariffe vere, le tue foto e richieste che arrivano dritte sul telefono. Non prendiamo commissioni e non tocchiamo mai i tuoi soldi.",
  b2b_cta: "Registra il tuo lido",
  b2b_cta_sub: "Gratis. Nessun contratto, nessuna esclusiva, smetti quando vuoi.",
  b2b_stat_clubs: "lidi già mappati",
  b2b_stat_langs: "lingue, una pagina",
  b2b_stat_fee: "commissioni, mai",
  b2b_what_title: "Cosa ottieni",
  b2b_w1_t: "Una pagina che vende per te",
  b2b_w1_d: "Tariffe, servizi, orari e regolamento, in sei lingue, su una pagina costruita per posizionarsi. I turisti smettono di telefonare per chiedere quanto costa un lettino.",
  b2b_w2_t: "Il tuo pannello",
  b2b_w2_d: "Conferma o rifiuta le richieste, carica foto, cambia i prezzi e chiudi le date in cui sei pieno o chiuso. Un link, nessuna password da dimenticare.",
  b2b_w3_t: "Gli ospiti ti contattano direttamente",
  b2b_w3_d: "I pulsanti telefono, WhatsApp ed email puntano al tuo numero, non al nostro. Il rapporto e i dati del cliente restano tuoi.",
  b2b_w4_t: "Tieni ogni euro",
  b2b_w4_d: "L’ospite paga da te in spiaggia, esattamente come adesso. Nessuna commissione, nessun costo di servizio tolto al tuo cliente, nessuna attesa per il bonifico.",
  b2b_diff_title: "In cosa siamo diversi dalle altre piattaforme",
  b2b_diff_us: "sunbedly",
  b2b_diff_them: "Le altre piattaforme",
  b2b_d1: "Commissione per prenotazione",
  b2b_d1_us: "Nessuna",
  b2b_d1_them: "Percentuale non dichiarata",
  b2b_d2: "Costi per il tuo ospite",
  b2b_d2_us: "Nessuno",
  b2b_d2_them: "Costo di servizio non rimborsabile",
  b2b_d3: "Chi tiene i soldi",
  b2b_d3_us: "Tu. Noi non li tocchiamo",
  b2b_d3_them: "La piattaforma, poi ti paga",
  b2b_d4: "I tuoi prezzi prima di prenotare",
  b2b_d4_us: "Pubblici sulla tua pagina",
  b2b_d4_them: "Nascosti fino al pagamento",
  b2b_d5: "Contatti dell’ospite",
  b2b_d5_us: "Direttamente a te",
  b2b_d5_them: "Trattenuti dalla piattaforma",
  b2b_d6: "Esclusiva",
  b2b_d6_us: "Nessuna, esci quando vuoi",
  b2b_d6_them: "Spesso contrattuale",
  b2b_honest_title: "Le cose come stanno",
  b2b_honest_d: "Siamo agli inizi. sunbedly mappa {clubs} lidi e cresce costa per costa, quindi non verremo a raccontarti di milioni di utenti. Quello che possiamo promettere è che la tua scheda non costa nulla, i tuoi prezzi sono visibili prima che qualcuno parta, e niente si mette tra te e il tuo ospite. Se smette di essere vero, esci lo stesso giorno.",
  b2b_how_title: "Come registrarsi",
  b2b_how_1_t: "Invia il modulo",
  b2b_how_1_d: "Nome del lido, località e un numero per raggiungerti. Due minuti.",
  b2b_how_2_t: "Costruiamo la tua pagina",
  b2b_how_2_d: "Studiamo il tuo lido e scriviamo la pagina, poi ti mandiamo il link al pannello per controllarla e correggere quello che non va.",
  b2b_how_3_t: "Arrivano le richieste",
  b2b_how_3_d: "I turisti ti trovano, vedono il prezzo vero e ti scrivono direttamente.",
  b2b_faq_title: "Le domande dei gestori",
  b2b_fq1: "Quanto costa?",
  b2b_fa1: "Niente. Nessun costo di iscrizione, nessuna commissione, nessuna quota su quello che spende il tuo ospite. Se un giorno introdurremo un’opzione a pagamento sarà qualcosa che scegli di comprare, non una percentuale tolta dalle tue prenotazioni.",
  b2b_fq2: "Mi serve un gestionale?",
  b2b_fa2: "No. La maggior parte dei lidi con cui lavoriamo prende prenotazioni su WhatsApp e con una piantina di carta, e va benissimo. La tua pagina può semplicemente mandare le persone al tuo telefono.",
  b2b_fq3: "Di chi sono i dati degli ospiti?",
  b2b_fa3: "Tuoi. Le richieste arrivano con i contatti dell’ospite, così rispondi direttamente. Non ci mettiamo in mezzo.",
  b2b_fq4: "Posso cambiare i prezzi da solo?",
  b2b_fa4: "Sì, dal tuo pannello, e la modifica è subito online. Puoi anche chiudere le date in cui sei pieno o chiuso.",
  b2b_fq5: "E se voglio smettere?",
  b2b_fa5: "Basta dircelo e togliamo la pagina. Nessun contratto, nessun preavviso, nessuna clausola di esclusiva.",
  about_title: "Informazioni sul lido",
  facilities_title: "Servizi",
  hours_title: "Orari di apertura",
  rules_title: "Buono a sapersi",
  good_for: "Ideale per",
  reviews_google: "Recensioni Google",
  reviews_sunbedly: "Recensioni sunbedly",
  hours_unknown: "Orari non ancora confermati. Verifica con il lido prima di partire.",
  club_faq_title: "Domande su {club}",
  club_title_type: "lido",
  club_title_in: "lido a {town}",
  cfaq_q_price: "Quanto costa un lettino da {club}?",
  cfaq_a_price: "Un posto da {club} parte da {price}. Le tariffe le decide il lido e in alta stagione possono cambiare, quindi consideralo un prezzo di partenza, non un preventivo.",
  cfaq_a_price_none: "{club} non ha pubblicato le tariffe, per questo indichiamo \"prezzo su richiesta\". Scrivi direttamente al lido e ti diranno la tariffa del giorno.",
  cfaq_q_book: "Serve prenotare in anticipo da {club}?",
  cfaq_a_book: "A luglio e agosto le prime file vanno via presto, quindi conviene prenotare. Invia una richiesta da qui e il lido conferma direttamente, di solito su WhatsApp.",
  cfaq_q_hours: "Quando è aperto {club}?",
  cfaq_q_contact: "Come contatto {club}?",
  cfaq_a_contact: "Chiama, scrivi su WhatsApp o manda una mail da questa pagina. La maggior parte dei lidi risponde più in fretta su WhatsApp.",
  cfaq_q_where: "Dove si trova esattamente {club}?",
  cfaq_a_where: "{club} si trova a {town}. Usa la mappa in questa pagina per le indicazioni porta a porta.",
};

const de: Dict = {
  nav_explore: "Clubs entdecken",
  nav_partner: "Club eintragen",
  hero_title_1: "{n} Beachclubs.",
  hero_title_2: "An einem Ort.",
  hero_sub: "Echte Preise sehen und deine Liege in wenigen Minuten buchen.",
  search_placeholder: "Wohin? Ort, Region oder Club",
  dest_label: "Reiseziel",
  socials_title: "Ihre eigenen Kanäle",
  dest_all: "Alle Ziele",
  dest_guide: "Guide zu {place}",
  sug_region: "Küste",
  sug_town: "Ort",
  sug_club: "Club",
  search_cta: "Beachclubs suchen",
  near_me: "In der Nähe",
  locating: "Standort wird gesucht...",
  regions_eyebrow: "Regionen",
  regions_title: "Wo dein Strandtag schon geregelt ist",
  regions_sub: "Von berühmten Stränden bis zu stillen Buchten: Küste wählen und sehen, wer eine Liege frei hat.",
  live_title: "Heute auf der Plattform",
  live_sub: "Wir erfassen Beachclubs selbst, Küste für Küste, vom Mittelmeer bis Bali und in die Karibik. Verifizierte Clubs zeigen echte Preise, der Rest wartet auf seinen Betreiber.",
  all_clubs: "Alle Clubs ansehen",
  stats_clubs: "Beachclubs",
  stats_regions: "Küsten",
  how_title: "So funktioniert es",
  how_1_t: "Strand finden",
  how_1_d: "Nach Region suchen oder in deiner Umgebung schauen. Alle Clubs in einer Liste, nicht in zehn Tabs.",
  how_2_t: "Echte Preise vergleichen",
  how_2_d: "Liegen, Sonnenschirme und was inklusive ist. Kein Anrufen, kein Raten.",
  how_3_t: "Liege buchen",
  how_3_d: "Eine Anfrage senden, der Club bestätigt direkt. Der Platz ist sicher, bevor du ankommst.",
  owners_eyebrow: "Für Betreiber",
  owners_title: "Du führst einen Beachclub?",
  owners_sub: "Buchungen von Reisenden, bevor sie überhaupt landen.",
  owners_cta: "Club eintragen",
  clubs_title: "Beachclubs",
  clubs_found: "Clubs gefunden",
  filter_all: "Alle Regionen",
  verified: "Von uns geprüft",
  unverified: "Noch nicht beansprucht",
  from: "ab",
  price_on_request: "Tagespreis erfragen",
  km_away: "km entfernt",
  view_club: "Club ansehen",
  approx_note: "Standort auf Ortsebene angezeigt.",
  amenity_bed_service: "Service an der Liege",
  amenity_jetty: "Steg ins Meer",
  amenity_lunch: "Mittagessen und Küche",
  amenity_towels: "Handtücher inklusive",
  amenity_sunscreen: "Sonnencreme inklusive",
  amenity_cushions: "Kissen inklusive",
  amenity_party: "DJ und Events",
  request_title: "Verfügbarkeit anfragen",
  request_sub: "Der Club bestätigt direkt. Bezahlt wird erst nach Bestätigung.",
  form_name: "Dein Name",
  form_contact: "WhatsApp-Nummer oder E-Mail",
  form_date: "Besuchsdatum",
  form_party: "Personen",
  form_message: "Noch etwas? (erste Reihe, Cabana, Mittagessen)",
  form_send: "Anfrage senden",
  form_sending: "Wird gesendet...",
  request_ok_t: "Anfrage gesendet",
  request_ok_d: "Wir leiten sie direkt an den Club weiter. Die Antwort kommt an den Kontakt, den du angegeben hast.",
  request_ok_expect: "Die meisten Clubs antworten innerhalb von 24 bis 48 Stunden.",
  request_ok_fallback: "Nach ein paar Tagen nichts gehört? Schreib direkt über WhatsApp, oder",
  request_error: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
  contact_us: "kontaktiere uns",
  wa_fastest: "Schnellster Weg, sie zu erreichen",
  footer_contact: "Kontakt",
  footer_contact_note: "Fragen zu sunbedly selbst?",
  claim_note: "Ist das dein Club? Übernimm diese Seite und setze deine Preise.",
  claim_cta: "Club übernehmen",
  partner_title: "Dein Club, wo Reisende suchen",
  partner_sub: "Eine kostenlose Seite mit deinen Preisen, deinen Extras und Anfragen direkt aufs Telefon. Keine Gebühr, keine Exklusivität, jederzeit kündbar.",
  partner_p1_t: "Kostenloser Eintrag",
  partner_p1_d: "Liegen, Preise und Extras auf einer Seite, die funktioniert, in sechs Sprachen.",
  partner_p2_t: "Anfragen statt Spam",
  partner_p2_d: "Reisende senden strukturierte Anfragen. Du bestätigst per WhatsApp wie bisher.",
  partner_p3_t: "Neue Gäste, nicht die Stammgäste",
  partner_p3_d: "Wir bringen internationale Reisende, die dich heute nicht finden.",
  partner_form_club: "Name des Clubs",
  partner_form_name: "Ansprechpartner",
  partner_form_contact: "WhatsApp-Nummer oder E-Mail",
  partner_form_country: "Land",
  partner_form_town: "Ort oder Strand",
  partner_form_instagram: "Instagram (optional)",
  partner_form_system: "Nutzt du ein Buchungssystem?",
  partner_sys_yes: "Ja",
  partner_sys_no: "Nein",
  partner_sys_unknown: "Weiß nicht",
  partner_form_message: "Erzähl von deinem Club (optional)",
  partner_send: "Eintrag anfragen",
  partner_ok_t: "Anfrage erhalten",
  partner_ok_d: "Wir richten deine Seite ein und melden uns wegen Preisen und Fotos.",
  footer_tag: "Die Beachclubs der Welt, auf einen Blick.",
  footer_travelers: "Reisende",
  footer_owners: "Clubbetreiber",
  footer_langs: "Sprache",
  back_to_clubs: "Alle Clubs",
  no_results: "Keine Clubs gefunden. Andere Region versuchen.",
  wa_book: "Per WhatsApp buchen",
  sponsored_badge: "Gesponsert",
  get_directions: "Route",
  show_map: "Karte anzeigen",
  show_list: "Liste anzeigen",
  last_checked: "Geprüft",
  season_may_oct: "Meist von Mai bis Oktober geöffnet",
  checking_availability: "Verfügbarkeit wird geprüft...",
  route_title: "Anreise",
  map_title: "Wähle deinen Platz am Strand",
  map_sub: "Datum wählen, dann die gewünschten Liegen antippen. Reihe A liegt direkt am Wasser.",
  sea: "Meer",
  jetty_label: "Steg",
  row_label: "Reihe",
  free_legend: "Frei",
  taken_legend: "Belegt",
  selected_legend: "Deine Wahl",
  max_spots: "Maximal 4 Plätze pro Reservierung.",
  reserve_cta: "Diese Liegen reservieren",
  reserve_ok_t: "Reservierung gesendet",
  reserve_ok_d: "Der Club bestätigt deine Plätze über den Kontakt, den du angegeben hast. Bezahlt wird vor Ort.",
  conflict_err: "Jemand war gerade schneller. Bitte neu wählen.",
  your_spots: "Deine Plätze",
  reviews_title: "Bewertungen",
  reviews_count: "Bewertungen",
  no_reviews_yet: "Noch keine Bewertungen. Mach den Anfang und erzähl von deinem Besuch.",
  write_review_cta: "Bewertung schreiben",
  review_form_title: "Teile deinen Besuch",
  review_form_sub: "Nur echte Besuche. Schreib so, dass andere Reisende etwas davon haben.",
  review_name: "Dein Name",
  review_contact: "E-Mail (nicht öffentlich sichtbar)",
  review_rating: "Deine Bewertung",
  review_headline: "Titel (optional)",
  review_body: "Deine Bewertung",
  review_visit_month: "Wann warst du dort?",
  review_booked_via: "Wie hast du gebucht?",
  booked_whatsapp: "WhatsApp",
  booked_lidoo: "Anfrage über sunbedly",
  booked_phone: "Telefon",
  booked_walkin: "Einfach vorbeigekommen",
  booked_other: "Anders",
  review_send: "Bewertung veröffentlichen",
  review_sending: "Wird veröffentlicht...",
  review_ok_t: "Danke fürs Teilen",
  review_ok_d: "Deine Bewertung ist live. So wissen andere Reisende und der Club, was sie erwartet.",
  review_spam_err: "Bitte entferne Links aus deiner Bewertung.",
  review_duplicate_err: "Du hast diesen Club bereits bewertet.",
  review_generic_err: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
  add_photo_cta: "Foto hinzufügen",
  add_photo_title: "Teile ein Foto von diesem Club",
  add_photo_sub: "Nur echte Fotos, von dir selbst aufgenommen. Vermeide erkennbare Personen, die dem nicht zugestimmt haben. Wir prüfen jeden Upload, bevor er live geht.",
  photo_name: "Dein Name (optional)",
  photo_file: "Foto auswählen",
  photo_send: "Foto hochladen",
  photo_sending: "Wird hochgeladen...",
  photo_ok_t: "Danke, Foto erhalten",
  photo_ok_d: "Es erscheint auf der Club-Seite, sobald es geprüft wurde.",
  photo_err_type: "Bitte ein JPG-, PNG- oder WebP-Foto verwenden.",
  photo_err_size: "Foto ist zu groß, maximal 6 MB.",
  photo_err_generic: "Hochladen fehlgeschlagen. Bitte versuche es erneut.",
  nav_faq: "FAQ",
  nav_prices: "Preisindex",
  nav_method: "Wie wir prüfen",
  faq_title: "Häufig gestellte Fragen",
  faq_sub: "Alles, was Reisende und Clubbetreiber uns am häufigsten fragen.",
  faq_q1: "Ist sunbedly kostenlos?",
  faq_a1: "Ja. Suchen, vergleichen und eine Anfrage senden kostet nichts. Du zahlst direkt beim Club, genau wie wenn du selbst anrufen oder schreiben würdest.",
  faq_q2: "Wie funktioniert die Buchung genau?",
  faq_a2: "Du sendest eine Anfrage mit Datum und Personenzahl. Der Club bestätigt direkt, meist per WhatsApp oder Telefon. Bezahlt wird beim Club, nicht bei sunbedly.",
  faq_q3: "Was, wenn ein Club nicht antwortet?",
  faq_a3: "Die meisten Clubs antworten innerhalb von 24 bis 48 Stunden. Nach ein paar Tagen nichts gehört? Schreib direkt über WhatsApp, oder kontaktiere uns, dann fragen wir beim Club nach.",
  faq_q4: "Sind die Preise echt?",
  faq_a4: "Verifizierte Clubs zeigen einen veröffentlichten Preis und das Datum der letzten Prüfung. Nicht verifizierte Einträge zeigen \"Preis auf Anfrage\", bis ein Betreiber ihn direkt bestätigt.",
  faq_q5: "Kann ich meine Buchung stornieren oder ändern?",
  faq_a5: "Kontaktiere den Club direkt, so wie du gebucht hast. sunbedly verarbeitet keine Zahlungen oder Stornierungen, der Club kümmert sich von Anfang bis Ende um deine Reservierung.",
  faq_q6: "Brauche ich ein Konto zum Suchen oder Buchen?",
  faq_a6: "Nein. Zum Stöbern und Anfragen brauchst du nie ein Konto. Clubbetreiber erstellen einen kostenlosen Eintrag, wenn sie ihre Seite verwalten möchten.",
  faq_q7: "Wie melde ich meinen Beachclub bei sunbedly an?",
  faq_a7: "Bewirb dich auf unserer Partnerseite. Kostenlos, keine Exklusivität, jederzeit kündbar.",
  faq_q8: "Welche Länder deckt sunbedly ab?",
  faq_a8: "Wir kartieren Küste für Küste, inzwischen in neunzehn Ländern: Italien, Spanien, Griechenland, Kroatien, Frankreich, Portugal, Türkei, Zypern, Malta, Montenegro, Albanien, Emirate, Thailand, Indonesien, Mexiko, USA, Südafrika, Tansania und Mauritius. Und es geht weiter.",
  meta_clubs_desc: "Beachclubs auf sunbedly: echte Preise, Ausstattung und direkter Kontakt, an den Küsten von neunzehn Ländern, von Ibiza bis Bali.",
  search_label: "Reiseziel",
  trust_clubs: "erfasste Beachclubs",
  trust_coasts: "Küsten abgedeckt",
  trust_free: "kostenlos, ohne Konto",
  destinations_title: "Wähle deine Küste",
  destinations_sub: "Sechs Küstenabschnitte, Club für Club erfasst. Fang dort an, wo du hinwillst.",
  explore_region: "Ansehen",
  why_title: "Warum sunbedly",
  why_1_t: "Echte Preise, vor der Abreise",
  why_1_d: "Wo ein Club seine Tarife veröffentlicht, zeigen wir sie. Keine Überraschung beim Tagespreis, wenn du schon im Sand stehst.",
  why_2_t: "Direkt zum Club",
  why_2_d: "Anrufen, WhatsApp schreiben oder mailen, direkt von der Seite aus. Kein Vermittler zwischen dir und dem Strand.",
  why_3_t: "Kein Konto, keine Buchungsgebühr",
  why_3_d: "Suchen, vergleichen und anfragen ohne Anmeldung. Du zahlst direkt beim Club, dein Geld läuft nie über uns.",
  contact_call: "Anrufen",
  near_word: "bei",
  oh_daily: "Täglich",
  oh_in_season: "in der Saison",
  oh_summer: "Sommersaison",
  co_italy: "Italien",
  co_spain: "Spanien",
  co_greece: "Griechenland",
  co_croatia: "Kroatien",
  co_france: "Frankreich",
  co_portugal: "Portugal",
  co_uae: "Vereinigte Arabische Emirate",
  co_netherlands: "Niederlande",
  co_mexico: "Mexiko",
  co_indonesia: "Indonesien",
  co_united_states: "USA",
  co_thailand: "Thailand",
  co_turkey: "Türkei",
  co_south_africa: "Südafrika",
  co_tanzania: "Tansania",
  co_mauritius: "Mauritius",
  co_malta: "Malta",
  co_montenegro: "Montenegro",
  co_albania: "Albanien",
  co_cyprus: "Zypern",
  pu_bed: "pro Liege",
  pu_bed_parasol: "Liege mit Schirm",
  pu_two_beds: "2 Liegen mit Schirm",
  pu_umbrella: "pro Schirmplatz",
  pu_person: "pro Person",
  pu_spot: "pro Platz",
  pu_daypass: "Tagesticket pro Person",
  pu_half_day: "halber Tag",
  pu_per_day: "pro Tag",
  pu_per_week: "pro Woche",
  pu_detail: "Wie der Club es beschreibt",
  contact_whatsapp: "WhatsApp",
  contact_email: "E-Mail",
  contact_title: "Diesen Club erreichen",
  contact_sub: "Die meisten Clubs antworten am schnellsten über WhatsApp.",
  rq_title: "Nach einem Datum fragen",
  rq_sub_wa: "Wir öffnen WhatsApp mit deiner Anfrage bereits formuliert, in der Sprache des Clubs.",
  rq_sub_mail: "Wir öffnen dein Mailprogramm mit der Anfrage bereits formuliert, in der Sprache des Clubs.",
  rq_date: "Datum",
  rq_beds: "Liegen",
  rq_name: "Dein Name",
  rq_contact: "Telefon oder E-Mail",
  rq_send_wa: "Über WhatsApp senden",
  rq_sub_phone: "Wir notieren deine Anfrage und geben dir die Nummer. Dieser Club nimmt Anfragen telefonisch entgegen.",
  rq_send_phone: "Nummer anzeigen",
  rq_ok_title_phone: "Ruf an und frag das",
  rq_ok_warn_phone: "Dieser Club hat einen Festnetzanschluss, WhatsApp kommt dort nicht an. Lies am Telefon den Satz unten vor.",
  rq_send_mail: "Per E-Mail senden",
  rq_sending_wa: "WhatsApp wird geöffnet",
  rq_sending_mail: "E-Mail wird geöffnet",
  rq_ok_title_wa: "Deine Anfrage liegt bereit in WhatsApp",
  rq_ok_title_mail: "Deine Anfrage liegt bereit in deiner E-Mail",
  rq_ok_club: "Club",
  rq_ok_who: "Auf den Namen",
  rq_ok_warn_wa:
    "Der Club hat deine Nachricht noch nicht. Drücke in WhatsApp noch auf Senden, sonst kommt nichts an.",
  rq_ok_warn_mail:
    "Der Club hat deine Nachricht noch nicht. Drücke in deiner E-Mail noch auf Senden, sonst kommt nichts an.",
  rq_ok_reopen_wa: "WhatsApp erneut öffnen",
  rq_ok_reopen_mail: "E-Mail erneut öffnen",
  rq_ok_call: "Anrufen: {phone}",
  rq_error: "Das hat nicht geklappt. Nutze die Schaltflächen unten, um den Club direkt zu erreichen.",
  rq_phone_only: "Dieser Club nimmt Anfragen telefonisch entgegen.",
  rq_sticky: "Verfügbarkeit",
  rq_privacy: "Dein Name und deine Kontaktdaten gehen nur an diesen Club.",
  nav_privacy: "Datenschutz",
  map_failed: "Die Karte konnte nicht geladen werden. Der Rest der Seite funktioniert.",
  sort_label: "Sortieren",
  sort_relevance: "Unsere Auswahl",
  sort_rating: "Am besten bewertet",
  sort_price_asc: "Preis, aufsteigend",
  sort_price_desc: "Preis, absteigend",
  sort_name: "Name, A bis Z",
  filter_has_price: "Nur Clubs mit veröffentlichtem Preis",
  results_count: "{n} Beachclubs",
  results_count_one: "1 Beachclub",
  updated_by: "Von sunbedly geprüft am {date}",
  foot_role: "sunbedly listet Beachclubs. Wir sind nicht der Verkäufer, wir nehmen keine Provision und wickeln keine Zahlungen ab: du zahlst beim Club, vor Ort.",
  nav_terms: "AGB",
  ck_title: "Statistiken, nur wenn du willst",
  ck_body: "Wir würden gern Google Analytics nutzen, um zu sehen, welche Küsten angeschaut werden. Das setzt ein Cookie. Bis zu deiner Wahl lädt nichts, und ein Nein ändert nichts an der Seite.",
  ck_accept: "Akzeptieren",
  ck_reject: "Ablehnen",
  ck_more: "Was wir erheben",
  ck_settings: "Cookie-Wahl",
  rf_title: "Klare Antworten zu {place}",
  rf_q_price: "Was kostet eine Liege in {place}?",
  rf_a_price: "Die Preise reichen von {cur} {low} bis {cur} {high}, mit {cur} {mid} als Mittelwert. Gemessen an den {n} Clubs in {place}, die einen Preis veröffentlichen, von {total} gelisteten. Geprüft am {date}.",
  rf_q_count: "Wie viele Beachclubs gibt es in {place}?",
  rf_a_count: "sunbedly listet {total} Beachclubs in {place}, verteilt auf {towns} Orte. {rated} davon haben eine Google-Bewertung, im Schnitt {rating} von 5.",
  rf_q_cheap: "Welcher Beachclub in {place} ist am günstigsten?",
  rf_a_cheap: "Von den Clubs in {place} mit veröffentlichtem Preis ist {club} mit {cur} {low} der günstigste. Clubs ohne Preisangabe sind nicht im Vergleich, günstigere kann es also geben.",
  rf_q_book: "Kann man in {place} online eine Liege buchen?",
  rf_a_book: "Fast nie. Beachclubs in {place} nehmen Reservierungen telefonisch oder über WhatsApp entgegen, meist am selben Tag. sunbedly schreibt die Nachricht in der Sprache des Clubs und verbindet dich direkt. Kein Konto, keine Gebühr.",
  region_clubs_title: "Clubs nach Küste",
  rg_sicily: "Sizilien",
  rg_miami: "Miami & Miami Beach",
  rg_tulum: "Tulum & Riviera Maya",
  rg_cape_town: "Kapstadt",
  rg_zanzibar: "Sansibar",
  rg_mauritius: "Mauritius",
  rg_bali: "Bali",
  rg_phuket: "Phuket",
  rg_koh_samui: "Koh Samui",
  rg_abu_dhabi: "Abu Dhabi",
  rg_turkey_aegean: "Bodrum & Çeşme",
  rg_cyprus: "Zypern",
  rg_malta: "Malta & Gozo",
  rg_canaries: "Kanarische Inseln",
  rg_montenegro: "Montenegro",
  rg_albania: "Albanische Riviera",
  rg_sardinia: "Sardinien",
  rg_cote_azur: "Côte d’Azur",
  rg_puglia: "Apulien",
  rg_amalfi: "Amalfiküste und Capri",
  rg_romagna: "Riviera Romagnola",
  rg_versilia_liguria: "Versilia und Ligurien",
  rg_costa_brava: "Costa Brava",
  rg_costa_blanca: "Costa Blanca",
  rg_costa_del_sol: "Costa del Sol",
  rg_mallorca: "Mallorca",
  rg_ibiza: "Ibiza",
  rg_algarve: "Algarve",
  rg_mykonos_santorini: "Mykonos und Santorin",
  rg_crete_rhodes: "Kreta und Rhodos",
  rg_corfu_halkidiki: "Korfu und Chalkidiki",
  rg_croatia_south: "Süddalmatien",
  rg_croatia_central: "Mitteldalmatien",
  rg_croatia_north: "Istrien und Kvarner",
  rg_dubai: "Dubai",
  rg_greece: "Griechische Inseln",
  filters_show: "Filter",
  filters_hide: "Filter ausblenden",
  filters_active: "{n} aktiv",
  lang_offer: "Diese Seite gibt es auch auf {lang}.",
  lang_switch: "Wechseln",
  lang_dismiss: "Nein danke",
  pg_prev: "Zurück",
  pg_next: "Weiter",
  pg_of: "Seite {a} von {b}",
  cl_none_t: "Dazu passt noch nichts",
  cl_none_d: "Versuch einen Filter weniger, oder such nach einem Ort statt einer Küste.",
  cl_clear_all: "Alles zurücksetzen",
  bk_title: "Trag eine Buchung ein, die du selbst angenommen hast",
  bk_sub: "Jemand hat angerufen oder stand am Tresen. Trag es hier ein, dann steht dein Tag an einem Ort und die Liegen, die wir zeigen, stimmen.",
  bk_name: "Name",
  bk_contact: "Telefon oder E-Mail (optional)",
  bk_date: "Datum",
  bk_beds: "Liegen",
  bk_note: "Notiz (optional)",
  bk_add: "Buchung eintragen",
  bk_saved: "Eingetragen.",
  bk_delete: "Entfernen",
  bk_by_club: "Von dir angenommen",
  bk_by_site: "Über sunbedly",
  bk_today: "Heute",
  bk_beds_today: "{n} Liegen heute gebucht",
  mg_title: "Club-Dashboard",
  mg_view_public: "Öffentliche Seite ansehen",
  mg_requests: "Anfragen",
  mg_requests_sub: "Gäste, die nach deinen Liegen gefragt haben. Bestätige eine und antworte über den hinterlassenen Kontakt.",
  mg_no_requests: "Noch keine Anfragen. Sie erscheinen hier, sobald jemand eine sendet.",
  mg_reservations: "Liegenreservierungen",
  mg_res_sub: "Bestimmte Liegen, auf deinem Strandplan gewählt.",
  mg_close_dates: "Tage sperren",
  mg_close_sub: "Tage, an denen du zu bist, ausgebucht oder privat vermietet. Gäste sehen für diese Tage sofort keine Liegen mehr.",
  mg_no_closed: "Keine gesperrten Tage. Dein Strand gilt an jedem Datum als offen.",
  mg_note_opt: "Notiz (optional)",
  mg_private: "Private Veranstaltung",
  mg_close_btn: "Diesen Tag sperren",
  mg_reopen: "Wieder öffnen",
  mg_prices: "Preise und Kontakt",
  mg_photos: "Deine Fotos",
  mg_saved: "Gespeichert und live.",
  mg_confirm: "Bestätigen",
  mg_decline: "Ablehnen",
  mg_lang: "Sprache",
  ofaq_title: "Fragen, die du haben könntest",
  ofaq_q1: "Woher habt ihr meine Daten?",
  ofaq_a1: "Aus dem, was dein Betrieb selbst veröffentlicht: deine Website und die offizielle Google-Places-API für Bewertung und Fotos. Nichts gekauft, nichts von Instagram oder Bewertungsportalen abgegriffen. Eine E-Mail genügt, und alles wird gelöscht.",
  ofaq_q2: "Wer kann diesen Link noch öffnen?",
  ofaq_a2: "Jeder, an den du ihn weiterleitest, also behandle ihn wie einen Schlüssel. Erraten lässt er sich nicht. Frag uns nach einem neuen, dann ist der alte sofort ungültig.",
  ofaq_q3: "Was passiert mit den Daten meiner Gäste?",
  ofaq_a3: "Sie kommen bei dir an und bleiben dort. Bei einer Anfrage siehst du Telefon oder E-Mail des Gastes und antwortest direkt. Wir verkaufen nichts, bewerben niemanden und kassieren nie, also gibt es nirgends Kartendaten.",
  ofaq_q4: "Warum steht meine Google-Bewertung dort?",
  ofaq_a4: "Weil eine Seite ohne Bewertung übersehen wird, und deine ist ohnehin öffentlich. Sie kommt über Googles offizielle API, immer mit Quellenangabe, und wird nie mit einer eigenen Note vermischt.",
  ofaq_q5: "Kann ich meine Seite offline nehmen?",
  ofaq_a5: "Ja, eine E-Mail und sie ist weg, meist am selben Tag. Keine Kündigungsfrist, kein Vertrag, und wir fragen nicht dreimal nach dem Grund.",
  ofaq_contact: "Frag uns einfach",
  region_meta_title: "Beachclubs in {place}",
  region_meta_desc: "{n} Beachclubs in {place}: echte Preise, Ausstattung und direkter Kontakt. Vergleiche sie und melde dich selbst, über sunbedly.",
  th_kids_area: "Beachclubs für Familien",
  th_pool: "Beachclubs mit Pool",
  th_pets_allowed: "Hundefreundliche Beachclubs",
  th_accessible: "Barrierefreie Beachclubs",
  th_water_sports: "Beachclubs mit Wassersport",
  th_dj: "Beachclubs mit DJ",
  th_cabanas: "Beachclubs mit Cabanas",
  th_parking: "Beachclubs mit Parkplatz",
  th_in: "{what} in {place}",
  th_meta: "{what} in {place}: {n} Clubs mit echten Preisen, Ausstattung und direktem Kontakt. Vergleiche sie und melde dich selbst, über sunbedly.",
  th_meta_all: "{what}: {n} Clubs an {r} Küsten, mit echten Preisen, Ausstattung und direktem Kontakt über sunbedly.",
  th_intro: "Jeder Club unten steht hier aufgrund dessen, was er selbst über seine Ausstattung veröffentlicht. Preise und Öffnungszeiten stehen dabei, wo der Club sie öffentlich macht, und du meldest dich direkt dort.",
  th_browse: "Suchst du etwas Bestimmtes?",
  th_other_regions: "Dasselbe an anderen Küsten",
  th_all_regions: "Küste wählen",
  th_count: "{n} Clubs",
  share: "Teilen",
  share_copied: "Link kopiert",
  badge_price_shown: "Preis bekannt",
  no_cut_note: "Du zahlst beim Club, vor Ort. Wir behalten nichts ein.",
  no_account_note: "Kein Konto, keine Karte, keine Buchungsgebühr.",
  reviews_count_google: "{n} Google-Bewertungen",
  region_priced_note: "{n} davon veröffentlichen, was eine Liege kostet.",
  contact_none_t: "Noch keine direkte Nummer",
  contact_none_d: "Wir haben keine Telefonnummer für diesen Club. Die eigene Website des Clubs ist vorerst der schnellste Weg, bis er bei sunbedly mitmacht.",
  contact_website: "Zur Website",
  contact_none_bare: "Wir haben noch keine Kontaktdaten für diesen Club. Betreibst du ihn? Sichere dir die Seite und trag sie in einer Minute ein.",
  fac_bar: "Bar",
  fac_restaurant: "Restaurant",
  fac_showers: "Duschen",
  fac_sunbeds: "Liegen",
  fac_umbrellas: "Sonnenschirme",
  fac_dj: "DJ",
  fac_live_music: "Livemusik",
  fac_parking: "Parkplatz",
  fac_accessible: "Barrierefrei",
  fac_wifi: "WLAN",
  fac_pool: "Pool",
  fac_terrace: "Terrasse",
  fac_toilets: "Toiletten",
  fac_kids_area: "Kinderbereich",
  fac_playground: "Spielplatz",
  fac_sea_view: "Meerblick",
  fac_water_sports: "Wassersport",
  fac_beach_volley: "Beachvolleyball",
  fac_beach_tennis: "Beachtennis",
  fac_changing_rooms: "Umkleiden",
  fac_cabanas: "Cabanas",
  fac_lifeguard: "Rettungsschwimmer",
  fac_pets_allowed: "Hunde erlaubt",
  fac_events: "Events",
  fac_jetty: "Steg",
  fac_massage: "Massage",
  fac_card_payment: "Kartenzahlung",
  fac_boat_rental: "Bootsverleih",
  fac_towels: "Handtücher inklusive",
  fac_sunscreen: "Sonnencreme vorhanden",
  fac_bed_service: "Service an der Liege",
  fac_lockers: "Schließfächer",
  fac_spa: "Spa",
  fac_gym: "Fitnessraum",
  fac_yoga: "Yoga",
  fac_sports: "Sport und Spiele",
  fac_boat_shuttle: "Bootsshuttle",
  fac_sea_access: "Direkter Zugang zum Meer",
  fac_lounge: "Loungebereich",
  fac_shop: "Shop",
  fac_adults_only: "Nur Erwachsene",
  fac_nightclub: "Nachtclub",
  fac_grounds: "Garten und Gelände",
  gallery_all: "Alle {n} Fotos ansehen",
  gallery_close: "Schließen",
  gallery_prev: "Vorheriges Foto",
  gallery_next: "Nächstes Foto",
  photo_by: "Foto: {name} via Google",
  photo_credit: "Foto: {name}",
  photo_google: "Foto via Google",
  photo_nearby: "Strand bei {place}",
  photo_nearby_note: "Umgebungsfoto, nicht vom Club selbst",
  nav_photo_credits: "Bildnachweise",
  nearby_title: "Weitere Beachclubs in der Nähe",
  nearby_sub: "Gleiche Küste, andere Stimmung.",
  crumb_home: "Start",
  book_panel_title: "Verfügbarkeit prüfen",
  book_panel_sub: "Wir leiten deine Anfrage direkt an den Club weiter. Ohne Konto, ohne Karte.",
  guides_eyebrow: "Guides",
  guides_title: "Welcher Beachclub sich wirklich lohnt",
  guides_sub: "Ranglisten auf Basis der Preise, die Clubs selbst veröffentlichen, nicht danach, wer für einen Platz bezahlt hat. Aktualisiert, wenn sich die Küste ändert.",
  guides_read: "Guide lesen",
  guides_all: "Alle Guides",
  guides_updated: "Aktualisiert",
  guides_clubs_in_piece: "Die Clubs in diesem Guide",
  nav_guides: "Guides",
  filters_title: "Filter",
  filter_priced: "Preis vorab sichtbar",
  filters_clear: "Filter zurücksetzen",
  footer_destinations: "Reiseziele",
  footer_beach_clubs_in: "Beachclubs in {place}",
  footer_more_coasts: "Wir erfassen nach und nach weitere Küsten. Lieber eine Küste richtig als ein Land mit zwei Clubs darin.",
  b2b_eyebrow: "Für Beachclub-Betreiber",
  b2b_title: "Dein Strand, deine Preise, deine Gäste.",
  b2b_sub: "Eine kostenlose Seite auf sunbedly mit deinen echten Preisen, deinen Fotos und Anfragen, die direkt auf dein Telefon kommen. Wir nehmen keine Provision und fassen dein Geld nie an.",
  b2b_cta: "Club eintragen",
  b2b_cta_sub: "Kostenlos. Kein Vertrag, keine Exklusivität, jederzeit kündbar.",
  b2b_stat_clubs: "Clubs bereits erfasst",
  b2b_stat_langs: "Sprachen, eine Seite",
  b2b_stat_fee: "Provision, für immer",
  b2b_what_title: "Was du bekommst",
  b2b_w1_t: "Eine Seite, die für dich verkauft",
  b2b_w1_d: "Deine Preise, Ausstattung, Öffnungszeiten und Hausregeln, in sechs Sprachen, auf einer Seite, die für Suchmaschinen gebaut ist. Reisende rufen nicht mehr an, um nach dem Liegenpreis zu fragen.",
  b2b_w2_t: "Dein eigenes Dashboard",
  b2b_w2_d: "Anfragen bestätigen oder ablehnen, Fotos hochladen, Preise ändern und Tage sperren, an denen du voll oder geschlossen bist. Ein Link, kein Passwort zum Vergessen.",
  b2b_w3_t: "Gäste melden sich direkt bei dir",
  b2b_w3_d: "Die Buttons für Anruf, WhatsApp und E-Mail zeigen auf deine Nummer, nicht auf unsere. Der Kontakt und die Gastdaten bleiben bei dir.",
  b2b_w4_t: "Du behältst jeden Euro",
  b2b_w4_d: "Der Gast zahlt bei dir am Strand, genau wie bisher. Keine Provision, keine Servicegebühr, die deinem Gast abgezogen wird, kein Warten auf eine Auszahlung.",
  b2b_diff_title: "Was hier anders läuft als bei den üblichen Plattformen",
  b2b_diff_us: "sunbedly",
  b2b_diff_them: "Übliche Buchungsplattform",
  b2b_d1: "Provision je Buchung",
  b2b_d1_us: "Keine",
  b2b_d1_them: "Ungenannter Prozentsatz",
  b2b_d2: "Gebühr für deinen Gast",
  b2b_d2_us: "Keine",
  b2b_d2_them: "Nicht erstattbare Servicegebühr",
  b2b_d3: "Wer das Geld hält",
  b2b_d3_us: "Du. Wir fassen es nicht an",
  b2b_d3_them: "Erst die Plattform, dann die Auszahlung",
  b2b_d4: "Deine Preise vor der Buchung",
  b2b_d4_us: "Öffentlich auf deiner Seite",
  b2b_d4_them: "Erst beim Buchen sichtbar",
  b2b_d5: "Kontaktdaten des Gastes",
  b2b_d5_us: "Direkt an dich",
  b2b_d5_them: "Bei der Plattform",
  b2b_d6: "Exklusivität",
  b2b_d6_us: "Keine, jederzeit raus",
  b2b_d6_them: "Oft vertraglich",
  b2b_honest_title: "Wo wir ehrlich zu dir sind",
  b2b_honest_d: "Wir stehen noch am Anfang. sunbedly erfasst {clubs} Clubs und wächst Küste für Küste, also erzählen wir dir nichts von Millionen Nutzern. Versprechen können wir: dein Eintrag kostet nichts, deine Preise sind sichtbar, bevor jemand anreist, und zwischen dir und deinem Gast steht nichts. Stimmt das nicht mehr, bist du am selben Tag raus.",
  b2b_how_title: "So wirst du gelistet",
  b2b_how_1_t: "Formular abschicken",
  b2b_how_1_d: "Clubname, Ort und eine Nummer, unter der wir dich erreichen. Zwei Minuten.",
  b2b_how_2_t: "Wir bauen deine Seite",
  b2b_how_2_d: "Wir recherchieren deinen Strand und schreiben ihn auf, dann bekommst du den Dashboard-Link, um alles zu prüfen und zu korrigieren.",
  b2b_how_3_t: "Die Anfragen kommen",
  b2b_how_3_d: "Reisende finden dich, sehen deinen echten Preis und schreiben dir direkt.",
  b2b_faq_title: "Was Betreiber fragen",
  b2b_fq1: "Was kostet das?",
  b2b_fa1: "Nichts. Keine Eintragsgebühr, keine Provision, kein Anteil an dem, was dein Gast ausgibt. Falls wir je eine kostenpflichtige Option einführen, ist das etwas, das du aktiv kaufst, kein Prozentsatz aus deinen Buchungen.",
  b2b_fq2: "Brauche ich ein Buchungssystem?",
  b2b_fa2: "Nein. Die meisten Clubs, mit denen wir arbeiten, nehmen Buchungen über WhatsApp und einen Papierplan entgegen, und das ist völlig in Ordnung. Deine Seite kann die Leute einfach an dein Telefon schicken.",
  b2b_fq3: "Wem gehören die Gastdaten?",
  b2b_fa3: "Dir. Anfragen kommen mit den Kontaktdaten des Gastes, damit du direkt antworten kannst. Wir stehen nicht dazwischen.",
  b2b_fq4: "Kann ich meine Preise selbst ändern?",
  b2b_fa4: "Ja, im Dashboard, und die Änderung ist sofort live. Du kannst auch Tage sperren, an denen du voll oder geschlossen bist.",
  b2b_fq5: "Und wenn ich aufhören will?",
  b2b_fa5: "Sag Bescheid, dann nehmen wir die Seite offline. Kein Vertrag, keine Kündigungsfrist, keine Exklusivitätsklausel.",
  about_title: "Über diesen Beachclub",
  facilities_title: "Ausstattung",
  hours_title: "Öffnungszeiten",
  rules_title: "Gut zu wissen",
  good_for: "Passt zu",
  reviews_google: "Google-Bewertungen",
  reviews_sunbedly: "sunbedly-Bewertungen",
  hours_unknown: "Öffnungszeiten noch nicht bestätigt. Frag vor der Anreise beim Club nach.",
  club_faq_title: "Fragen zu {club}",
  club_title_type: "Beachclub",
  club_title_in: "Beachclub in {town}",
  cfaq_q_price: "Was kostet eine Liege bei {club}?",
  cfaq_a_price: "Ein Platz bei {club} beginnt bei {price}. Die Preise legt der Club selbst fest und in der Hochsaison können sie abweichen, sieh es also als Startpreis, nicht als Angebot.",
  cfaq_a_price_none: "{club} hat keine Preise veröffentlicht, deshalb steht dort \"Preis auf Anfrage\". Schreib den Club direkt an, dann bekommst du den Tagespreis.",
  cfaq_q_book: "Muss ich bei {club} vorher reservieren?",
  cfaq_a_book: "Im Juli und August sind die vorderen Reihen früh weg, vorher buchen lohnt sich also. Sende hier eine Anfrage, der Club bestätigt direkt, meist per WhatsApp.",
  cfaq_q_hours: "Wann hat {club} geöffnet?",
  cfaq_q_contact: "Wie erreiche ich {club}?",
  cfaq_a_contact: "Ruf an, schreib per WhatsApp oder mail direkt von dieser Seite. Die meisten Beachclubs antworten am schnellsten über WhatsApp.",
  cfaq_q_where: "Wo genau liegt {club}?",
  cfaq_a_where: "{club} liegt in {town}. Nutze die Karte auf dieser Seite für die Route von Tür zu Tür.",
};

const fr: Dict = {
  nav_explore: "Explorer les clubs",
  nav_partner: "Inscrire votre club",
  hero_title_1: "{n} beach clubs.",
  hero_title_2: "Au même endroit.",
  hero_sub: "Comparez les vrais prix et réservez votre transat en quelques minutes.",
  search_placeholder: "Où ? Ville, région ou club",
  dest_label: "Destination",
  socials_title: "Leurs propres canaux",
  dest_all: "Toutes les destinations",
  dest_guide: "Guide de {place}",
  sug_region: "Côte",
  sug_town: "Ville",
  sug_club: "Club",
  search_cta: "Rechercher un beach club",
  near_me: "Près de moi",
  locating: "Localisation...",
  regions_eyebrow: "Régions",
  regions_title: "Toute la côte, déjà repérée",
  regions_sub: "Des plages célèbres aux criques tranquilles, choisissez une côte et voyez qui a un transat pour vous.",
  live_title: "Sur la plateforme aujourd’hui",
  live_sub: "Nous recensons nous-mêmes les beach clubs, côte après côte, de la Méditerranée à Bali et aux Caraïbes. Les clubs vérifiés affichent des tarifs réels, les autres attendent que leur gérant les revendique.",
  all_clubs: "Voir tous les clubs",
  stats_clubs: "beach clubs",
  stats_regions: "côtes",
  how_title: "Comment ça marche",
  how_1_t: "Trouvez votre plage",
  how_1_d: "Cherchez par région ou laissez-nous vous localiser. Tous les clubs dans une seule liste, pas dix onglets.",
  how_2_t: "Comparez les vrais prix",
  how_2_d: "Transats, parasols et ce qui est inclus. Sans appeler, sans deviner.",
  how_3_t: "Réservez votre transat",
  how_3_d: "Envoyez une demande, le club confirme directement. Votre place est retenue avant même votre arrivée.",
  owners_eyebrow: "Pour les gérants",
  owners_title: "Vous gérez un beach club ?",
  owners_sub: "Recevez des réservations avant même l’arrivée des voyageurs.",
  owners_cta: "Inscrire votre club",
  clubs_title: "Beach clubs",
  clubs_found: "clubs trouvés",
  filter_all: "Toutes les régions",
  verified: "Vérifié par nos soins",
  unverified: "Fiche non revendiquée",
  from: "dès",
  price_on_request: "Demander le tarif du jour",
  km_away: "km d’ici",
  view_club: "Voir le club",
  approx_note: "Position affichée au niveau de la ville.",
  amenity_bed_service: "Service au transat",
  amenity_jetty: "Ponton vers la mer",
  amenity_lunch: "Déjeuner et cuisine",
  amenity_towels: "Serviettes incluses",
  amenity_sunscreen: "Crème solaire incluse",
  amenity_cushions: "Coussins inclus",
  amenity_party: "DJ et événements",
  request_title: "Demander la disponibilité",
  request_sub: "Le club confirme directement. Aucun paiement avant confirmation.",
  form_name: "Votre nom",
  form_contact: "Numéro WhatsApp ou email",
  form_date: "Date de visite",
  form_party: "Personnes",
  form_message: "Autre chose ? (premier rang, cabane, déjeuner)",
  form_send: "Envoyer la demande",
  form_sending: "Envoi...",
  request_ok_t: "Demande envoyée",
  request_ok_d: "Nous la transmettons directement au club. Vous recevrez une réponse aux coordonnées que vous avez laissées.",
  request_ok_expect: "La plupart des clubs répondent sous 24 à 48 heures.",
  request_ok_fallback: "Pas de réponse après quelques jours ? Écrivez-leur directement sur WhatsApp, ou",
  request_error: "Une erreur s’est produite. Veuillez réessayer.",
  contact_us: "contactez-nous",
  wa_fastest: "Le moyen le plus rapide de les joindre",
  footer_contact: "Contact",
  footer_contact_note: "Une question sur sunbedly ?",
  claim_note: "C’est votre club ? Revendiquez cette page et fixez vos prix.",
  claim_cta: "Revendiquer ce club",
  partner_title: "Votre club, là où les voyageurs cherchent",
  partner_sub: "Une page gratuite avec vos prix, vos extras et des demandes directes sur votre téléphone. Sans frais d’inscription, sans exclusivité, arrêtez quand vous voulez.",
  partner_p1_t: "Fiche gratuite",
  partner_p1_d: "Vos transats, prix et extras sur une page efficace, en six langues.",
  partner_p2_t: "Des demandes, pas du spam",
  partner_p2_d: "Les voyageurs envoient des demandes structurées. Vous confirmez sur WhatsApp comme d’habitude.",
  partner_p3_t: "De nouveaux clients, pas vos habitués",
  partner_p3_d: "Nous amenons des voyageurs internationaux qui ne vous trouvent pas encore.",
  partner_form_club: "Nom du club",
  partner_form_name: "Personne à contacter",
  partner_form_contact: "Numéro WhatsApp ou email",
  partner_form_country: "Pays",
  partner_form_town: "Ville ou plage",
  partner_form_instagram: "Instagram (facultatif)",
  partner_form_system: "Utilisez-vous un système de réservation ?",
  partner_sys_yes: "Oui",
  partner_sys_no: "Non",
  partner_sys_unknown: "Je ne sais pas",
  partner_form_message: "Parlez-nous de votre club (facultatif)",
  partner_send: "Demander une fiche",
  partner_ok_t: "Demande reçue",
  partner_ok_d: "Nous préparons votre page et vous contactons pour confirmer prix et photos.",
  footer_tag: "Les beach clubs du monde, enfin réunis.",
  footer_travelers: "Voyageurs",
  footer_owners: "Gérants de clubs",
  footer_langs: "Langue",
  back_to_clubs: "Tous les clubs",
  no_results: "Aucun club trouvé. Essayez une autre région.",
  wa_book: "Réserver sur WhatsApp",
  sponsored_badge: "Sponsorisé",
  get_directions: "Itinéraire",
  show_map: "Voir la carte",
  show_list: "Voir la liste",
  last_checked: "Vérifié le",
  season_may_oct: "Généralement ouvert de mai à octobre",
  checking_availability: "Vérification de la disponibilité...",
  route_title: "Comment y aller",
  map_title: "Choisissez votre place sur la plage",
  map_sub: "Choisissez une date, puis sélectionnez les transats voulus. La rangée A est au bord de l’eau.",
  sea: "Mer",
  jetty_label: "Ponton",
  row_label: "Rangée",
  free_legend: "Libre",
  taken_legend: "Occupé",
  selected_legend: "Votre choix",
  max_spots: "Maximum 4 ensembles transat-parasol par réservation.",
  reserve_cta: "Réserver ces transats",
  reserve_ok_t: "Réservation envoyée",
  reserve_ok_d: "Le club confirme vos places aux coordonnées que vous avez laissées. Vous payez sur place.",
  conflict_err: "Quelqu’un vient de prendre une de ces places. Choisissez à nouveau.",
  your_spots: "Vos places",
  reviews_title: "Avis",
  reviews_count: "avis",
  no_reviews_yet: "Pas encore d’avis. Soyez le premier à partager votre visite.",
  write_review_cta: "Écrire un avis",
  review_form_title: "Partagez votre visite",
  review_form_sub: "Uniquement des visites réelles. Soyez utile au prochain voyageur.",
  review_name: "Votre nom",
  review_contact: "Email (non affiché publiquement)",
  review_rating: "Votre note",
  review_headline: "Titre (facultatif)",
  review_body: "Votre avis",
  review_visit_month: "Quand y êtes-vous allé ?",
  review_booked_via: "Comment avez-vous réservé ?",
  booked_whatsapp: "WhatsApp",
  booked_lidoo: "demande via sunbedly",
  booked_phone: "Téléphone",
  booked_walkin: "Sans réservation",
  booked_other: "Autre",
  review_send: "Publier l’avis",
  review_sending: "Publication...",
  review_ok_t: "Merci d’avoir partagé",
  review_ok_d: "Votre avis est en ligne. Il aide les autres voyageurs, et le club, à savoir à quoi s’attendre.",
  review_spam_err: "Veuillez retirer les liens de votre avis.",
  review_duplicate_err: "Vous avez déjà laissé un avis sur ce club.",
  review_generic_err: "Une erreur est survenue. Veuillez réessayer.",
  add_photo_cta: "Ajouter une photo",
  add_photo_title: "Partagez une photo de ce club",
  add_photo_sub: "Uniquement des photos réelles, prises par vous. Évitez les personnes reconnaissables qui n’ont pas donné leur accord. Nous vérifions chaque envoi avant publication.",
  photo_name: "Votre nom (facultatif)",
  photo_file: "Choisir une photo",
  photo_send: "Envoyer la photo",
  photo_sending: "Envoi...",
  photo_ok_t: "Merci, photo reçue",
  photo_ok_d: "Elle apparaîtra sur la page du club une fois vérifiée.",
  photo_err_type: "Utilisez une photo JPG, PNG ou WebP.",
  photo_err_size: "Photo trop volumineuse, maximum 6 Mo.",
  photo_err_generic: "Échec de l’envoi. Veuillez réessayer.",
  nav_faq: "FAQ",
  nav_prices: "Indice des prix",
  nav_method: "Comment nous vérifions",
  faq_title: "Questions fréquentes",
  faq_sub: "Tout ce que les voyageurs et les gérants nous demandent le plus.",
  faq_q1: "sunbedly est-il gratuit ?",
  faq_a1: "Oui. Chercher, comparer et envoyer une demande ne coûte rien. Vous payez le club directement, exactement comme si vous l’appeliez ou lui écriviez vous-même.",
  faq_q2: "Comment fonctionne la réservation ?",
  faq_a2: "Vous envoyez une demande avec vos dates et le nombre de personnes. Le club confirme directement, généralement par WhatsApp ou téléphone. Le paiement se fait sur place, pas sur sunbedly.",
  faq_q3: "Que se passe-t-il si un club ne répond pas ?",
  faq_a3: "La plupart des clubs répondent sous 24 à 48 heures. Pas de réponse après quelques jours ? Écrivez-leur directement sur WhatsApp, ou contactez-nous et nous relancerons le club.",
  faq_q4: "Les prix sont-ils réels ?",
  faq_a4: "Les clubs vérifiés affichent un prix publié et la date de la dernière vérification. Les annonces non vérifiées affichent \"prix sur demande\" jusqu’à confirmation directe par le propriétaire.",
  faq_q5: "Puis-je annuler ou modifier ma réservation ?",
  faq_a5: "Contactez directement le club, de la même manière que vous avez réservé. sunbedly ne traite ni les paiements ni les annulations, le club gère votre réservation du début à la fin.",
  faq_q6: "Ai-je besoin d’un compte pour chercher ou réserver ?",
  faq_a6: "Non. Chercher et envoyer une demande ne nécessite jamais d’inscription. Les propriétaires de clubs créent une fiche gratuite s’ils veulent gérer leur page.",
  faq_q7: "Comment inscrire mon beach club sur sunbedly ?",
  faq_a7: "Faites une demande sur notre page partenaire. C’est gratuit, sans exclusivité, et vous pouvez arrêter à tout moment.",
  faq_q8: "Quels pays sunbedly couvre-t-il ?",
  faq_a8: "Nous cartographions littoral par littoral, et nous en sommes à dix-neuf pays : Italie, Espagne, Grèce, Croatie, France, Portugal, Turquie, Chypre, Malte, Monténégro, Albanie, Émirats, Thaïlande, Indonésie, Mexique, États-Unis, Afrique du Sud, Tanzanie et Île Maurice. Et cela continue.",
  meta_clubs_desc: "Parcourez les beach clubs sur sunbedly : tarifs réels, équipements et contact direct, sur les côtes de dix-neuf pays, d’Ibiza à Bali.",
  search_label: "Destination",
  trust_clubs: "beach clubs recensés",
  trust_coasts: "côtes couvertes",
  trust_free: "gratuit, sans compte",
  destinations_title: "Choisissez votre côte",
  destinations_sub: "Six littoraux recensés club par club. Commencez par votre destination.",
  explore_region: "Découvrir",
  why_title: "Pourquoi sunbedly",
  why_1_t: "Des prix réels, avant de partir",
  why_1_d: "Quand un club publie ses tarifs, nous les affichons. Pas de mauvaise surprise sur le prix de la journée une fois les pieds dans le sable.",
  why_2_t: "Contact direct avec le club",
  why_2_d: "Appelez, écrivez sur WhatsApp ou envoyez un mail depuis la page. Aucun intermédiaire entre vous et la plage.",
  why_3_t: "Sans compte, sans frais de réservation",
  why_3_d: "Cherchez, comparez et envoyez une demande sans inscription. Vous payez le club, votre argent ne passe jamais par nous.",
  contact_call: "Appeler",
  near_word: "près de",
  oh_daily: "Tous les jours",
  oh_in_season: "en saison",
  oh_summer: "Saison estivale",
  co_italy: "Italie",
  co_spain: "Espagne",
  co_greece: "Grèce",
  co_croatia: "Croatie",
  co_france: "France",
  co_portugal: "Portugal",
  co_uae: "Émirats arabes unis",
  co_netherlands: "Pays-Bas",
  co_mexico: "Mexique",
  co_indonesia: "Indonésie",
  co_united_states: "États-Unis",
  co_thailand: "Thaïlande",
  co_turkey: "Turquie",
  co_south_africa: "Afrique du Sud",
  co_tanzania: "Tanzanie",
  co_mauritius: "Île Maurice",
  co_malta: "Malte",
  co_montenegro: "Monténégro",
  co_albania: "Albanie",
  co_cyprus: "Chypre",
  pu_bed: "par transat",
  pu_bed_parasol: "transat avec parasol",
  pu_two_beds: "2 transats avec parasol",
  pu_umbrella: "par parasol",
  pu_person: "par personne",
  pu_spot: "par emplacement",
  pu_daypass: "pass journée par personne",
  pu_half_day: "demi-journée",
  pu_per_day: "par jour",
  pu_per_week: "par semaine",
  pu_detail: "Selon la description du club",
  contact_whatsapp: "WhatsApp",
  contact_email: "Email",
  contact_title: "Contacter ce club",
  contact_sub: "La plupart des clubs répondent le plus vite sur WhatsApp.",
  rq_title: "Demander une date",
  rq_sub_wa: "Nous ouvrons WhatsApp avec votre demande déjà rédigée, dans la langue du club.",
  rq_sub_mail: "Nous ouvrons votre messagerie avec la demande déjà rédigée, dans la langue du club.",
  rq_date: "Date",
  rq_beds: "Transats",
  rq_name: "Votre nom",
  rq_contact: "Téléphone ou e-mail",
  rq_send_wa: "Envoyer sur WhatsApp",
  rq_sub_phone: "Nous notons votre demande et vous donnons le numéro. Ce club prend les demandes par téléphone.",
  rq_send_phone: "Afficher le numéro",
  rq_ok_title_phone: "Appelez-les et demandez ceci",
  rq_ok_warn_phone: "Ce club a une ligne fixe, WhatsApp n'arrivera pas. Lisez la phrase ci-dessous au téléphone.",
  rq_send_mail: "Envoyer par e-mail",
  rq_sending_wa: "Ouverture de WhatsApp",
  rq_sending_mail: "Ouverture de votre messagerie",
  rq_ok_title_wa: "Votre demande est prête dans WhatsApp",
  rq_ok_title_mail: "Votre demande est prête dans votre messagerie",
  rq_ok_club: "Club",
  rq_ok_who: "Au nom de",
  rq_ok_warn_wa:
    "Le club n’a pas encore votre message. Appuyez sur envoyer dans WhatsApp, sinon rien ne part.",
  rq_ok_warn_mail:
    "Le club n’a pas encore votre message. Appuyez sur envoyer dans votre messagerie, sinon rien ne part.",
  rq_ok_reopen_wa: "Rouvrir WhatsApp",
  rq_ok_reopen_mail: "Rouvrir la messagerie",
  rq_ok_call: "Appeler {phone}",
  rq_error: "Cela n’a pas fonctionné. Utilisez les boutons ci-dessous pour joindre le club.",
  rq_phone_only: "Ce club prend les demandes par téléphone.",
  rq_sticky: "Disponibilité",
  rq_privacy: "Votre nom et vos coordonnées vont uniquement à ce club.",
  nav_privacy: "Confidentialité",
  map_failed: "La carte n’a pas pu se charger. Le reste de la page fonctionne.",
  sort_label: "Trier",
  sort_relevance: "Notre choix",
  sort_rating: "Les mieux notés",
  sort_price_asc: "Prix, croissant",
  sort_price_desc: "Prix, décroissant",
  sort_name: "Nom, de A à Z",
  filter_has_price: "Uniquement les clubs avec un tarif publié",
  results_count: "{n} beach clubs",
  results_count_one: "1 beach club",
  updated_by: "Vérifié par sunbedly le {date}",
  foot_role: "sunbedly référence des beach clubs. Nous ne sommes pas le vendeur, nous ne prenons aucune commission et ne traitons aucun paiement : vous payez le club, sur place.",
  nav_terms: "Conditions",
  ck_title: "Statistiques, uniquement si vous acceptez",
  ck_body: "Nous aimerions utiliser Google Analytics pour voir quelles côtes sont consultées. Cela dépose un cookie. Rien ne se charge avant votre choix, et refuser ne change rien au fonctionnement du site.",
  ck_accept: "Accepter",
  ck_reject: "Refuser",
  ck_more: "Ce que nous collectons",
  ck_settings: "Choix des cookies",
  rf_title: "Réponses directes sur {place}",
  rf_q_price: "Combien coûte un transat en {place} ?",
  rf_a_price: "Les tarifs vont de {cur} {low} à {cur} {high}, avec {cur} {mid} comme valeur médiane. Mesuré sur les {n} clubs en {place} qui publient un tarif, sur {total} référencés. Vérifié le {date}.",
  rf_q_count: "Combien de beach clubs y a-t-il en {place} ?",
  rf_a_count: "sunbedly référence {total} beach clubs en {place}, répartis sur {towns} communes. {rated} ont une note Google, en moyenne {rating} sur 5.",
  rf_q_cheap: "Quel beach club en {place} est le moins cher ?",
  rf_a_cheap: "Parmi les clubs en {place} qui publient un tarif, {club} est le moins cher à {cur} {low}. Les clubs sans tarif publié ne sont pas dans cette comparaison, il peut donc en exister de moins chers.",
  rf_q_book: "Peut-on réserver un transat en ligne en {place} ?",
  rf_a_book: "Presque jamais. Les beach clubs en {place} prennent les réservations par téléphone ou WhatsApp, souvent le jour même. sunbedly rédige le message dans la langue du club et vous met en relation directe. Sans compte ni frais.",
  region_clubs_title: "Clubs par côte",
  rg_sicily: "Sicile",
  rg_miami: "Miami et Miami Beach",
  rg_tulum: "Tulum et Riviera Maya",
  rg_cape_town: "Le Cap",
  rg_zanzibar: "Zanzibar",
  rg_mauritius: "Île Maurice",
  rg_bali: "Bali",
  rg_phuket: "Phuket",
  rg_koh_samui: "Koh Samui",
  rg_abu_dhabi: "Abu Dhabi",
  rg_turkey_aegean: "Bodrum et Çeşme",
  rg_cyprus: "Chypre",
  rg_malta: "Malte et Gozo",
  rg_canaries: "Îles Canaries",
  rg_montenegro: "Monténégro",
  rg_albania: "Riviera albanaise",
  rg_sardinia: "Sardaigne",
  rg_cote_azur: "Côte d’Azur",
  rg_puglia: "Pouilles",
  rg_amalfi: "Côte amalfitaine et Capri",
  rg_romagna: "Riviera Romagnole",
  rg_versilia_liguria: "Versilia et Ligurie",
  rg_costa_brava: "Costa Brava",
  rg_costa_blanca: "Costa Blanca",
  rg_costa_del_sol: "Costa del Sol",
  rg_mallorca: "Majorque",
  rg_ibiza: "Ibiza",
  rg_algarve: "Algarve",
  rg_mykonos_santorini: "Mykonos et Santorin",
  rg_crete_rhodes: "Crète et Rhodes",
  rg_corfu_halkidiki: "Corfou et Chalcidique",
  rg_croatia_south: "Dalmatie du Sud",
  rg_croatia_central: "Dalmatie centrale",
  rg_croatia_north: "Istrie et Kvarner",
  rg_dubai: "Dubaï",
  rg_greece: "Îles grecques",
  filters_show: "Filtres",
  filters_hide: "Masquer les filtres",
  filters_active: "{n} actifs",
  lang_offer: "Cette page existe aussi en {lang}.",
  lang_switch: "Basculer",
  lang_dismiss: "Non merci",
  pg_prev: "Précédent",
  pg_next: "Suivant",
  pg_of: "Page {a} sur {b}",
  cl_none_t: "Aucun résultat pour l’instant",
  cl_none_d: "Essayez avec un filtre de moins, ou cherchez une ville plutôt qu’un littoral.",
  cl_clear_all: "Tout effacer",
  bk_title: "Enregistrer une réservation prise par vos soins",
  bk_sub: "Quelqu’un a téléphoné ou s’est présenté. Notez-le ici : votre journée reste au même endroit et les transats que nous affichons sont les bons.",
  bk_name: "Nom",
  bk_contact: "Téléphone ou email (facultatif)",
  bk_date: "Date",
  bk_beds: "Transats",
  bk_note: "Note (facultatif)",
  bk_add: "Ajouter",
  bk_saved: "Ajoutée.",
  bk_delete: "Supprimer",
  bk_by_club: "Prise par vous",
  bk_by_site: "Via sunbedly",
  bk_today: "Aujourd’hui",
  bk_beds_today: "{n} transats réservés aujourd’hui",
  mg_title: "Tableau de bord",
  mg_view_public: "Voir la page publique",
  mg_requests: "Demandes de réservation",
  mg_requests_sub: "Les visiteurs qui ont demandé vos transats. Confirmez et répondez au contact laissé.",
  mg_no_requests: "Aucune demande pour l’instant. Elles apparaissent ici dès qu’un visiteur en envoie une.",
  mg_reservations: "Réservations de transats",
  mg_res_sub: "Transats précis choisis sur votre plan de plage.",
  mg_close_dates: "Fermer des journées",
  mg_close_sub: "Journées où vous êtes fermés, complets ou privatisés. Les visiteurs ne voient plus de transats pour ces dates.",
  mg_no_closed: "Aucune journée fermée. Votre plage apparaît ouverte à toutes les dates.",
  mg_note_opt: "Note (facultatif)",
  mg_private: "Événement privé",
  mg_close_btn: "Fermer cette date",
  mg_reopen: "Rouvrir",
  mg_prices: "Tarifs et contact",
  mg_photos: "Vos photos",
  mg_saved: "Enregistré et en ligne.",
  mg_confirm: "Confirmer",
  mg_decline: "Refuser",
  mg_lang: "Langue",
  ofaq_title: "Questions que vous vous posez",
  ofaq_q1: "Comment avez-vous eu mes coordonnées ?",
  ofaq_a1: "De ce que votre établissement publie lui-même : votre site, et l’API officielle Google Places pour la note et les photos. Rien d’acheté, rien de récupéré sur Instagram ou les sites d’avis. Un email suffit pour tout faire supprimer.",
  ofaq_q2: "Qui d’autre peut ouvrir ce lien ?",
  ofaq_a2: "Toute personne à qui vous le transférez : traitez-le comme une clé. Il ne peut pas être deviné. Demandez-nous-en un nouveau et l’ancien cesse aussitôt de fonctionner.",
  ofaq_q3: "Que deviennent les coordonnées de mes clients ?",
  ofaq_a3: "Elles vous parviennent et s’arrêtent là. Une demande vous montre le téléphone ou l’email du visiteur pour répondre directement. Nous ne les vendons pas, ne les démarchons pas, et n’encaissons jamais : aucune donnée bancaire nulle part.",
  ofaq_q4: "Pourquoi ma note Google est-elle affichée ?",
  ofaq_a4: "Parce qu’une page sans note passe inaperçue, et la vôtre est déjà publique. Elle vient de l’API officielle de Google, toujours créditée, et n’est jamais mélangée à une note maison.",
  ofaq_q5: "Puis-je retirer ma page ?",
  ofaq_a5: "Oui, un email et elle disparaît, en général le jour même. Aucun préavis, aucun contrat, et nous ne demanderons pas trois fois pourquoi.",
  ofaq_contact: "Écrivez-nous",
  region_meta_title: "Plages et beach clubs à {place}",
  region_meta_desc: "{n} beach clubs à {place} : tarifs réels, équipements et contact direct. Comparez-les et joignez-les vous-même sur sunbedly.",
  th_kids_area: "Beach clubs pour les familles",
  th_pool: "Beach clubs avec piscine",
  th_pets_allowed: "Beach clubs qui acceptent les chiens",
  th_accessible: "Beach clubs accessibles en fauteuil roulant",
  th_water_sports: "Beach clubs avec sports nautiques",
  th_dj: "Beach clubs avec DJ",
  th_cabanas: "Beach clubs avec cabanes",
  th_parking: "Beach clubs avec parking",
  th_in: "{what} : {place}",
  th_meta: "{what} : {place} — {n} clubs avec tarifs réels, équipements et contact direct. Comparez-les et joignez-les vous-même sur sunbedly.",
  th_meta_all: "{what} : {n} clubs sur {r} côtes, avec tarifs réels, équipements et contact direct sur sunbedly.",
  th_intro: "Chaque club ci-dessous figure ici d’après ce qu’il publie sur ses propres équipements. Les tarifs et les horaires sont indiqués là où le club les rend publics, et vous le contactez directement.",
  th_browse: "Vous cherchez quelque chose de précis ?",
  th_other_regions: "La même chose sur d’autres côtes",
  th_all_regions: "Choisissez une côte",
  th_count: "{n} clubs",
  share: "Partager",
  share_copied: "Lien copié",
  badge_price_shown: "Tarif affiché",
  no_cut_note: "Vous payez le club, sur place. Nous ne prenons aucune commission.",
  no_account_note: "Sans compte, sans carte, sans frais de réservation.",
  reviews_count_google: "{n} avis Google",
  region_priced_note: "{n} d’entre eux publient le prix d’un transat.",
  contact_none_t: "Pas encore de ligne directe",
  contact_none_d: "Nous n’avons pas de numéro pour ce club. Leur site reste le plus rapide pour les joindre en attendant qu’ils rejoignent sunbedly.",
  contact_website: "Voir leur site",
  contact_none_bare: "Nous n’avons pas encore de coordonnées pour ce club. Vous le gérez ? Revendiquez la page et ajoutez-les en une minute.",
  fac_bar: "Bar",
  fac_restaurant: "Restaurant",
  fac_showers: "Douches",
  fac_sunbeds: "Transats",
  fac_umbrellas: "Parasols",
  fac_dj: "DJ",
  fac_live_music: "Musique live",
  fac_parking: "Parking",
  fac_accessible: "Accessible",
  fac_wifi: "Wifi",
  fac_pool: "Piscine",
  fac_terrace: "Terrasse",
  fac_toilets: "Toilettes",
  fac_kids_area: "Espace enfants",
  fac_playground: "Aire de jeux",
  fac_sea_view: "Vue mer",
  fac_water_sports: "Sports nautiques",
  fac_beach_volley: "Beach-volley",
  fac_beach_tennis: "Beach tennis",
  fac_changing_rooms: "Cabines",
  fac_cabanas: "Cabanes",
  fac_lifeguard: "Maître-nageur",
  fac_pets_allowed: "Animaux admis",
  fac_events: "Événements",
  fac_jetty: "Ponton",
  fac_massage: "Massage",
  fac_card_payment: "Paiement par carte",
  fac_boat_rental: "Location de bateaux",
  fac_towels: "Serviettes incluses",
  fac_sunscreen: "Crème solaire fournie",
  fac_bed_service: "Service au transat",
  fac_lockers: "Casiers",
  fac_spa: "Spa",
  fac_gym: "Salle de sport",
  fac_yoga: "Yoga",
  fac_sports: "Sports et jeux",
  fac_boat_shuttle: "Navette bateau",
  fac_sea_access: "Accès direct à la mer",
  fac_lounge: "Espace lounge",
  fac_shop: "Boutique",
  fac_adults_only: "Réservé aux adultes",
  fac_nightclub: "Club de nuit",
  fac_grounds: "Jardins et espaces verts",
  gallery_all: "Voir les {n} photos",
  gallery_close: "Fermer",
  gallery_prev: "Photo précédente",
  gallery_next: "Photo suivante",
  photo_by: "Photo : {name} via Google",
  photo_credit: "Photo : {name}",
  photo_google: "Photo via Google",
  photo_nearby: "Plage près de {place}",
  photo_nearby_note: "Photo des environs, pas du club lui-même",
  nav_photo_credits: "Crédits photo",
  nearby_title: "Autres beach clubs à proximité",
  nearby_sub: "Même littoral, autre ambiance.",
  crumb_home: "Accueil",
  book_panel_title: "Vérifier les disponibilités",
  book_panel_sub: "Nous transmettons votre demande directement au club. Sans compte, sans carte.",
  guides_eyebrow: "Guides",
  guides_title: "Quel beach club en vaut vraiment la peine",
  guides_sub: "Des classements établis à partir des tarifs que les clubs publient eux-mêmes, pas selon qui a payé sa place. Mis à jour à mesure que les côtes évoluent.",
  guides_read: "Lire le guide",
  guides_all: "Tous les guides",
  guides_updated: "Mis à jour",
  guides_clubs_in_piece: "Les clubs de ce guide",
  nav_guides: "Guides",
  filters_title: "Filtres",
  filter_priced: "Tarif affiché à l’avance",
  filters_clear: "Effacer les filtres",
  footer_destinations: "Destinations",
  footer_beach_clubs_in: "Beach clubs à {place}",
  footer_more_coasts: "Nous recensons d’autres côtes, une à la fois. Mieux vaut couvrir une côte correctement que d’ajouter un pays avec deux clubs seulement.",
  b2b_eyebrow: "Pour les gérants de beach clubs",
  b2b_title: "Votre plage, vos prix, vos clients.",
  b2b_sub: "Une page gratuite sur sunbedly avec vos vrais tarifs, vos photos et des demandes qui arrivent directement sur votre téléphone. Nous ne prenons aucune commission et ne touchons jamais votre argent.",
  b2b_cta: "Inscrire votre club",
  b2b_cta_sub: "Gratuit. Sans contrat, sans exclusivité, arrêtez quand vous voulez.",
  b2b_stat_clubs: "clubs déjà recensés",
  b2b_stat_langs: "langues, une page",
  b2b_stat_fee: "commission, jamais",
  b2b_what_title: "Ce que vous obtenez",
  b2b_w1_t: "Une page qui vend pour vous",
  b2b_w1_d: "Vos tarifs, équipements, horaires et règlement, en six langues, sur une page conçue pour être trouvée. Les voyageurs cessent d’appeler pour demander le prix d’un transat.",
  b2b_w2_t: "Votre propre tableau de bord",
  b2b_w2_d: "Confirmez ou refusez les demandes, ajoutez des photos, changez vos prix et bloquez les dates où vous êtes complet ou fermé. Un lien, aucun mot de passe à oublier.",
  b2b_w3_t: "Les clients vous contactent directement",
  b2b_w3_d: "Les boutons appel, WhatsApp et email pointent vers votre numéro, pas le nôtre. Vous gardez la relation et les données client.",
  b2b_w4_t: "Vous gardez chaque euro",
  b2b_w4_d: "Le client vous paie sur la plage, exactement comme aujourd’hui. Aucune commission, aucun frais de service prélevé sur votre client, aucune attente de virement.",
  b2b_diff_title: "Ce qui change par rapport aux plateformes habituelles",
  b2b_diff_us: "sunbedly",
  b2b_diff_them: "Plateforme de réservation classique",
  b2b_d1: "Commission par réservation",
  b2b_d1_us: "Aucune",
  b2b_d1_them: "Pourcentage non communiqué",
  b2b_d2: "Frais pour votre client",
  b2b_d2_us: "Aucun",
  b2b_d2_them: "Frais de service non remboursables",
  b2b_d3: "Qui détient l’argent",
  b2b_d3_us: "Vous. Nous n’y touchons pas",
  b2b_d3_them: "La plateforme, puis reverse",
  b2b_d4: "Vos prix avant réservation",
  b2b_d4_us: "Publics sur votre page",
  b2b_d4_them: "Cachés jusqu’au paiement",
  b2b_d5: "Coordonnées du client",
  b2b_d5_us: "Directement à vous",
  b2b_d5_them: "Conservées par la plateforme",
  b2b_d6: "Exclusivité",
  b2b_d6_us: "Aucune, partez quand vous voulez",
  b2b_d6_them: "Souvent contractuelle",
  b2b_honest_title: "Soyons honnêtes avec vous",
  b2b_honest_d: "Nous débutons. sunbedly recense {clubs} plages et grandit côte après côte, alors nous n’allons pas vous parler de millions d’utilisateurs. Ce que nous pouvons promettre : votre fiche ne coûte rien, vos prix sont visibles avant que quelqu’un ne se déplace, et rien ne s’interpose entre vous et votre client. Si cela cesse d’être vrai, vous partez le jour même.",
  b2b_how_title: "S’inscrire",
  b2b_how_1_t: "Envoyez le formulaire",
  b2b_how_1_d: "Nom du club, ville et un numéro pour vous joindre. Deux minutes.",
  b2b_how_2_t: "Nous construisons votre page",
  b2b_how_2_d: "Nous étudions votre plage et rédigeons votre page, puis nous vous envoyons le lien du tableau de bord pour tout vérifier et corriger.",
  b2b_how_3_t: "Les demandes arrivent",
  b2b_how_3_d: "Les voyageurs vous trouvent, voient votre vrai prix et vous écrivent directement.",
  b2b_faq_title: "Les questions des gérants",
  b2b_fq1: "Combien ça coûte ?",
  b2b_fa1: "Rien. Aucun frais d’inscription, aucune commission, aucune part de ce que dépense votre client. Si nous introduisons un jour une option payante, ce sera quelque chose que vous choisirez d’acheter, pas un pourcentage prélevé sur vos réservations.",
  b2b_fq2: "Ai-je besoin d’un système de réservation ?",
  b2b_fa2: "Non. La plupart des clubs avec lesquels nous travaillons prennent les réservations sur WhatsApp avec un plan papier, et cela convient très bien. Votre page peut simplement envoyer les gens vers votre téléphone.",
  b2b_fq3: "À qui appartiennent les données client ?",
  b2b_fa3: "À vous. Les demandes arrivent avec les coordonnées du client pour que vous répondiez directement. Nous ne nous interposons pas.",
  b2b_fq4: "Puis-je changer mes prix moi-même ?",
  b2b_fa4: "Oui, depuis votre tableau de bord, et le changement est immédiat. Vous pouvez aussi fermer les dates où vous êtes complet ou fermé.",
  b2b_fq5: "Et si je veux arrêter ?",
  b2b_fa5: "Dites-le nous et nous retirons la page. Aucun contrat, aucun préavis, aucune clause d’exclusivité.",
  about_title: "À propos de ce beach club",
  facilities_title: "Équipements",
  hours_title: "Horaires d’ouverture",
  rules_title: "Bon à savoir",
  good_for: "Idéal pour",
  reviews_google: "Avis Google",
  reviews_sunbedly: "Avis sunbedly",
  hours_unknown: "Horaires pas encore confirmés. Vérifiez auprès du club avant de partir.",
  club_faq_title: "Questions sur {club}",
  club_title_type: "beach club",
  club_title_in: "beach club à {town}",
  cfaq_q_price: "Combien coûte un transat à {club} ?",
  cfaq_a_price: "Une place à {club} démarre à {price}. Le club fixe ses propres tarifs et ils peuvent changer en haute saison, considérez donc ce prix comme un point de départ, pas un devis.",
  cfaq_a_price_none: "{club} n’a pas publié ses tarifs, d’où la mention \"prix sur demande\". Écrivez directement à la plage et ils vous donneront le tarif du jour.",
  cfaq_q_book: "Faut-il réserver à {club} ?",
  cfaq_a_book: "En juillet et août, les meilleures rangées partent vite, réserver en vaut donc la peine. Envoyez une demande ici et le club confirme directement, généralement sur WhatsApp.",
  cfaq_q_hours: "Quand {club} est-il ouvert ?",
  cfaq_q_contact: "Comment contacter {club} ?",
  cfaq_a_contact: "Appelez, écrivez sur WhatsApp ou envoyez un mail depuis cette page. La plupart des clubs répondent le plus vite sur WhatsApp.",
  cfaq_q_where: "Où se trouve exactement {club} ?",
  cfaq_a_where: "{club} se trouve à {town}. Utilisez la carte de cette page pour l’itinéraire porte à porte.",
};

// Spanish matters more than its late arrival suggests: Spaniards are the single
// largest visitor group in Ibiza (833k in 2025, ahead of the UK), and Spain is
// a coast we are actively mapping.
const es: Dict = {
  nav_explore: "Explorar clubes",
  nav_partner: "Publica tu club",
  hero_title_1: "{n} clubes de playa.",
  hero_title_2: "En un solo sitio.",
  hero_sub: "Mira precios reales y reserva tu hamaca en minutos.",
  search_placeholder: "¿Adónde vas? Pueblo, región o club",
  dest_label: "Destino",
  socials_title: "Sus propios canales",
  dest_all: "Todos los destinos",
  dest_guide: "Guía de {place}",
  sug_region: "Costa",
  sug_town: "Localidad",
  sug_club: "Club",
  search_cta: "Buscar beach clubs",
  near_me: "Cerca de mí",
  locating: "Localizándote...",
  regions_eyebrow: "Regiones",
  regions_title: "Donde el mar ya te espera",
  regions_sub: "De playas famosas a calas tranquilas, elige una costa y mira quién tiene una hamaca para ti.",
  live_title: "Hoy en la plataforma",
  live_sub: "Mapeamos los beach clubs nosotros mismos, costa a costa, del Mediterráneo a Bali y el Caribe. Los verificados muestran precios reales, el resto espera a que su dueño los reclame.",
  all_clubs: "Ver todos los clubes",
  stats_clubs: "beach clubs",
  stats_regions: "costas",
  how_title: "Cómo funciona",
  how_1_t: "Encuentra tu playa",
  how_1_d: "Busca por región o deja que busquemos cerca de ti. Todos los clubes en una sola lista, no en diez pestañas.",
  how_2_t: "Compara precios reales",
  how_2_d: "Hamacas, sombrillas y qué está incluido. Sin llamar a todas partes, sin adivinar.",
  how_3_t: "Reserva tu hamaca",
  how_3_d: "Envía una solicitud y el club confirma directamente. Tu sitio queda resuelto antes de que llegues.",
  owners_eyebrow: "Para dueños",
  owners_title: "¿Tienes un beach club?",
  owners_sub: "Consigue reservas de viajeros antes de que aterricen.",
  owners_cta: "Publica tu club",
  clubs_title: "Beach clubs",
  clubs_found: "clubes encontrados",
  filter_all: "Todas las regiones",
  verified: "Verificado por nosotros",
  unverified: "Aún sin reclamar",
  from: "desde",
  price_on_request: "Pregunta el precio de hoy",
  km_away: "km de distancia",
  view_club: "Ver club",
  approx_note: "Mostramos la ubicación a nivel de pueblo.",
  amenity_bed_service: "Servicio en tu hamaca",
  amenity_jetty: "Pasarela al mar",
  amenity_lunch: "Comida y cocina",
  amenity_towels: "Toallas incluidas",
  amenity_sunscreen: "Protector solar incluido",
  amenity_cushions: "Cojines incluidos",
  amenity_party: "DJ y eventos",
  request_title: "Solicita disponibilidad",
  request_sub: "El club confirma directamente. No pagas hasta que lo haga.",
  form_name: "Tu nombre",
  form_contact: "Número de WhatsApp o email",
  form_date: "Fecha de la visita",
  form_party: "Personas",
  form_message: "¿Algo más? (primera fila, cabaña, comida)",
  form_send: "Enviar solicitud",
  form_sending: "Enviando...",
  request_ok_t: "Solicitud enviada",
  request_ok_d: "Se la pasamos directamente al club. Te responderán al contacto que nos dejaste.",
  request_ok_expect: "La mayoría de los clubes responde en 24 a 48 horas.",
  request_ok_fallback: "¿Sin respuesta después de unos días? Escríbeles directamente por WhatsApp, o",
  request_error: "Algo ha ido mal. Inténtalo de nuevo.",
  contact_us: "contacta con nosotros",
  wa_fastest: "La forma más rápida de contactar con ellos",
  footer_contact: "Contacto",
  footer_contact_note: "¿Preguntas sobre sunbedly?",
  claim_note: "¿Es tu club? Reclama esta página y pon tus propios precios.",
  claim_cta: "Reclamar este club",
  partner_title: "Pon tu club donde miran los viajeros",
  partner_sub: "Una página gratis con tus precios, tus extras y solicitudes directas a tu móvil. Sin cuota de alta, sin exclusividad, cancela cuando quieras.",
  partner_p1_t: "Ficha gratuita",
  partner_p1_d: "Tus hamacas, precios y extras en una página que funciona, en seis idiomas.",
  partner_p2_t: "Solicitudes, no spam",
  partner_p2_d: "Los viajeros envían solicitudes estructuradas. Tú confirmas por WhatsApp como ya haces.",
  partner_p3_t: "Clientes nuevos, no tus habituales",
  partner_p3_d: "Te traemos viajeros internacionales que hoy no te encuentran.",
  partner_form_club: "Nombre del club",
  partner_form_name: "Persona de contacto",
  partner_form_contact: "Número de WhatsApp o email",
  partner_form_country: "País",
  partner_form_town: "Pueblo o playa",
  partner_form_instagram: "Instagram (opcional)",
  partner_form_system: "¿Usas algún sistema de reservas?",
  partner_sys_yes: "Sí",
  partner_sys_no: "No",
  partner_sys_unknown: "No lo sé",
  partner_form_message: "Cuéntanos sobre tu club (opcional)",
  partner_send: "Solicitar una ficha",
  partner_ok_t: "Solicitud recibida",
  partner_ok_d: "Preparamos tu página y nos ponemos en contacto contigo para confirmar precios y fotos.",
  footer_tag: "Los beach clubs del mundo, en un solo sitio.",
  footer_travelers: "Viajeros",
  footer_owners: "Dueños de clubes",
  footer_langs: "Idioma",
  back_to_clubs: "Todos los clubes",
  no_results: "Todavía no hay clubes que coincidan. Prueba con otra región.",
  wa_book: "Reserva por WhatsApp",
  sponsored_badge: "Patrocinado",
  get_directions: "Cómo llegar",
  show_map: "Ver mapa",
  show_list: "Ver lista",
  last_checked: "Comprobado",
  season_may_oct: "Normalmente abierto de mayo a octubre",
  checking_availability: "Comprobando disponibilidad...",
  route_title: "Cómo llegar",
  map_title: "Elige tu sitio en la playa",
  map_sub: "Elige una fecha y toca las hamacas que quieras. La fila A está justo en el agua.",
  sea: "Mar",
  jetty_label: "Pasarela",
  row_label: "Fila",
  free_legend: "Libre",
  taken_legend: "Ocupada",
  selected_legend: "Tu elección",
  max_spots: "Máximo 4 sitios por reserva.",
  reserve_cta: "Reservar estas hamacas",
  reserve_ok_t: "Reserva enviada",
  reserve_ok_d: "El club te confirma tus sitios directamente al contacto que nos dejaste. Pagas en el club.",
  conflict_err: "Alguien acaba de coger uno de estos sitios. Elige otra vez.",
  your_spots: "Tus sitios",
  reviews_title: "Opiniones",
  reviews_count: "opiniones",
  no_reviews_yet: "Todavía no hay opiniones. Sé el primero en compartir tu visita.",
  write_review_cta: "Escribir una opinión",
  review_form_title: "Comparte tu visita",
  review_form_sub: "Solo visitas reales. Que sea útil para el próximo viajero.",
  review_name: "Tu nombre",
  review_contact: "Email (no se muestra públicamente)",
  review_rating: "Tu valoración",
  review_headline: "Título (opcional)",
  review_body: "Tu opinión",
  review_visit_month: "¿Cuándo estuviste?",
  review_booked_via: "¿Cómo reservaste?",
  booked_whatsapp: "WhatsApp",
  booked_lidoo: "solicitud vía sunbedly",
  booked_phone: "Teléfono",
  booked_walkin: "Sin reserva",
  booked_other: "Otro",
  review_send: "Publicar opinión",
  review_sending: "Publicando...",
  review_ok_t: "Gracias por compartir",
  review_ok_d: "Tu opinión ya está publicada. Ayuda a otros viajeros, y al club, a saber qué esperar.",
  review_spam_err: "Quita los enlaces de tu opinión, por favor.",
  review_duplicate_err: "Ya has valorado este club.",
  review_generic_err: "Algo ha ido mal. Inténtalo de nuevo.",
  add_photo_cta: "Añadir una foto",
  add_photo_title: "Comparte una foto de este club",
  add_photo_sub: "Solo fotos reales, hechas por ti. Evita personas reconocibles que no hayan dado su consentimiento. Revisamos cada foto antes de publicarla.",
  photo_name: "Tu nombre (opcional)",
  photo_file: "Elegir foto",
  photo_send: "Subir foto",
  photo_sending: "Subiendo...",
  photo_ok_t: "Gracias, foto recibida",
  photo_ok_d: "Aparecerá en la página del club una vez revisada.",
  photo_err_type: "Usa una foto en JPG, PNG o WebP, por favor.",
  photo_err_size: "La foto pesa demasiado, máximo 6 MB.",
  photo_err_generic: "Error al subir. Inténtalo de nuevo.",
  nav_faq: "Preguntas",
  nav_prices: "Índice de precios",
  nav_method: "Cómo verificamos",
  faq_title: "Preguntas frecuentes",
  faq_sub: "Todo lo que más nos preguntan viajeros y dueños de clubes.",
  faq_q1: "¿Es gratis usar sunbedly?",
  faq_a1: "Sí. Buscar, comparar y enviar una solicitud no cuesta nada. Pagas al club directamente, igual que si les llamaras o escribieras tú mismo.",
  faq_q2: "¿Cómo funciona la reserva exactamente?",
  faq_a2: "Envías una solicitud con tus fechas y el número de personas. El club confirma directamente, normalmente por WhatsApp o teléfono. El pago se hace en el club, no en sunbedly.",
  faq_q3: "¿Y si un club no responde?",
  faq_a3: "La mayoría de los clubes responde en 24 a 48 horas. Si no tienes noticias después de unos días, escríbeles directamente por WhatsApp, o contacta con nosotros y hablamos con el club.",
  faq_q4: "¿Los precios son reales?",
  faq_a4: "Los clubes verificados muestran un precio publicado y la fecha de la última comprobación. Las fichas sin verificar muestran \"precio a consultar\" hasta que el dueño lo confirma directamente.",
  faq_q5: "¿Puedo cancelar o cambiar mi reserva?",
  faq_a5: "Contacta con el club directamente, de la misma forma en que reservaste. sunbedly no procesa pagos ni cancelaciones, el club gestiona tu reserva de principio a fin.",
  faq_q6: "¿Necesito una cuenta para buscar o reservar?",
  faq_a6: "No. Para buscar y enviar una solicitud nunca hace falta registrarse. Los dueños de clubes crean una ficha gratuita si quieren gestionar su página.",
  faq_q7: "¿Cómo publico mi beach club en sunbedly?",
  faq_a7: "Solicítalo en nuestra página para dueños. Es gratis, no hay exclusividad, y puedes dejarlo cuando quieras.",
  faq_q8: "¿Qué países cubre sunbedly?",
  faq_a8: "Cartografiamos costa a costa, y ya son diecinueve países: Italia, España, Grecia, Croacia, Francia, Portugal, Turquía, Chipre, Malta, Montenegro, Albania, Emiratos, Tailandia, Indonesia, México, Estados Unidos, Sudáfrica, Tanzania y Mauricio. Y seguimos.",
  meta_clubs_desc: "Explora los beach clubs en sunbedly: precios reales, servicios y contacto directo, en las costas de diecinueve países, de Ibiza a Bali.",
  search_label: "Destino",
  trust_clubs: "beach clubs mapeados",
  trust_coasts: "costas cubiertas",
  trust_free: "gratis, sin cuenta",
  destinations_title: "Elige tu costa",
  destinations_sub: "Seis costas mapeadas club a club. Empieza por tu destino.",
  explore_region: "Descubrir",
  why_title: "Por qué sunbedly",
  why_1_t: "Precios reales, antes de ir",
  why_1_d: "Donde un club publica sus tarifas, nosotros las mostramos. Sin sorpresas con el precio del día cuando ya tienes los pies en la arena.",
  why_2_t: "Contacto directo con el club",
  why_2_d: "Llama, escribe por WhatsApp o manda un email desde la propia página. Sin intermediarios entre tú y la playa.",
  why_3_t: "Sin cuenta, sin gastos de reserva",
  why_3_d: "Busca, compara y envía una solicitud sin registrarte. Pagas al club, tu dinero nunca pasa por nosotros.",
  contact_call: "Llamar",
  near_word: "cerca de",
  oh_daily: "Todos los días",
  oh_in_season: "en temporada",
  oh_summer: "Temporada de verano",
  co_italy: "Italia",
  co_spain: "España",
  co_greece: "Grecia",
  co_croatia: "Croacia",
  co_france: "Francia",
  co_portugal: "Portugal",
  co_uae: "Emiratos Árabes Unidos",
  co_netherlands: "Países Bajos",
  co_mexico: "México",
  co_indonesia: "Indonesia",
  co_united_states: "Estados Unidos",
  co_thailand: "Tailandia",
  co_turkey: "Turquía",
  co_south_africa: "Sudáfrica",
  co_tanzania: "Tanzania",
  co_mauritius: "Mauricio",
  co_malta: "Malta",
  co_montenegro: "Montenegro",
  co_albania: "Albania",
  co_cyprus: "Chipre",
  pu_bed: "por hamaca",
  pu_bed_parasol: "hamaca con sombrilla",
  pu_two_beds: "2 hamacas con sombrilla",
  pu_umbrella: "por sombrilla",
  pu_person: "por persona",
  pu_spot: "por plaza",
  pu_daypass: "entrada de día por persona",
  pu_half_day: "media jornada",
  pu_per_day: "por día",
  pu_per_week: "por semana",
  pu_detail: "Según lo describe el club",
  contact_whatsapp: "WhatsApp",
  contact_email: "Email",
  contact_title: "Contacta con este club",
  contact_sub: "La mayoría de los clubes responde más rápido por WhatsApp.",
  rq_title: "Pregunta por una fecha",
  rq_sub_wa: "Abrimos WhatsApp con tu consulta ya escrita, en el idioma del club.",
  rq_sub_mail: "Abrimos tu correo con la consulta ya escrita, en el idioma del club.",
  rq_date: "Fecha",
  rq_beds: "Hamacas",
  rq_name: "Tu nombre",
  rq_contact: "Teléfono o correo",
  rq_send_wa: "Enviar por WhatsApp",
  rq_sub_phone: "Anotamos tu consulta y te damos el número. Este club atiende las solicitudes por teléfono.",
  rq_send_phone: "Ver el número",
  rq_ok_title_phone: "Llámalos y pregunta esto",
  rq_ok_warn_phone: "Este club tiene fijo, así que WhatsApp no llega. Lee por teléfono la frase de abajo.",
  rq_send_mail: "Enviar por correo",
  rq_sending_wa: "Abriendo WhatsApp",
  rq_sending_mail: "Abriendo tu correo",
  rq_ok_title_wa: "Tu solicitud está lista en WhatsApp",
  rq_ok_title_mail: "Tu solicitud está lista en tu correo",
  rq_ok_club: "Club",
  rq_ok_who: "A nombre de",
  rq_ok_warn_wa:
    "El club todavía no tiene tu mensaje. Pulsa enviar en WhatsApp, o no llegará nada.",
  rq_ok_warn_mail:
    "El club todavía no tiene tu mensaje. Pulsa enviar en tu correo, o no llegará nada.",
  rq_ok_reopen_wa: "Volver a abrir WhatsApp",
  rq_ok_reopen_mail: "Volver a abrir el correo",
  rq_ok_call: "Llamar {phone}",
  rq_error: "No ha funcionado. Usa los botones de abajo para contactar con el club.",
  rq_phone_only: "Este club atiende las solicitudes por teléfono.",
  rq_sticky: "Disponibilidad",
  rq_privacy: "Tu nombre y tus datos van solo a este club.",
  nav_privacy: "Privacidad",
  map_failed: "El mapa no ha cargado. El resto de la página funciona.",
  sort_label: "Ordenar",
  sort_relevance: "Nuestra selección",
  sort_rating: "Mejor valorados",
  sort_price_asc: "Precio, de menor a mayor",
  sort_price_desc: "Precio, de mayor a menor",
  sort_name: "Nombre, de la A a la Z",
  filter_has_price: "Solo clubes con precio publicado",
  results_count: "{n} clubes de playa",
  results_count_one: "1 club de playa",
  updated_by: "Comprobado por sunbedly el {date}",
  foot_role: "sunbedly lista clubes de playa. No somos el vendedor, no cobramos comisión y no gestionamos pagos: pagas al club, en el club.",
  nav_terms: "Condiciones",
  ck_title: "Estadísticas, solo si tú quieres",
  ck_body: "Nos gustaría usar Google Analytics para ver qué costas se consultan. Eso deja una cookie. No se carga nada hasta que elijas, y rechazar no cambia nada del sitio.",
  ck_accept: "Aceptar",
  ck_reject: "Rechazar",
  ck_more: "Qué recogemos",
  ck_settings: "Elección de cookies",
  rf_title: "Respuestas directas sobre {place}",
  rf_q_price: "¿Cuánto cuesta una hamaca en {place}?",
  rf_a_price: "Los precios van de {cur} {low} a {cur} {high}, con {cur} {mid} como valor central. Medido sobre los {n} clubes en {place} que publican precio, de {total} listados. Comprobado el {date}.",
  rf_q_count: "¿Cuántos clubes de playa hay en {place}?",
  rf_a_count: "sunbedly lista {total} clubes de playa en {place}, repartidos por {towns} localidades. {rated} tienen valoración de Google, con una media de {rating} sobre 5.",
  rf_q_cheap: "¿Cuál es el club de playa más barato en {place}?",
  rf_a_cheap: "De los clubes en {place} que publican precio, {club} es el más bajo con {cur} {low}. Los clubes sin precio publicado no entran en esa comparación, así que puede haber más baratos.",
  rf_q_book: "¿Se puede reservar una hamaca online en {place}?",
  rf_a_book: "Casi nunca. Los clubes de playa en {place} aceptan reservas por teléfono o WhatsApp, normalmente el mismo día. sunbedly escribe el mensaje en el idioma del club y te pone en contacto directo. Sin cuenta ni comisión.",
  region_clubs_title: "Clubes por costa",
  rg_sicily: "Sicilia",
  rg_miami: "Miami y Miami Beach",
  rg_tulum: "Tulum y Riviera Maya",
  rg_cape_town: "Ciudad del Cabo",
  rg_zanzibar: "Zanzíbar",
  rg_mauritius: "Isla Mauricio",
  rg_bali: "Bali",
  rg_phuket: "Phuket",
  rg_koh_samui: "Koh Samui",
  rg_abu_dhabi: "Abu Dabi",
  rg_turkey_aegean: "Bodrum y Çeşme",
  rg_cyprus: "Chipre",
  rg_malta: "Malta y Gozo",
  rg_canaries: "Islas Canarias",
  rg_montenegro: "Montenegro",
  rg_albania: "Riviera albanesa",
  rg_sardinia: "Cerdeña",
  rg_cote_azur: "Costa Azul",
  rg_puglia: "Apulia",
  rg_amalfi: "Costa Amalfitana y Capri",
  rg_romagna: "Riviera Romañola",
  rg_versilia_liguria: "Versilia y Liguria",
  rg_costa_brava: "Costa Brava",
  rg_costa_blanca: "Costa Blanca",
  rg_costa_del_sol: "Costa del Sol",
  rg_mallorca: "Mallorca",
  rg_ibiza: "Ibiza",
  rg_algarve: "Algarve",
  rg_mykonos_santorini: "Miconos y Santorini",
  rg_crete_rhodes: "Creta y Rodas",
  rg_corfu_halkidiki: "Corfú y Calcídica",
  rg_croatia_south: "Dalmacia meridional",
  rg_croatia_central: "Dalmacia central",
  rg_croatia_north: "Istria y Kvarner",
  rg_dubai: "Dubái",
  rg_greece: "Islas griegas",
  filters_show: "Filtros",
  filters_hide: "Ocultar filtros",
  filters_active: "{n} activos",
  lang_offer: "Esta página también está en {lang}.",
  lang_switch: "Cambiar",
  lang_dismiss: "No, gracias",
  pg_prev: "Anterior",
  pg_next: "Siguiente",
  pg_of: "Página {a} de {b}",
  cl_none_t: "Aún no hay resultados",
  cl_none_d: "Prueba con un filtro menos, o busca un pueblo en lugar de una costa.",
  cl_clear_all: "Borrar todo",
  bk_title: "Anota una reserva que has cogido tú",
  bk_sub: "Alguien ha llamado o se ha presentado. Anótalo aquí: tu día queda en un solo sitio y las hamacas que mostramos son las que quedan.",
  bk_name: "Nombre",
  bk_contact: "Teléfono o email (opcional)",
  bk_date: "Fecha",
  bk_beds: "Hamacas",
  bk_note: "Nota (opcional)",
  bk_add: "Añadir reserva",
  bk_saved: "Añadida.",
  bk_delete: "Eliminar",
  bk_by_club: "Cogida por ti",
  bk_by_site: "Vía sunbedly",
  bk_today: "Hoy",
  bk_beds_today: "{n} hamacas reservadas hoy",
  mg_title: "Panel del club",
  mg_view_public: "Ver la página pública",
  mg_requests: "Solicitudes de reserva",
  mg_requests_sub: "Viajeros que han preguntado por tus hamacas. Confirma y responde al contacto que dejaron.",
  mg_no_requests: "Aún no hay solicitudes. Aparecen aquí en cuanto alguien envía una.",
  mg_reservations: "Reservas de hamacas",
  mg_res_sub: "Hamacas concretas elegidas en tu plano de playa.",
  mg_close_dates: "Cerrar días",
  mg_close_sub: "Días en que estáis cerrados, llenos o alquilados en privado. Los viajeros dejan de ver hamacas para esas fechas.",
  mg_no_closed: "Ningún día cerrado. Tu playa aparece abierta en todas las fechas.",
  mg_note_opt: "Nota (opcional)",
  mg_private: "Evento privado",
  mg_close_btn: "Cerrar esta fecha",
  mg_reopen: "Reabrir",
  mg_prices: "Precios y contacto",
  mg_photos: "Tus fotos",
  mg_saved: "Guardado y publicado.",
  mg_confirm: "Confirmar",
  mg_decline: "Rechazar",
  mg_lang: "Idioma",
  ofaq_title: "Preguntas que puedes tener",
  ofaq_q1: "¿Cómo habéis conseguido mis datos?",
  ofaq_a1: "De lo que tu negocio publica por su cuenta: tu web, y la API oficial de Google Places para la valoración y las fotos. Nada comprado, nada extraído de Instagram ni de webs de reseñas. Puedes pedir que se borre todo con un correo.",
  ofaq_q2: "¿Quién más puede abrir este enlace?",
  ofaq_a2: "Cualquiera a quien se lo reenvíes, así que trátalo como una llave. No se puede adivinar. Pídenos uno nuevo y el anterior deja de funcionar al instante.",
  ofaq_q3: "¿Qué pasa con los datos de mis clientes?",
  ofaq_a3: "Llegan a ti y ahí se quedan. En una solicitud ves el teléfono o el correo del viajero para responder directamente. No los vendemos, no hacemos marketing y nunca cobramos, así que no hay datos de tarjeta en ningún sitio.",
  ofaq_q4: "¿Por qué aparece mi valoración de Google?",
  ofaq_a4: "Porque una página sin valoración pasa desapercibida, y la tuya ya es pública. Viene de la API oficial de Google, siempre atribuida a ellos, y nunca se mezcla con una nota nuestra.",
  ofaq_q5: "¿Puedo quitar mi página?",
  ofaq_a5: "Sí, un correo y desaparece, normalmente el mismo día. Sin preaviso, sin contrato, y no te preguntaremos tres veces por qué.",
  ofaq_contact: "Escríbenos",
  region_meta_title: "Beach clubs en {place}",
  region_meta_desc: "{n} beach clubs en {place}: precios reales, servicios y contacto directo. Compáralos y contacta tú mismo desde sunbedly.",
  th_kids_area: "Beach clubs para familias",
  th_pool: "Beach clubs con piscina",
  th_pets_allowed: "Beach clubs que admiten perros",
  th_accessible: "Beach clubs accesibles en silla de ruedas",
  th_water_sports: "Beach clubs con deportes acuáticos",
  th_dj: "Beach clubs con DJ",
  th_cabanas: "Beach clubs con cabañas",
  th_parking: "Beach clubs con aparcamiento",
  th_in: "{what} en {place}",
  th_meta: "{what} en {place}: {n} clubes con precios reales, servicios y contacto directo. Compáralos y contacta tú mismo desde sunbedly.",
  th_meta_all: "{what}: {n} clubes en {r} costas, con precios reales, servicios y contacto directo desde sunbedly.",
  th_intro: "Cada club de esta lista aparece según lo que publica sobre sus propios servicios. Los precios y horarios se muestran donde el club los hace públicos, y el contacto es directo con ellos.",
  th_browse: "¿Buscas algo concreto?",
  th_other_regions: "Lo mismo en otras costas",
  th_all_regions: "Elige una costa",
  th_count: "{n} clubes",
  share: "Compartir",
  share_copied: "Enlace copiado",
  badge_price_shown: "Precio indicado",
  no_cut_note: "Pagas al club, en el club. Nosotros no nos quedamos nada.",
  no_account_note: "Sin cuenta, sin tarjeta, sin gastos de reserva.",
  reviews_count_google: "{n} reseñas de Google",
  region_priced_note: "{n} de ellos publican lo que cuesta una hamaca.",
  contact_none_t: "Aún sin línea directa",
  contact_none_d: "No tenemos teléfono de este club. Su propia web es la vía más rápida hasta que se unan a sunbedly.",
  contact_website: "Ir a su web",
  contact_none_bare: "Aún no tenemos datos de contacto de este club. Si lo llevas tú, reclama la página y añádelos en un minuto.",
  fac_bar: "Bar",
  fac_restaurant: "Restaurante",
  fac_showers: "Duchas",
  fac_sunbeds: "Hamacas",
  fac_umbrellas: "Sombrillas",
  fac_dj: "DJ",
  fac_live_music: "Música en vivo",
  fac_parking: "Parking",
  fac_accessible: "Accesible",
  fac_wifi: "Wifi",
  fac_pool: "Piscina",
  fac_terrace: "Terraza",
  fac_toilets: "Aseos",
  fac_kids_area: "Zona infantil",
  fac_playground: "Parque infantil",
  fac_sea_view: "Vistas al mar",
  fac_water_sports: "Deportes acuáticos",
  fac_beach_volley: "Vóley playa",
  fac_beach_tennis: "Tenis playa",
  fac_changing_rooms: "Vestuarios",
  fac_cabanas: "Cabañas",
  fac_lifeguard: "Socorrista",
  fac_pets_allowed: "Mascotas permitidas",
  fac_events: "Eventos",
  fac_jetty: "Pasarela",
  fac_massage: "Masaje",
  fac_card_payment: "Pago con tarjeta",
  fac_boat_rental: "Alquiler de barcos",
  fac_towels: "Toallas incluidas",
  fac_sunscreen: "Crema solar incluida",
  fac_bed_service: "Servicio en la hamaca",
  fac_lockers: "Taquillas",
  fac_spa: "Spa",
  fac_gym: "Gimnasio",
  fac_yoga: "Yoga",
  fac_sports: "Deportes y juegos",
  fac_boat_shuttle: "Lanzadera en barco",
  fac_sea_access: "Acceso directo al mar",
  fac_lounge: "Zona lounge",
  fac_shop: "Tienda",
  fac_adults_only: "Solo adultos",
  fac_nightclub: "Discoteca",
  fac_grounds: "Jardines y zonas verdes",
  gallery_all: "Ver las {n} fotos",
  gallery_close: "Cerrar",
  gallery_prev: "Foto anterior",
  gallery_next: "Foto siguiente",
  photo_by: "Foto: {name} vía Google",
  photo_credit: "Foto: {name}",
  photo_google: "Foto vía Google",
  photo_nearby: "Playa cerca de {place}",
  photo_nearby_note: "Foto del entorno, no del club en sí",
  nav_photo_credits: "Créditos fotográficos",
  nearby_title: "Otros beach clubs cerca",
  nearby_sub: "La misma costa, otro ambiente.",
  crumb_home: "Inicio",
  book_panel_title: "Consultar disponibilidad",
  book_panel_sub: "Enviamos tu solicitud directamente al club. Sin cuenta, sin tarjeta.",
  guides_eyebrow: "Guías",
  guides_title: "Qué beach club merece la pena de verdad",
  guides_sub: "Rankings hechos con los precios que los clubes publican ellos mismos, no con quién pagó por aparecer. Se actualizan a medida que cambian las costas.",
  guides_read: "Leer la guía",
  guides_all: "Todas las guías",
  guides_updated: "Actualizado",
  guides_clubs_in_piece: "Los clubes de esta guía",
  nav_guides: "Guías",
  filters_title: "Filtros",
  filter_priced: "Precio visible de antemano",
  filters_clear: "Borrar filtros",
  footer_destinations: "Destinos",
  footer_beach_clubs_in: "Beach clubs en {place}",
  footer_more_coasts: "Vamos mapeando más costas, una a una. Preferimos cubrir bien una costa que publicar un país con solo dos clubes.",
  b2b_eyebrow: "Para dueños de beach clubs",
  b2b_title: "Tu playa, tus precios, tus clientes.",
  b2b_sub: "Una página gratuita en sunbedly con tus tarifas reales, tus fotos y solicitudes que llegan directas a tu móvil. No cobramos comisión y nunca tocamos tu dinero.",
  b2b_cta: "Publica tu club",
  b2b_cta_sub: "Gratis. Sin contrato, sin exclusividad, lo dejas cuando quieras.",
  b2b_stat_clubs: "clubes ya mapeados",
  b2b_stat_langs: "idiomas, una página",
  b2b_stat_fee: "comisión, nunca",
  b2b_what_title: "Qué incluye",
  b2b_w1_t: "Una página que vende por ti",
  b2b_w1_d: "Tus tarifas, servicios, horario y normas, en seis idiomas, en una página hecha para posicionar. Los viajeros dejan de llamar para preguntar cuánto cuesta una hamaca.",
  b2b_w2_t: "Tu propio panel",
  b2b_w2_d: "Confirma o rechaza solicitudes, sube fotos, cambia tus precios y cierra los días que estés lleno o cerrado. Un enlace, ninguna contraseña que olvidar.",
  b2b_w3_t: "Los clientes contactan contigo directamente",
  b2b_w3_d: "Los botones de llamar, WhatsApp y email apuntan a tu número, no al nuestro. La relación y los datos del cliente son tuyos.",
  b2b_w4_t: "Te quedas con cada euro",
  b2b_w4_d: "El cliente te paga en la playa, igual que ahora. Sin comisión, sin gastos de servicio descontados a tu cliente, sin esperar a que te transfieran.",
  b2b_diff_title: "En qué se diferencia de la plataforma habitual",
  b2b_diff_us: "sunbedly",
  b2b_diff_them: "Plataforma de reservas habitual",
  b2b_d1: "Comisión por reserva",
  b2b_d1_us: "Ninguna",
  b2b_d1_them: "Porcentaje no publicado",
  b2b_d2: "Cargo a tu cliente",
  b2b_d2_us: "Ninguno",
  b2b_d2_them: "Gastos de servicio no reembolsables",
  b2b_d3: "Quién retiene el dinero",
  b2b_d3_us: "Tú. Nosotros no lo tocamos",
  b2b_d3_them: "La plataforma, y luego te paga",
  b2b_d4: "Tus precios antes de reservar",
  b2b_d4_us: "Públicos en tu página",
  b2b_d4_them: "Ocultos hasta el pago",
  b2b_d5: "Datos de contacto del cliente",
  b2b_d5_us: "Directos a ti",
  b2b_d5_them: "En poder de la plataforma",
  b2b_d6: "Exclusividad",
  b2b_d6_us: "Ninguna, te vas cuando quieras",
  b2b_d6_them: "A menudo por contrato",
  b2b_honest_title: "Donde somos sinceros contigo",
  b2b_honest_d: "Estamos empezando. sunbedly tiene {clubs} clubes mapeados y crece costa a costa, así que no vamos a contarte que tenemos millones de usuarios. Lo que sí prometemos: tu ficha no cuesta nada, tus precios se ven antes de que alguien viaje, y no hay nada entre tú y tu cliente. Si eso deja de ser cierto, te vas el mismo día.",
  b2b_how_title: "Cómo publicar tu club",
  b2b_how_1_t: "Envía el formulario",
  b2b_how_1_d: "Nombre del club, pueblo y un número donde localizarte. Dos minutos.",
  b2b_how_2_t: "Montamos tu página",
  b2b_how_2_d: "Investigamos tu playa y escribimos la página, y después te mandamos el enlace del panel para que la revises y corrijas lo que haga falta.",
  b2b_how_3_t: "Empiezan a llegar solicitudes",
  b2b_how_3_d: "Los viajeros te encuentran, ven tu precio real y te escriben directamente.",
  b2b_faq_title: "Lo que preguntan los dueños",
  b2b_fq1: "¿Cuánto cuesta?",
  b2b_fa1: "Nada. Sin cuota de alta, sin comisión, sin parte de lo que gaste tu cliente. Si algún día introducimos una opción de pago será algo que elijas comprar, no un porcentaje descontado de tus reservas.",
  b2b_fq2: "¿Necesito un sistema de reservas?",
  b2b_fa2: "No. La mayoría de los clubes con los que trabajamos aceptan reservas por WhatsApp y con un plano en papel, y no pasa nada. Tu página puede simplemente mandar a la gente a tu teléfono.",
  b2b_fq3: "¿De quién son los datos del cliente?",
  b2b_fa3: "Tuyos. Las solicitudes llegan con los datos de contacto del propio cliente para que respondas directamente. No nos ponemos en medio.",
  b2b_fq4: "¿Puedo cambiar mis precios yo mismo?",
  b2b_fa4: "Sí, desde tu panel, y el cambio se ve al momento. También puedes cerrar los días que estés lleno o cerrado.",
  b2b_fq5: "¿Y si quiero dejarlo?",
  b2b_fa5: "Dínoslo y quitamos la página. Sin contrato, sin preaviso y sin cláusula de exclusividad.",
  about_title: "Sobre este beach club",
  facilities_title: "Servicios",
  hours_title: "Horario",
  rules_title: "Conviene saber",
  good_for: "Ideal para",
  reviews_google: "Opiniones de Google",
  reviews_sunbedly: "Opiniones de sunbedly",
  hours_unknown: "Horario aún sin confirmar. Comprueba con el club antes de ir.",
  club_faq_title: "Preguntas sobre {club}",
  club_title_type: "beach club",
  club_title_in: "beach club en {town}",
  cfaq_q_price: "¿Cuánto cuesta una hamaca en {club}?",
  cfaq_a_price: "Un sitio en {club} empieza en {price}. El club fija sus propias tarifas y en temporada alta pueden cambiar, así que tómalo como precio de partida, no como presupuesto.",
  cfaq_a_price_none: "{club} no ha publicado sus tarifas, por eso ponemos \"precio a consultar\". Escribe al club directamente y te dirán el precio del día.",
  cfaq_q_book: "¿Hace falta reservar en {club}?",
  cfaq_a_book: "En julio y agosto las primeras filas vuelan, así que reservar merece la pena. Envía una solicitud desde aquí y el club confirma directamente, normalmente por WhatsApp.",
  cfaq_q_hours: "¿Cuándo abre {club}?",
  cfaq_q_contact: "¿Cómo contacto con {club}?",
  cfaq_a_contact: "Llama, escribe por WhatsApp o manda un email desde esta misma página. La mayoría de los clubes responde más rápido por WhatsApp.",
  cfaq_q_where: "¿Dónde está exactamente {club}?",
  cfaq_a_where: "{club} está en {town}. Usa el mapa de esta página para llegar puerta a puerta.",
};

export const DICTS: Record<Lang, Dict> = { en, nl, it, de, fr, es };
// Locale prefix routes exist for these 5 (english is the unprefixed default).
// Which language a club owner most likely reads, by the country their beach is
// in. Greece, Croatia, Portugal and the UAE fall back to English on purpose:
// a machine translation into Greek reads worse than competent English, and the
// owner can switch anyway.
export const OWNER_LANG_BY_COUNTRY: Record<string, Lang> = {
  Italy: "it",
  Spain: "es",
  France: "fr",
  Netherlands: "nl",
  Germany: "de",
  Austria: "de",
  Switzerland: "de",
  Belgium: "nl",
};

export const LOCALIZED_LANGS: Lang[] = ["nl", "it", "de", "fr", "es"];

type Vars = Record<string, string | number>;
type I18nValue = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Vars) => string;
  /** A language we think the visitor would rather read, if it is not this one. */
  suggestion: Lang | null;
  /**
   * True when the language comes from the URL rather than from a guess.
   * /nl/club/x means the visitor is on the Dutch page on purpose; the country
   * we think they are in must not override that.
   */
  langFromUrl: boolean;
};

// `{name}`-style placeholders, so a per-club sentence ("How much is a sunbed at
// Baia Muri?") stays one translatable string per language instead of being
// concatenated from fragments, which breaks word order outside English.
// Region names as the reader’s language writes them. Falls back to whatever
// the database holds, which is the English name, so a coast added tomorrow
// still renders rather than showing a raw key.
// The database stores country names in English ("United Arab Emirates"); the
// reader gets their own. Almost every name folds onto its dictionary key by
// rule, so only the ones that do not are listed.
const COUNTRY_KEYS: Record<string, string> = {
  "united arab emirates": "co_uae",
};

export function countryName(country: string, t: (k: string) => string): string {
  const key =
    COUNTRY_KEYS[country.toLowerCase()] ??
    `co_${country.toLowerCase().replace(/\s+/g, "_")}`;
  const translated = t(key);
  return translated === key ? country : translated;
}

export function regionName(key: string, stored: string, t: (k: string) => string): string {
  // Region keys are hyphenated slugs; dictionary keys cannot be, so the two
  // meet here rather than in every caller.
  const dictKey = `rg_${key.replace(/-/g, "_")}`;
  const translated = t(dictKey);
  return translated === dictKey ? stored : translated;
}

export function fill(template: string, vars?: Vars): string {
  if (!vars) return template;
  return Object.entries(vars).reduce(
    (out, [k, v]) => out.split(`{${k}}`).join(String(v)),
    template,
  );
}

const I18nContext = createContext<I18nValue>({
  lang: "en",
  setLang: () => {},
  t: (key, vars) => fill(en[key] ?? key, vars),
  suggestion: null,
  langFromUrl: false,
});

// `urlLocale` makes the language authoritative from the URL. Every page type
// now has a real /nl, /it, /de, /fr, /es address, so the unprefixed URL is
// simply the English one and nothing else.
//
// It used to auto-swap the body language from a geo cookie while the title and
// <html lang> stayed English, which meant sunbedly.com served an Italian page
// under an English title. That is the worst thing you can hand a crawler, and
// it is why a Dutch visitor was reading Italian. Detection now only *suggests*
// a language, through a banner the visitor can accept or ignore.
export function I18nProvider({
  children,
  urlLocale,
}: {
  children: ReactNode;
  urlLocale?: Lang;
}) {
  // What we think the visitor would rather read. Never applied on its own.
  const [suggested, setSuggested] = useState<Lang | null>(null);
  // An explicit choice on a page that has no locale route of its own.
  const [manualLang, setManualLang] = useState<Lang | null>(null);

  useEffect(() => {
    const stored = window.localStorage.getItem("sunbedly_lang");
    if (stored && stored in DICTS) {
      setManualLang(stored as Lang);
      return;
    }
    // The browser's own language setting first, the country of the request
    // second. It used to be the other way round, which meant a Dutch traveller
    // sitting in Italy was offered Italian: we were guessing from where the
    // body is rather than from what the person reads. Accept-Language is a
    // stated preference; an IP address is an inference about a location.
    const nav = window.navigator.language.slice(0, 2).toLowerCase();
    const fromBrowser = nav in DICTS ? (nav as Lang) : null;
    const geoMatch = document.cookie.match(/(?:^|; )sunbedly_lang_geo=([a-z]{2})/);
    const geo = geoMatch && geoMatch[1] in DICTS ? (geoMatch[1] as Lang) : null;
    const guess = fromBrowser ?? geo;
    if (guess && guess !== "en") setSuggested(guess);
  }, []);

  const lang = urlLocale ?? manualLang ?? "en";
  // Only offer what the visitor is not already reading.
  const suggestion = suggested && suggested !== lang ? suggested : null;

  // The shell sets <html lang> from the path, which is right on /nl/... and
  // wrong on an unprefixed URL where the reader picked a language by hand:
  // the document then claimed English while the body was Dutch. A screen
  // reader would read that Dutch out with English pronunciation, and a
  // translation tool would offer to translate a page it thinks is already in
  // your language. The server cannot know a choice that lives in localStorage,
  // so the browser corrects it after mount.
  useEffect(() => {
    if (document.documentElement.lang !== lang) document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback(
    (l: Lang) => {
      if (urlLocale) return; // this page’s language comes from the URL; switching is a navigation
      setManualLang(l);
      setSuggested(null);
      try {
        window.localStorage.setItem("sunbedly_lang", l);
      } catch {
        /* private mode */
      }
    },
    [urlLocale],
  );

  const t = useCallback(
    (key: string, vars?: Vars) => fill(DICTS[lang][key] ?? en[key] ?? key, vars),
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t, suggestion, langFromUrl: Boolean(urlLocale) }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
